/*
==============================================================================
LYNQR Package Configurator
File: assets/lynqr-configurator.js

Part 1
- Bootstrap
- Application State
- Event Bus
- Helper Functions
- Price Engine
- Configuration Engine

Author: Lynqr
==============================================================================
*/

"use strict";

/*==============================================================================
Application Namespace
==============================================================================*/

window.LynqrApp = (() => {
  /*==========================================================================
    Bootstrap Data
    ==========================================================================*/

  if (!window.Lynqr) {
    console.error("[Lynqr] Bootstrap data not found.");
    return;
  }

  const data = window.Lynqr;

  const FLAT_STATUS_OPTIONS = Object.freeze(["BTO", "SBF", "MOP", "EXT’G"]);

  function checkoutTrace(stage, details = {}) {
    console.info("[LYNQR][CHECKOUT-TRACE]", stage, details);
  }

  /*==============================================================================
Local Storage
==============================================================================*/

  const STORAGE_KEY = `lynqr-package-${data.product.handle}`;

  /*==========================================================================
    Application State
    ==========================================================================*/

  const state = {
    isDirty: false,
    product: data.product,

    package: data.package,

    themes: data.themes,

    materialPalettes: data.materialPalettes,

    rooms: data.rooms,

    selectedTheme: null,

    selectedPalette: null,

    selectedFlatStatus: null,

    totalPrice: Number(data.product.price),

    roomSelections: {},

    // Theme-scoped visual data is populated on demand and cached by handle.
    themeData: {},
  };
  /*==============================================================================
Freeze Static Collections
These should never be modified after bootstrap.
==============================================================================*/

  deepFreeze(state.themes);

  deepFreeze(state.materialPalettes);

  deepFreeze(state.rooms);

  /*==============================================================================
Deep Freeze
==============================================================================*/

  function deepFreeze(object) {
    Object.freeze(object);

    Object.getOwnPropertyNames(object).forEach((property) => {
      const value = object[property];

      if (
        value !== null &&
        (typeof value === "object" || typeof value === "function") &&
        !Object.isFrozen(value)
      ) {
        deepFreeze(value);
      }
    });

    return object;
  }
  /*==========================================================================
    Event Bus
    ==========================================================================*/

  const events = new EventTarget();

  function emit(name, detail = {}) {
    events.dispatchEvent(
      new CustomEvent(name, {
        detail,
      }),
    );
  }

  function on(name, callback) {
    events.addEventListener(name, callback);
  }

  /*==============================================================================
Save Configuration
==============================================================================*/

  function saveConfiguration() {
    try {
      localStorage.setItem(
        STORAGE_KEY,

        JSON.stringify(buildConfiguration()),
      );
    } catch (error) {
      console.warn(
        "[Lynqr] Unable to save configuration.",

        error,
      );
    }
  }

  /*==============================================================================
Restore Configuration
==============================================================================*/

  function restoreConfiguration() {
    console.groupCollapsed("[Lynqr][RESTORE] restoreConfiguration()");

    try {
      const json = localStorage.getItem(STORAGE_KEY);

      /*
        --------------------------------------------------------------------------
        RESET ROOM UPGRADES FIRST
        --------------------------------------------------------------------------
        */

      Object.values(state.roomSelections).forEach((selection) => {
        console.log("[Lynqr][RESTORE] Clearing room upgrades", {
          room: selection.room?.handle,

          before: selection.upgrades,
        });

        selection.upgrades = [];
      });

      /*
        --------------------------------------------------------------------------
        RESET PRICE
        --------------------------------------------------------------------------
        */

      state.totalPrice = Number(state.product?.price || 0);

      /*
        --------------------------------------------------------------------------
        NO SAVED CONFIGURATION
        --------------------------------------------------------------------------
        */

      if (!json) {
        console.info("[Lynqr][RESTORE] No saved configuration found.");

        calculateTotal();

        refreshConfiguration();

        state.isDirty = false;

        console.groupEnd();

        return true;
      }

      const config = JSON.parse(json);

      console.log("[Lynqr][RESTORE] Saved configuration", config);

      const savedFlatStatus =
        config.flatStatus || config.package?.flatStatus || null;
      if (savedFlatStatus) {
        selectFlatStatus(savedFlatStatus);
      }

      /*
        --------------------------------------------------------------------------
        THEME
        --------------------------------------------------------------------------
        */

      if (config.theme?.handle) {
        console.log("[Lynqr][RESTORE] Restoring theme", config.theme.handle);

        selectTheme(config.theme.handle);
      }

      /*
        --------------------------------------------------------------------------
        PALETTE
        --------------------------------------------------------------------------
        */

      if (config.palette?.handle) {
        console.log(
          "[Lynqr][RESTORE] Restoring palette",
          config.palette.handle,
        );

        selectPalette(config.palette.handle);
      }

      if (Array.isArray(config.rooms)) {
        config.rooms.forEach((room) => {
          const roomHandle = room.roomHandle || room.handle;
          if (!roomHandle) return;
          if (room.palette?.handle) {
            selectRoomPalette(roomHandle, room.palette.handle);
          }
          restoreRoomOptions(roomHandle, room.options || []);

          (room.upgrades || []).forEach((upgrade) => {
            const upgradeHandle =
              upgrade.handle ||
              upgrade.slug ||
              upgrade.upgradeHandle ||
              upgrade.upgrade?.handle ||
              upgrade.upgrade?.slug;
            if (upgradeHandle) {
              toggleUpgrade(roomHandle, upgradeHandle, true, upgrade.quantity ?? 1);
            }
          });
        });
      }

      /*
        --------------------------------------------------------------------------
        ROOM UPGRADES
        --------------------------------------------------------------------------

        Saved multi-select room upgrades are replayed above through
        toggleUpgrade() so canonical handles, prices and UI state use the same
        path as a customer selection.
        --------------------------------------------------------------------------
        */

      /*
        --------------------------------------------------------------------------
        RECALCULATE
        --------------------------------------------------------------------------
        */

      calculateTotal();

      /*
        --------------------------------------------------------------------------
        REFRESH UI
        --------------------------------------------------------------------------
        */

      refreshConfiguration();

      /*
        --------------------------------------------------------------------------
        RESTORE IS NOT A USER CHANGE
        --------------------------------------------------------------------------
        */

      state.isDirty = false;

      /*
        --------------------------------------------------------------------------
        DEBUG
        --------------------------------------------------------------------------
        */

      console.log("[Lynqr][RESTORE] FINAL STATE", {
        theme: state.selectedTheme?.handle || null,

        palette: state.selectedPalette?.handle || null,

        total: state.totalPrice,

        rooms: Object.values(state.roomSelections).map((selection) => ({
          room: selection.room?.handle,

          upgrades: selection.upgrades.map((upgrade) => ({
            handle: getUpgradeHandle(upgrade),

            price: upgrade.price,
          })),
        })),
      });

      console.info("[Lynqr][RESTORE] Configuration restored.");

      console.groupEnd();

      return true;
    } catch (error) {
      console.error("[Lynqr][RESTORE] Failed to restore configuration.", error);

      console.groupEnd();

      return false;
    }
  }

  /*==========================================================================
    Helpers
    ==========================================================================*/

  function clone(obj) {
    return structuredClone(obj);
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  // Only this formatter's escaped text and allowlisted markup may reach innerHTML.
  function renderShopifyRichText(value) {
    if (value === null || value === undefined || value === "") return "";
    const plainText = (text) => escapeHtml(text).replace(/\r\n|\r|\n/g, "<br>");
    const original = value;
    if (typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        return value.trim() ? plainText(value) : "";
      }
    }
    if (!value || value.type !== "root" || !Array.isArray(value.children)) {
      const text = typeof original === "string" ? original : JSON.stringify(original);
      return text?.trim() ? plainText(text) : "";
    }
    const renderNode = (node) => {
      if (!node || typeof node !== "object") return "";
      if (node.type === "text") {
        let text = plainText(node.value ?? "");
        if (!text) return "";
        if (node.bold === true) text = `<strong>${text}</strong>`;
        if (node.italic === true) text = `<em>${text}</em>`;
        return text;
      }
      if (node.type === "line-break" || node.type === "break") return "<br>";
      const content = Array.isArray(node.children)
        ? node.children.map(renderNode).join("") : "";
      if (!content.replace(/<[^>]*>/g, "").trim() && !content.includes("<br>")) return "";
      switch (node.type) {
        case "root": return content;
        case "paragraph": return `<p>${content}</p>`;
        case "list": {
          const tag = node.listType === "ordered" ? "ol" : "ul";
          return `<${tag}>${content}</${tag}>`;
        }
        case "list-item": return `<li>${content}</li>`;
        case "heading": {
          const level = Number(node.level);
          const tag = Number.isInteger(level) && level >= 1 && level <= 6 ? `h${level}` : "p";
          return `<${tag}>${content}</${tag}>`;
        }
        case "link": {
          const href = typeof node.url === "string" ? node.url.trim() : "";
          try {
            const url = new URL(href, window.location.href);
            if (!href || /[\u0000-\u001f\u007f]/.test(href) ||
                !["https:", "http:", "mailto:", "tel:"].includes(url.protocol)) return content;
          } catch {
            return content;
          }
          const title = node.title ? ` title="${escapeHtml(node.title)}"` : "";
          const target = node.target === "_blank" ? ' target="_blank" rel="noopener noreferrer"' : "";
          return `<a href="${escapeHtml(href)}"${title}${target}>${content}</a>`;
        }
        default: return content;
      }
    };
    return renderNode(value);
  }

  function roomUpgradeDescription(roomUpgrade) {
    return renderShopifyRichText(roomUpgrade.descriptionOverride) ||
      renderShopifyRichText(roomUpgrade.upgrade?.description);
  }

  function roomShortDescription(room) {
    return String(room?.shortDescription || room?.short_description || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function currency(value) {
    const currencyCode = state.product.currency || "SGD";
    const amount = new Intl.NumberFormat("en-SG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

    return `${currencyCode} ${amount}`;
  }

  function slugify(value) {
    return value.toLowerCase().trim().replace(/\s+/g, "-");
  }

  function normalizeFlatStatus(value) {
    return String(value || "")
      .trim()
      .replaceAll("’", "'")
      .toUpperCase();
  }

  function refreshFlatStatusSelection() {
    const selected = normalizeFlatStatus(state.selectedFlatStatus);
    document
      .querySelectorAll("[data-lynqr-flat-status-option]")
      .forEach((input) => {
        const isSelected = normalizeFlatStatus(input.value) === selected;
        input.checked = isSelected;
        input
          .closest(".lynqr-flat-status__option")
          ?.classList.toggle("is-selected", isSelected);
      });

    const hiddenInput = document.querySelector("#lynqr-flat-status");
    if (hiddenInput) hiddenInput.value = state.selectedFlatStatus || "";
  }

  function selectFlatStatus(value) {
    const normalized = normalizeFlatStatus(value);
    const selected = FLAT_STATUS_OPTIONS.find(
      (option) => normalizeFlatStatus(option) === normalized,
    );
    if (!selected) return false;

    state.selectedFlatStatus = selected;
    refreshFlatStatusSelection();
    refreshStep3State();
    refreshCartButtonState();
    markDirty();
    scheduleRender();
    emit("flat-status.changed", { flatStatus: selected });
    return true;
  }

  /*==============================================================================
Theme Selection
==============================================================================*/

  function selectTheme(handle) {
    console.groupCollapsed("[Lynqr][THEME] selectTheme()");

    console.log("[Lynqr][THEME] CLICK / REQUEST", {
      requestedHandle: handle,
      normalizedHandle: String(handle || "")
        .trim()
        .toLowerCase(),

      currentTheme: state.selectedTheme?.handle || null,

      currentPalette: state.selectedPalette?.handle || null,
    });

    const normalizedHandle = String(handle || "")
      .trim()
      .toLowerCase();

    if (!normalizedHandle) {
      console.warn("[Lynqr][THEME] Empty theme handle");

      console.groupEnd();

      return false;
    }

    /*
    --------------------------------------------------------------------------
    AVAILABLE THEMES
    --------------------------------------------------------------------------
    */

    console.log(
      "[Lynqr][THEME] Available themes",
      state.themes.map((theme) => ({
        handle: theme.handle,
        name: theme.name,
        sortOrder: theme.sortOrder,
      })),
    );

    /*
    --------------------------------------------------------------------------
    FIND THEME
    --------------------------------------------------------------------------
    */

    const theme = state.themes.find(
      (item) =>
        String(item.handle || "")
          .trim()
          .toLowerCase() === normalizedHandle,
    );

    if (!theme) {
      console.error("[Lynqr][THEME] THEME NOT FOUND", {
        requestedHandle: handle,
        normalizedHandle,

        availableHandles: state.themes.map((item) => item.handle),
      });

      console.groupEnd();

      return false;
    }

    console.log("[Lynqr][THEME] THEME RESOLVED", {
      handle: theme.handle,
      name: theme.name,

      fullTheme: theme,
    });

    /*
    --------------------------------------------------------------------------
    NO STATE CHANGE
    --------------------------------------------------------------------------
    */

    if (state.selectedTheme?.handle === theme.handle) {
      console.log("[Lynqr][THEME] Theme already selected");

      loadThemeData(theme.handle);

      console.groupEnd();

      return true;
    }

    /*
    --------------------------------------------------------------------------
    SET THEME
    --------------------------------------------------------------------------
    */

    const previousTheme = state.selectedTheme?.handle || null;

    const previousPalette = state.selectedPalette?.handle || null;

    state.selectedTheme = theme;

    console.log("[Lynqr][THEME] STATE UPDATED", {
      previousTheme,
      newTheme: state.selectedTheme?.handle,

      previousPalette,
      currentPalette: state.selectedPalette?.handle || null,
    });

    /*
    --------------------------------------------------------------------------
    FIND PALETTES FOR THEME
    --------------------------------------------------------------------------
    */

    const validPalettes = state.materialPalettes.filter(
      (palette) =>
        String(palette.theme || "")
          .trim()
          .toLowerCase() === normalizedHandle,
    );

    console.log("[Lynqr][THEME] PALETTE MATCHING", {
      selectedTheme: theme.handle,

      selectedThemeNormalized: normalizedHandle,

      totalPalettes: state.materialPalettes.length,

      matchingPalettes: validPalettes.length,

      matchingPaletteHandles: validPalettes.map((palette) => palette.handle),

      matchingPaletteNames: validPalettes.map((palette) => palette.name),
    });

    /*
    --------------------------------------------------------------------------
    SHOW ALL PALETTE THEME VALUES
    --------------------------------------------------------------------------

    This is especially important.

    If a theme has 0 palettes, this will immediately show
    whether the palette records actually point to another theme handle.
    --------------------------------------------------------------------------
    */

    console.table(
      state.materialPalettes.map((palette) => ({
        palette: palette.name,

        paletteHandle: palette.handle,

        paletteTheme: palette.theme,

        paletteThemeNormalized: String(palette.theme || "")
          .trim()
          .toLowerCase(),

        selectedTheme: normalizedHandle,

        matches:
          String(palette.theme || "")
            .trim()
            .toLowerCase() === normalizedHandle,
      })),
    );

    /*
    --------------------------------------------------------------------------
    CLEAR INVALID PALETTE
    --------------------------------------------------------------------------
    */

    if (
      !state.selectedPalette ||
      !validPalettes.some(
        (palette) =>
          String(palette.handle || "")
            .trim()
            .toLowerCase() ===
          String(state.selectedPalette.handle || "")
            .trim()
            .toLowerCase(),
      )
    ) {
      console.log(
        "[Lynqr][THEME] Current palette is invalid for new theme — clearing",
        {
          currentPalette: state.selectedPalette?.handle || null,

          newTheme: theme.handle,

          validPalettes: validPalettes.map((palette) => palette.handle),
        },
      );

      state.selectedPalette = null;
    } else {
      console.log("[Lynqr][THEME] Current palette remains valid", {
        palette: state.selectedPalette.handle,
      });
    }

    /*
    --------------------------------------------------------------------------
    EMIT THEME EVENT
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][THEME] Emitting theme.changed", {
      theme: theme.handle,

      palette: state.selectedPalette?.handle || null,
    });

    emit("theme.changed", theme);

    loadThemeData(theme.handle);

    console.log("[Lynqr][THEME] FINAL STATE", {
      theme: state.selectedTheme?.handle || null,

      palette: state.selectedPalette?.handle || null,

      availablePalettes: availablePalettes().map((palette) => ({
        handle: palette.handle,

        name: palette.name,

        theme: palette.theme,
      })),
    });

    console.groupEnd();

    return true;
  }

  /*==========================================================================
    Material Palette
    ==========================================================================*/

  function availablePalettes(roomHandle = null) {
    if (!state.selectedTheme) {
      console.warn(
        "[Lynqr][PALETTE] availablePalettes() called with NO selected theme",
      );

      return [];
    }

    const selectedTheme = String(state.selectedTheme.handle || "")
      .trim()
      .toLowerCase();

    let palettes = state.materialPalettes.filter((palette) => {
      const paletteTheme = String(palette.theme || "")
        .trim()
        .toLowerCase();

      return paletteTheme === selectedTheme;
    });

    const selection = roomHandle ? roomSelection(roomHandle) : null;
    const allowedHandles = Array.isArray(selection?.room?.materialPalettes)
      ? selection.room.materialPalettes
          .map((value) =>
            String(value || "")
              .trim()
              .toLowerCase(),
          )
          .filter(Boolean)
      : [];

    if (allowedHandles.length) {
      palettes = palettes.filter((palette) =>
        allowedHandles.includes(
          String(palette?.handle || "")
            .trim()
            .toLowerCase(),
        ),
      );
    }

    palettes.sort((a, b) => {
      const numericSortOrder = (palette) => {
        const value = palette?.sortOrder;
        if (value === null || value === undefined || value === "") {
          return Number.MAX_SAFE_INTEGER;
        }
        const numericValue = Number(value);
        return Number.isFinite(numericValue)
          ? numericValue
          : Number.MAX_SAFE_INTEGER;
      };

      return numericSortOrder(a) - numericSortOrder(b);
    });

    console.log("[Lynqr][PALETTE] availablePalettes()", {
      selectedTheme: state.selectedTheme.handle,

      normalizedTheme: selectedTheme,

      totalPalettes: state.materialPalettes.length,

      availableCount: palettes.length,

      availablePalettes: palettes.map((palette) => ({
        handle: palette.handle,

        name: palette.name,

        theme: palette.theme,
      })),
    });

    return palettes;
  }

  function selectRoomPalette(roomHandle, paletteHandle) {
    const selection = roomSelection(roomHandle);
    if (!selection) return false;

    const normalizedHandle = String(paletteHandle || "")
      .trim()
      .toLowerCase();
    const palette = availablePalettes(roomHandle).find(
      (item) =>
        String(item?.handle || "")
          .trim()
          .toLowerCase() === normalizedHandle,
    );

    if (!palette) return false;

    selection.palette = palette;

    renderRoomControls(
      document.querySelector(`.lynqr-room-card[data-room="${roomHandle}"]`),
      selection.room,
    );
    refreshRoomConfiguration(roomHandle);
    refreshStep3State();
    refreshCartButtonState();
    markDirty();
    scheduleRender();
    emit("room.palette.changed", { roomHandle, palette });
    return true;
  }

  function selectRoomOption(roomHandle, groupHandle, optionHandle) {
    const selection = roomSelection(roomHandle);
    if (!selection) return false;

    const group = activeOptionGroups(selection).find(
      (item) =>
        String(item?.handle || item?.slug || "")
          .trim()
          .toLowerCase() ===
        String(groupHandle || "")
          .trim()
          .toLowerCase(),
    );
    const option = (group?.options || []).find(
      (item) =>
        String(item?.handle || item?.slug || "")
          .trim()
          .toLowerCase() ===
        String(optionHandle || "")
          .trim()
          .toLowerCase(),
    );

    if (!group || !option || !roomOptionIsEligible(option, selection))
      return false;

    const canonicalGroupHandle = group.handle || group.slug;
    const previous = selectedRoomOption(selection, canonicalGroupHandle);
    const previousKey = Object.keys(selection.options).find(
      (key) => selection.options[key] === previous,
    );
    if (previousKey) delete selection.options[previousKey];
    selection.options[canonicalGroupHandle] = { group, option };
    clearIncompatibleRoomOptions(roomHandle);
    calculateTotal();
    refreshRoomOptionSelection(roomHandle);
    refreshStep3State();
    refreshCartButtonState();
    markDirty();
    scheduleRender();
    emit("room.option.changed", { roomHandle, group, option });
    return true;
  }

  function selectedRoomOption(selection, groupHandle) {
    const normalizedGroupHandle = String(groupHandle || "")
      .trim()
      .toLowerCase();

    return Object.entries(selection?.options || {}).find(
      ([storedHandle, storedSelection]) =>
        [
          storedHandle,
          storedSelection?.group?.handle,
          storedSelection?.group?.slug,
        ].some(
          (value) =>
            String(value || "")
              .trim()
              .toLowerCase() === normalizedGroupHandle,
        ),
    )?.[1];
  }

  function roomOptionIsSelected(roomHandle, groupHandle, optionHandle) {
    const selectedOption = selectedRoomOption(
      roomSelection(roomHandle),
      groupHandle,
    )?.option;
    const normalizedOptionHandle = String(optionHandle || "")
      .trim()
      .toLowerCase();

    return [selectedOption?.handle, selectedOption?.slug].some(
      (value) =>
        String(value || "")
          .trim()
          .toLowerCase() === normalizedOptionHandle,
    );
  }

  function clearRoomOption(roomHandle, groupHandle, refresh = true) {
    const selection = roomSelection(roomHandle);
    if (!selection?.options?.[groupHandle]) return true;
    delete selection.options[groupHandle];
    if (!refresh) return true;
    clearIncompatibleRoomOptions(roomHandle);
    calculateTotal();
    refreshRoomOptionSelection(roomHandle);
    refreshStep3State();
    refreshCartButtonState();
    markDirty();
    scheduleRender();
    return true;
  }

  function roomOptionReferences(option) {
    return [
      option?.id,
      option?.gid,
      option?.system?.id,
      option?.handle,
      option?.system?.handle,
      option?.slug,
    ]
      .filter(Boolean)
      .flatMap((value) => {
        const key = String(value?.value ?? value)
          .trim()
          .toLowerCase();
        const metaobjectId = key.match(
          /^gid:\/\/shopify\/metaobject\/(\d+)$/,
        )?.[1];
        return metaobjectId ? [key, metaobjectId] : [key];
      });
  }

  function normalizeRoomOptionCompatibility(references) {
    if (references == null || references === "") return [];
    if (Array.isArray(references))
      return references.flatMap(normalizeRoomOptionCompatibility);
    if (typeof references === "object" && "value" in references) {
      return normalizeRoomOptionCompatibility(references.value);
    }
    return [references];
  }

  function roomOptionIsEligible(option, selection) {
    const references = normalizeRoomOptionCompatibility(
      option?.compatibleWith ?? option?.compatible_with,
    );
    if (!references.length) return true;
    const selectedReferences = new Set(
      Object.values(selection?.options || {}).flatMap(({ option: selected }) =>
        roomOptionReferences(selected),
      ),
    );
    return references.some((reference) =>
      roomOptionReferences(
        typeof reference === "string" ? { id: reference } : reference,
      ).some((id) => selectedReferences.has(id)),
    );
  }

  function clearIncompatibleRoomOptions(roomHandle) {
    const selection = roomSelection(roomHandle);
    let cleared;
    do {
      cleared = false;
      Object.entries(selection?.options || {}).forEach(
        ([groupHandle, { option }]) => {
          if (!roomOptionIsEligible(option, selection)) {
            clearRoomOption(roomHandle, groupHandle, false);
            cleared = true;
          }
        },
      );
    } while (cleared);
  }

  function clearUnavailableRoomSelections() {
    Object.entries(state.roomSelections || {}).forEach(([roomHandle, selection]) => {
      const effective = getEffectiveRoomConfiguration(selection);
      const availableUpgrades = new Set(effective.upgrades.map(getUpgradeHandle).filter(Boolean));
      const previousUpgradeCount = selection.upgrades?.length || 0;
      selection.upgrades = (selection.upgrades || []).filter((upgrade) =>
        availableUpgrades.has(getUpgradeHandle(upgrade)),
      );
      selection.upgrades.forEach((upgrade) => {
        const current = effective.upgrades.find((candidate) =>
          getUpgradeHandle(candidate) === getUpgradeHandle(upgrade),
        );
        if (getUpgradeMaxQuantity(current) === null &&
            getUpgradeMaxQuantity(upgrade) === null && upgrade.quantity === undefined) return;
        upgrade.maxQuantity = current?.maxQuantity;
        if (getUpgradeMaxQuantity(upgrade) !== null) {
          upgrade.quantity = getUpgradeQuantity(upgrade);
        } else {
          delete upgrade.quantity;
        }
      });

      const activeGroups = activeOptionGroups(selection);
      const currentOptions = Object.entries(selection.options || {});
      currentOptions.forEach(([storedGroupHandle, stored]) => {
        const normalizedGroupHandle = String(
          stored?.group?.handle || stored?.group?.slug || storedGroupHandle || "",
        ).trim().toLowerCase();
        const group = activeGroups.find((candidate) =>
          String(candidate?.handle || candidate?.slug || "").trim().toLowerCase() === normalizedGroupHandle,
        );
        const option = (group?.options || []).find((candidate) =>
          roomOptionReferences(candidate).some((identity) =>
            roomOptionReferences(stored?.option).includes(identity),
          ),
        );
        if (!group || !option || !roomOptionIsEligible(option, selection)) {
          delete selection.options[storedGroupHandle];
        }
      });
      clearIncompatibleRoomOptions(roomHandle);

      if (previousUpgradeCount !== selection.upgrades.length ||
          currentOptions.length !== Object.keys(selection.options || {}).length) {
        console.log("[Lynqr][ROOM-SET] Removed selections unavailable for Theme", {
          room: roomHandle,
          theme: state.selectedTheme?.handle || null,
          upgrades: selection.upgrades.map(getUpgradeHandle),
          optionGroups: Object.keys(selection.options || {}),
        });
      }
    });
  }

  function restoreRoomOptions(roomHandle, options) {
    // Saved order need not place prerequisites before dependent values.
    let pending = [...options];
    while (pending.length) {
      const remaining = pending.filter((option) => {
        const groupHandle = option.groupHandle || option.group;
        const optionHandle = option.handle || option.slug;
        return (
          !groupHandle ||
          !optionHandle ||
          !selectRoomOption(roomHandle, groupHandle, optionHandle)
        );
      });
      if (remaining.length === pending.length) break;
      pending = remaining;
    }
  }

  /*==============================================================================
Material Palette Selection
==============================================================================*/

  function selectPalette(handle) {
    const normalizedHandle = String(handle || "")
      .trim()
      .toLowerCase();

    if (!normalizedHandle) {
      return false;
    }

    /*
    ==========================================================================
    RESOLVE PALETTE
    ==========================================================================
    */

    const palette = availablePalettes().find(
      (item) =>
        String(item.handle || "")
          .trim()
          .toLowerCase() === normalizedHandle,
    );

    if (!palette) {
      console.warn(
        "[Lynqr][PALETTE] Palette not available for selected theme",
        {
          requestedHandle: handle,
          normalizedHandle,
          selectedTheme: state.selectedTheme?.handle,
          available: availablePalettes().map((item) => item.handle),
        },
      );

      return false;
    }

    /*
    ==========================================================================
    SET STATE
    ==========================================================================
    */

    state.selectedPalette = palette;

    console.log("[Lynqr][PALETTE] STATE UPDATED", {
      handle: state.selectedPalette?.handle,

      name: state.selectedPalette?.name,

      theme: state.selectedTheme?.handle,
    });

    /*
    ==========================================================================
    CLEAR VARIANT CACHE
    ==========================================================================
    */

    /*
    ==========================================================================
    PALETTE CARD UI
    ==========================================================================
    */

    refreshPaletteSelection();

    /*
    ==========================================================================
    STEP 3
    ==========================================================================
    */

    refreshStep3State();

    /*
    ==========================================================================
    UPDATE RIGHT SUMMARY DIRECTLY
    ==========================================================================
    
    Do NOT depend on refreshConfiguration() for this.
    */

    const paletteSummary = document.querySelector("[data-lynqr-palette]");

    if (paletteSummary) {
      paletteSummary.textContent =
        state.selectedPalette.name ||
        state.selectedPalette.label ||
        "Not Selected";
    }

    renderRooms();

    /*
    ==========================================================================
    HERO
    ==========================================================================
    */

    refreshHeroImage();

    /*
    ==========================================================================
    PRICE / SUMMARY
    ==========================================================================
    
    These do not depend on the palette price, but keep the right panel
    synchronised with the current state.
    */

    renderConfigurationSummary();

    refreshPriceSummary();

    /*
    ==========================================================================
    EVENT
    ==========================================================================
    */

    emit("palette.changed", palette);

    /*
    ==========================================================================
    PERSIST
    ==========================================================================
    */

    markDirty();

    scheduleSave();

    /*
    ==========================================================================
    CONSOLIDATED RENDER
    ==========================================================================
    */

    scheduleRender();

    return true;
  }
  /*==========================================================================
    Room State
    ==========================================================================*/

  function initialiseRooms() {
    state.roomSelections = {};

    state.rooms.forEach((room) => {
      if (!room?.handle) {
        return;
      }

      state.roomSelections[room.handle] = {
        room,
        palette: null,
        options: {},
        upgrades: [],
      };
    });
  }

  /*==============================================================================
Room Helpers
==============================================================================*/

  function roomSelection(roomHandle) {
    const normalizedHandle = String(roomHandle || "")
      .trim()
      .toLowerCase();

    if (!normalizedHandle) {
      return null;
    }

    /*
    --------------------------------------------------------------------------
    Fast exact lookup
    --------------------------------------------------------------------------
    */

    if (state.roomSelections[roomHandle]) {
      return state.roomSelections[roomHandle];
    }

    /*
    --------------------------------------------------------------------------
    Normalised lookup
    --------------------------------------------------------------------------
    */

    const key = Object.keys(state.roomSelections).find(
      (value) => String(value).trim().toLowerCase() === normalizedHandle,
    );

    if (!key) {
      return null;
    }

    return state.roomSelections[key];
  }
  /*==============================================================================
Upgrade Identity Helpers

IMPORTANT:
The Room Upgrade metaobject has its own identity, but gallery configuration
keys are based on the referenced Upgrade's slug.

Example:

Room Upgrade
    handle: room-upgrade-lighting-123

Referenced Upgrade
    slug: lighting

Room Gallery configuration key
    lighting

Therefore ALL configurator logic must use the canonical Upgrade slug
when available.
==============================================================================*/

  function normalizeUpgradeHandle(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  function getUpgradeHandle(roomUpgrade) {
    if (!roomUpgrade) {
      return "";
    }

    /*
    --------------------------------------------------------------------------
    Preferred identity
    --------------------------------------------------------------------------

    The referenced Upgrade slug is the canonical gallery identifier.
    --------------------------------------------------------------------------
    */

    const referencedSlug = roomUpgrade?.upgrade?.slug;

    if (referencedSlug) {
      return normalizeUpgradeHandle(referencedSlug);
    }

    /*
    --------------------------------------------------------------------------
    Direct slug
    --------------------------------------------------------------------------
    */

    if (roomUpgrade?.slug) {
      return normalizeUpgradeHandle(roomUpgrade.slug);
    }

    /*
    --------------------------------------------------------------------------
    Fallback
    --------------------------------------------------------------------------
    */

    return normalizeUpgradeHandle(roomUpgrade?.handle);
  }
  function normalizeThemeHandle(theme) {
    return String(theme?.handle || theme || "")
      .trim()
      .toLowerCase();
  }

  function getEffectiveRoomConfiguration(roomOrSelection, selectedTheme = state.selectedTheme) {
    const room = roomOrSelection?.room || roomOrSelection;
    if (!room) return { usesSetB: false, upgrades: [], optionGroups: [] };
    const themeHandle = normalizeThemeHandle(selectedTheme);
    const setBThemeHandles = (Array.isArray(room.setBThemes) ? room.setBThemes : [])
      .map(normalizeThemeHandle)
      .filter(Boolean);
    const usesSetB = Boolean(themeHandle && setBThemeHandles.includes(themeHandle));
    return usesSetB
      ? {
          usesSetB: true,
          upgrades: Array.isArray(room.setBUpgrades) ? room.setBUpgrades : [],
          optionGroups: Array.isArray(room.setBOptionGroups)
            ? room.setBOptionGroups
            : [],
        }
      : {
          usesSetB: false,
          upgrades: Array.isArray(room.upgrades) ? room.upgrades : [],
          optionGroups: Array.isArray(room.optionGroups)
            ? room.optionGroups
            : [],
        };
  }

  function roomUpgradeIdentifiers(roomHandle) {
    return roomSelection(roomHandle)
      .upgrades.map((u) => u.handle)

      .sort();
  }

  function activeOptionGroups(selection) {
    if (!selection) return [];
    const selectedUpgrades = new Set(
      (selection.upgrades || []).map((upgrade) => getUpgradeHandle(upgrade)),
    );
    return getEffectiveRoomConfiguration(selection).optionGroups.filter((group) => {
      const dependency = normalizeUpgradeHandle(group?.revealedByUpgrade);
      return !dependency || selectedUpgrades.has(dependency);
    });
  }

  function optionGroupIsPaid(group) {
    return (
      group?.paid === true ||
      group?.paid === "true" ||
      (group?.options || []).some((option) => Number(option?.price || 0) > 0)
    );
  }

  function selectedPaidOptions(selection) {
    return Object.values(selection?.options || {}).filter(
      ({ group, option }) =>
        optionGroupIsPaid(group) && Number(option?.price || 0) > 0,
    );
  }

  function roomAdditionalTotal(selection) {
    const upgradeTotal = (selection?.upgrades || []).reduce(
      (total, upgrade) => total + getUpgradeLineTotal(upgrade),
      0,
    );
    const paidOptionTotal = selectedPaidOptions(selection).reduce(
      (total, selectionOption) =>
        total + Number(selectionOption?.option?.price || 0),
      0,
    );
    return upgradeTotal + paidOptionTotal;
  }

  function groupRoomOptionsForDisplay(options = []) {
    const displayGroups = [];
    const displayGroupIndexes = new Map();
    const numericSortOrder = (value) => {
      if (value === null || value === undefined || String(value).trim() === "") {
        return null;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    [...options]
      .sort((a, b) =>
        (numericSortOrder(a?.sortOrder) ?? 0) - (numericSortOrder(b?.sortOrder) ?? 0),
      )
      .forEach((option) => {
        const subgroupIdentity = String(option?.subgroup?.handle || "").trim() ||
          String(option?.subgroup?.id || "").trim();
        const subgroup = subgroupIdentity ? option.subgroup : null;
        const name = String(subgroup?.name || "").trim();
        const description = String(subgroup?.description || "").trim();
        const identity = subgroupIdentity || "__ungrouped__";
        const key = identity.toLowerCase();
        let index = displayGroupIndexes.get(key);

        if (index === undefined) {
          index = displayGroups.length;
          displayGroupIndexes.set(key, index);
          displayGroups.push({
            subgroup,
            id: subgroup?.id || null,
            handle: subgroup?.handle || null,
            name,
            description,
            sortOrder: subgroup?.sortOrder ?? null,
            sortKey: identity.toLowerCase(),
            options: [],
          });
        }

        displayGroups[index].options.push(option);
      });

    return displayGroups.sort((a, b) => {
      // Values without a valid Subgroup always precede named sections.
      if (!a.subgroup && b.subgroup) return -1;
      if (a.subgroup && !b.subgroup) return 1;
      const aOrder = numericSortOrder(a.sortOrder);
      const bOrder = numericSortOrder(b.sortOrder);
      if (aOrder !== null && bOrder === null) return -1;
      if (aOrder === null && bOrder !== null) return 1;
      if (aOrder !== null && bOrder !== null && aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return (
        a.name.localeCompare(b.name) || a.sortKey.localeCompare(b.sortKey)
      );
    });
  }

  function roomOptionDisplayName(group, option) {
    const subgroupName = String(option?.subgroup?.name || "").trim();
    return subgroupName
      ? `${subgroupName} - ${option.name}`
      : `${group.name}: ${option.name}`;
  }

  function roomSelectedAddonSummary(selection) {
    const upgradeNames = (selection?.upgrades || [])
      .map(upgradeDisplayName)
      .filter(Boolean);
    const paidOptionNames = selectedPaidOptions(selection).map(
      ({ group, option }) => roomOptionDisplayName(group, option),
    );

    return {
      names: [...upgradeNames, ...paidOptionNames],
      total: roomAdditionalTotal(selection),
    };
  }
  /*==============================================================================
Toggle Room Upgrade
==============================================================================*/

  function getUpgradeMaxQuantity(upgrade) {
    const max = Number(upgrade?.maxQuantity);
    return Number.isSafeInteger(max) && max >= 1 ? max : null;
  }

  function normalizeUpgradeQuantity(upgrade, quantity = 1) {
    const value = Number(quantity);
    if (!Number.isFinite(value)) return 1;
    if (value <= 0) return 0;
    const max = getUpgradeMaxQuantity(upgrade);
    return max === null ? 1 : Math.min(max, Math.max(1, Math.floor(value)));
  }

  function getUpgradeQuantity(upgrade) {
    return upgrade ? normalizeUpgradeQuantity(upgrade, upgrade.quantity ?? 1) : 0;
  }

  // Selection state stores the normalized unit price in dollars.
  function getUpgradeLineTotal(upgrade) {
    return fromCents(toCents(upgrade?.price) * getUpgradeQuantity(upgrade));
  }

  function upgradeDisplayName(upgrade) {
    const name = upgrade?.upgrade?.name || upgrade?.name || "";
    const quantity = getUpgradeQuantity(upgrade);
    return name && quantity > 1 ? `${name} × ${quantity}` : name;
  }

  function setUpgradeQuantity(roomHandle, upgradeHandle, quantity) {
    return toggleUpgrade(roomHandle, upgradeHandle, true, quantity);
  }

  function toggleUpgrade(
    roomHandle, upgradeHandle, shouldSelect = true, requestedQuantity,
  ) {
    checkoutTrace("upgrade.toggle.start", {
      roomHandle,
      upgradeHandle,
      shouldSelect,
      before: Object.values(state.roomSelections || {}).flatMap((selection) =>
        (selection?.upgrades || []).map((upgrade) => ({
          handle: getUpgradeHandle(upgrade),
          price: upgrade?.price,
        })),
      ),
    });

    console.groupCollapsed("[Lynqr][UPGRADE] toggleUpgrade()");

    console.log("[Lynqr][UPGRADE] INPUT", {
      roomHandle,
      upgradeHandle,
      shouldSelect,
    });

    /*--------------------------------------------------------------------------
    ROOM
    --------------------------------------------------------------------------*/

    const selection = roomSelection(roomHandle);

    if (!selection) {
      console.error("[Lynqr][UPGRADE] ROOM NOT FOUND", {
        roomHandle,
        availableRooms: Object.keys(state.roomSelections),
      });

      console.groupEnd();

      return false;
    }

    console.log("[Lynqr][UPGRADE] ROOM FOUND", {
      room: selection.room?.name,

      roomHandle: selection.room?.handle,

      existingUpgrades: selection.upgrades,
    });

    /*--------------------------------------------------------------------------
    NORMALISE REQUEST
    --------------------------------------------------------------------------*/

    const requestedHandle = normalizeUpgradeHandle(upgradeHandle);

    if (!requestedHandle) {
      console.error("[Lynqr][UPGRADE] EMPTY UPGRADE HANDLE");

      console.groupEnd();

      return false;
    }

    /*--------------------------------------------------------------------------
    FIND THE ACTUAL ROOM UPGRADE
    --------------------------------------------------------------------------

    IMPORTANT:

    The UI may provide:

        lighting-upgrade

    while the Room Upgrade canonical slug is:

        lighting

    and the actual Room Upgrade handle may be:

        master-bedroom-lighting

    Therefore we intentionally match against ALL known identities.
    --------------------------------------------------------------------------*/

    const roomUpgrades = getEffectiveRoomConfiguration(selection).upgrades;

    console.log(
      "[Lynqr][UPGRADE] AVAILABLE UPGRADE IDENTITIES",
      roomUpgrades.map((upgrade, index) => ({
        index,

        rawHandle: normalizeUpgradeHandle(upgrade?.handle),

        slug: normalizeUpgradeHandle(upgrade?.slug),

        referencedHandle: normalizeUpgradeHandle(upgrade?.upgrade?.handle),

        referencedSlug: normalizeUpgradeHandle(upgrade?.upgrade?.slug),

        canonical: getUpgradeHandle(upgrade),

        name: upgrade?.upgrade?.name || upgrade?.name,

        price: upgrade?.price,

        enabled: upgrade?.enabled,
      })),
    );

    const roomUpgrade = roomUpgrades.find((upgrade) => {
      if (upgrade?.enabled === false || upgrade?.enabled === "false") {
        return false;
      }

      const identities = [
        normalizeUpgradeHandle(upgrade?.handle),

        normalizeUpgradeHandle(upgrade?.slug),

        normalizeUpgradeHandle(upgrade?.upgrade?.handle),

        normalizeUpgradeHandle(upgrade?.upgrade?.slug),

        normalizeUpgradeHandle(getUpgradeHandle(upgrade)),
      ].filter(Boolean);

      const matched = identities.includes(requestedHandle);

      console.log("[Lynqr][UPGRADE] IDENTITY MATCH", {
        requested: requestedHandle,

        identities,

        matched,

        upgrade,
      });

      return matched;
    });

    /*--------------------------------------------------------------------------
    CANONICAL UPGRADE NOT FOUND
    --------------------------------------------------------------------------*/

    if (!roomUpgrade) {
      console.error("[Lynqr][UPGRADE] UPGRADE RESOLUTION FAILED", {
        room: roomHandle,

        requested: requestedHandle,

        available: roomUpgrades.map((upgrade) => ({
          handle: upgrade?.handle,

          slug: upgrade?.slug,

          referencedHandle: upgrade?.upgrade?.handle,

          referencedSlug: upgrade?.upgrade?.slug,

          canonical: getUpgradeHandle(upgrade),

          name: upgrade?.upgrade?.name || upgrade?.name,

          price: upgrade?.price,
        })),
      });

      console.groupEnd();

      return false;
    }

    const canonicalHandle = getUpgradeHandle(roomUpgrade);
    const quantity = shouldSelect
      ? normalizeUpgradeQuantity(roomUpgrade, requestedQuantity ?? 1)
      : 0;
    shouldSelect = quantity > 0;

    console.log("[Lynqr][UPGRADE] UPGRADE RESOLVED", {
      requested: requestedHandle,

      canonical: canonicalHandle,

      rawHandle: roomUpgrade?.handle,

      slug: roomUpgrade?.slug,

      name: roomUpgrade?.upgrade?.name || roomUpgrade?.name,

      price: roomUpgrade?.price,
    });

    /*--------------------------------------------------------------------------
    FIND EXISTING SELECTION USING CANONICAL IDENTITY
    --------------------------------------------------------------------------*/

    const existingIndex = selection.upgrades.findIndex(
      (upgrade) => getUpgradeHandle(upgrade) === canonicalHandle,
    );

    console.log("[Lynqr][UPGRADE] EXISTING SELECTION", {
      existingIndex,

      selected: selection.upgrades.map((upgrade) => ({
        canonical: getUpgradeHandle(upgrade),

        rawHandle: upgrade?.handle,

        name: upgrade?.upgrade?.name || upgrade?.name,

        price: upgrade?.price,
      })),
    });

    /*--------------------------------------------------------------------------
    SELECT
    --------------------------------------------------------------------------*/

    if (shouldSelect) {
      if (existingIndex !== -1 && (
        requestedQuantity === undefined ||
        (getUpgradeMaxQuantity(roomUpgrade) === null &&
          getUpgradeMaxQuantity(selection.upgrades[existingIndex]) === null)
      )) {
        console.log("[Lynqr][UPGRADE] ALREADY SELECTED");

        refreshUpgradeSelection(roomHandle);

        refreshRoomUpgradeSummary(roomHandle);

        console.groupEnd();

        return true;
      }

      /*----------------------------------------------------------------------
        ADD ACTUAL ROOM UPGRADE
        ----------------------------------------------------------------------*/

      const selectedUpgrade = {
        ...roomUpgrade,

        /*
         * Shopify Room Upgrade prices are stored in minor units.
         * Example:
         *   20000 -> SGD 200
         *   60000 -> SGD 600
         *   25000 -> SGD 250
         *
         * Once an upgrade enters selection state,
         * store the normalized application price.
         */
        price: getRoomUpgradePrice(roomUpgrade),
        ...(getUpgradeMaxQuantity(roomUpgrade) !== null ? { quantity } : {}),
      };

      if (existingIndex !== -1) {
        // Keep one selected object and update it in place, including current max.
        Object.assign(selection.upgrades[existingIndex], selectedUpgrade);
        selection.upgrades[existingIndex].maxQuantity = roomUpgrade.maxQuantity;
        if (getUpgradeMaxQuantity(roomUpgrade) === null) {
          delete selection.upgrades[existingIndex].quantity;
        }
      } else {
        selection.upgrades.push(selectedUpgrade);
      }

      console.log("[Lynqr][UPGRADE] ADDED", {
        canonical: canonicalHandle,

        room: roomHandle,

        price: roomUpgrade?.price,

        upgrades: selection.upgrades,
      });
    } else {
      /*--------------------------------------------------------------------------
    DESELECT
    --------------------------------------------------------------------------*/
      if (existingIndex !== -1) {
        const removed = selection.upgrades.splice(existingIndex, 1)[0];

        console.log("[Lynqr][UPGRADE] REMOVED", {
          canonical: getUpgradeHandle(removed),

          price: removed?.price,
        });
      }
    }

    /*--------------------------------------------------------------------------
    ROOM SUBTOTAL
    --------------------------------------------------------------------------*/

    const roomSubtotal = selection.upgrades.reduce(
      (total, upgrade) => total + getUpgradeLineTotal(upgrade),
      0,
    );

    checkoutTrace("upgrade.toggle.end", {
      roomHandle,
      upgradeHandle: canonicalHandle,
      shouldSelect,
      selectedCount: selection.upgrades.length,
      selected: selection.upgrades.map((upgrade) => ({
        handle: getUpgradeHandle(upgrade),
        price: upgrade?.price,
        variantId: upgrade?.shopifyVariantId,
      })),
      totalPrice: state.totalPrice,
      roomSubtotal,
    });

    console.log("[Lynqr][UPGRADE] ROOM SUBTOTAL BEFORE UI", {
      room: selection.room?.name,

      roomHandle: selection.room?.handle,

      upgradeCount: selection.upgrades.length,

      roomSubtotal,

      formatted: currency(roomSubtotal),
    });

    /*--------------------------------------------------------------------------
    GLOBAL PRICE
    --------------------------------------------------------------------------*/

    const globalTotal = calculateTotal();

    console.log("[Lynqr][UPGRADE] GLOBAL TOTAL", {
      globalTotal,
      stateTotal: state.totalPrice,
    });

    const activeGroupHandles = new Set(
      activeOptionGroups(selection).map((group) => group.handle),
    );
    Object.keys(selection.options || {}).forEach((groupHandle) => {
      if (!activeGroupHandles.has(groupHandle)) {
        delete selection.options[groupHandle];
      }
    });
    renderRoomControls(
      document.querySelector(`.lynqr-room-card[data-room="${roomHandle}"]`),
      selection.room,
    );

    /*--------------------------------------------------------------------------
    UI
    --------------------------------------------------------------------------*/

    refreshUpgradeSelection(roomHandle);

    refreshRoomUpgradeSummary(roomHandle);

    /*--------------------------------------------------------------------------
    VARIANT / GALLERY
    --------------------------------------------------------------------------*/

    const galleryContext = buildRoomGalleryContext(roomHandle);

    console.log("[Lynqr][UPGRADE] RESOLVE ROOM GALLERY", {
      roomHandle,
      galleryContext,
    });

    const variant = resolveRoomGallery(roomHandle, galleryContext);

    console.log("[Lynqr][UPGRADE] ROOM GALLERY RESULT", {
      roomHandle,

      key: [
        galleryContext.theme,
        galleryContext.palette,
        roomHandle,
        galleryContext.configuration,
      ].join("|"),

      found: !!variant,

      variant,
    });

    renderRoomGallery(roomHandle, variant);

    /*--------------------------------------------------------------------------
    STATE / RENDER
    --------------------------------------------------------------------------*/

    markDirty();

    scheduleRender();

    /*--------------------------------------------------------------------------
    EVENT
    --------------------------------------------------------------------------*/

    emit("room.changed", {
      roomHandle,

      room: selection,

      configurationKey: configurationKey(roomHandle),

      variant,

      upgrades: [...selection.upgrades],

      upgradeTotal: roomSubtotal,
    });

    console.log("[Lynqr][UPGRADE] COMPLETE", {
      room: roomHandle,

      canonical: canonicalHandle,

      upgrades: selection.upgrades.map((upgrade) => ({
        canonical: getUpgradeHandle(upgrade),

        name: upgrade?.upgrade?.name || upgrade?.name,

        price: upgrade?.price,
      })),

      roomSubtotal,

      globalTotal: state.totalPrice,
    });

    console.groupEnd();

    return true;
  }

  /*==============================================================================
Price Engine
==============================================================================*/

  function calculateTotal() {
    let total = Number(state.product?.price || 0);

    Object.values(state.roomSelections).forEach((selection) => {
      total += roomAdditionalTotal(selection);
    });

    state.totalPrice = total;

    emit("price.changed", {
      total,
      formatted: currency(total),
    });

    return total;
  }

  function roundMoney(value) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  function toCents(value) {
    return Math.round((Number(value) || 0) * 100);
  }

  function fromCents(value) {
    return (Number(value) || 0) / 100;
  }

  function pricingBreakdown() {
    const rawBasePackageValue = state.product?.price;
    const hasBasePackageValue =
      rawBasePackageValue !== null &&
      rawBasePackageValue !== "" &&
      Number.isFinite(Number(rawBasePackageValue)) &&
      Number(rawBasePackageValue) > 0;
    const basePackageValue = hasBasePackageValue
      ? Number(rawBasePackageValue)
      : 0;
    const fullPackageValue = Number(state.totalPrice) || basePackageValue;
    const upgradeTotal = Math.max(0, fullPackageValue - basePackageValue);
    const depositPercentage = Number(state.product?.depositPercentage) || 70;
    const configuredPackageValueCents = toCents(fullPackageValue);
    const expectedDepositCents = Math.round(
      (configuredPackageValueCents * depositPercentage) / 100,
    );
    const baseDepositPriceCents =
      Number(state.product?.baseDepositPriceCents) ||
      toCents(state.product?.checkoutPrice);
    const upgradeDepositCents = Object.values(
      state.roomSelections || {},
    ).reduce(
      (total, selection) =>
        total +
        (selection?.upgrades || []).reduce(
          (roomTotal, selectedUpgrade) =>
            roomTotal +
            Number(
              selectedUpgrade?.shopifyDepositPriceCents ||
                toCents(selectedUpgrade?.shopifyDepositPrice),
            ) * getUpgradeQuantity(selectedUpgrade),
          0,
        ),
      0,
    );
    const paidOptionDepositCents = Object.values(
      state.roomSelections || {},
    ).reduce(
      (total, selection) =>
        total +
        selectedPaidOptions(selection).reduce(
          (roomTotal, selectionOption) =>
            roomTotal +
            Number(
              selectionOption?.option?.shopifyDepositPriceCents ||
                toCents(selectionOption?.option?.shopifyDepositPrice),
            ),
          0,
        ),
      0,
    );
    const shopifyDepositCents =
      baseDepositPriceCents + upgradeDepositCents + paidOptionDepositCents;
    const expectedDeposit = fromCents(expectedDepositCents);
    const gstRate = 9;
    const depositGstCents = Math.round((expectedDepositCents * gstRate) / 100);
    const totalPayableNowCents = expectedDepositCents + depositGstCents;
    const shopifyDeposit = fromCents(shopifyDepositCents);
    const failureCode = !hasBasePackageValue
      ? "MISSING_FULL_PACKAGE_VALUE"
      : expectedDepositCents !== shopifyDepositCents
        ? "DEPOSIT_MAPPING_MISMATCH"
        : null;

    return {
      basePackageValue: roundMoney(basePackageValue),
      basePackageValueCents: toCents(basePackageValue),
      upgradeTotal: roundMoney(upgradeTotal),
      fullPackageValue: roundMoney(fullPackageValue),
      depositPercentage,
      expectedDeposit,
      gstRate,
      depositGst: fromCents(depositGstCents),
      depositGstCents,
      totalPayableNow: fromCents(totalPayableNowCents),
      totalPayableNowCents,
      configuredPackageValueCents,
      expectedDepositCents,
      expectedCartTotalCents: expectedDepositCents,
      baseDepositPriceCents,
      upgradeDepositCents,
      paidOptionDepositCents,
      shopifyDepositCents,
      shopifyDeposit,
      remainingBalance: roundMoney(fullPackageValue - expectedDeposit),
      failureCode,
      isValid: failureCode === null,
    };
  }

  function expectedCartTotalCents(pricing, quantity = 1) {
    return Number(pricing?.expectedDepositCents || 0) * (Number(quantity) || 1);
  }

  /*==============================================================================
Configuration Key
==============================================================================*/

  function configurationKey(roomHandle) {
    const selection = roomSelection(roomHandle);

    if (!selection) {
      return "standard";
    }

    /*
    --------------------------------------------------------------------------
    Convert selected upgrades to canonical Upgrade slugs
    --------------------------------------------------------------------------
    */

    const upgrades = selection.upgrades
      .map((upgrade) => getUpgradeHandle(upgrade))
      .filter(Boolean)
      .sort();

    /*
    --------------------------------------------------------------------------
    Standard Configuration
    --------------------------------------------------------------------------
    */

    if (!upgrades.length) {
      return "standard";
    }

    /*
    --------------------------------------------------------------------------
    Combined Configuration

    Example:

        handles
        lighting
        handles+lighting
    --------------------------------------------------------------------------
    */

    return upgrades.join("+");
  }

  /*==============================================================================
UI Initialisation
==============================================================================*/

  const ui = {
    themeContainer: null,

    paletteContainer: null,

    galleryContainer: null,

    roomContainer: null,

    summaryContainer: null,

    totalPrice: null,
  };

  function cacheDom() {
    ui.themeContainer = document.querySelector("[data-lynqr-theme-selector]");

    ui.paletteContainer = document.querySelector(
      "[data-lynqr-palette-selector]",
    );

    ui.galleryContainer = document.querySelector("[data-lynqr-gallery]");

    ui.roomContainer = document.querySelector("[data-lynqr-room-list]");

    ui.summaryContainer = document.querySelector("[data-lynqr-price-summary]");

    ui.totalPrice = document.querySelector("[data-lynqr-total-price]");

    ui.configuratorSidebar = document.querySelector(
      ".lynqr-configurator-sidebar",
    );

    ui.mobileSummaryBar = document.querySelector(
      "[data-lynqr-mobile-summary-bar]",
    );

    ui.mobileSummaryToggle = document.querySelector(
      "[data-lynqr-mobile-summary-toggle]",
    );

    ui.mobileSummaryDrawer = document.querySelector(
      "[data-lynqr-mobile-summary-drawer]",
    );

    ui.mobileSummaryBackdrop = document.querySelector(
      "[data-lynqr-mobile-summary-backdrop]",
    );

    ui.mobileSummaryTotal = document.querySelector(
      "[data-lynqr-mobile-summary-total]",
    );

    ui.mobileSummaryPackage = document.querySelector(
      "[data-lynqr-mobile-summary-package]",
    );

    ui.mobileSummaryClose = document.querySelector(
      "[data-lynqr-mobile-summary-close]",
    );
  }
  /*==============================================================================
Step 3 Visibility / Package Validation State
==============================================================================*/

  function refreshStep3State() {
    const flatStatusSelected = Boolean(state.selectedFlatStatus);
    const themeSelected = Boolean(state.selectedTheme?.handle);
    const roomsConfigured = Object.values(state.roomSelections).every(
      (selection) => {
        const paletteSelected = Boolean(selection?.palette?.handle);
        const requiredOptionsSelected = activeOptionGroups(selection)
          .filter((group) => group?.required === true)
          .every((group) => Boolean(selection.options?.[group.handle]));
        return paletteSelected && requiredOptionsSelected;
      },
    );
    const configurationReady =
      flatStatusSelected && themeSelected && roomsConfigured;

    /*
    --------------------------------------------------------------------------
    STEP 3
    --------------------------------------------------------------------------
    */

    const step3 = document.querySelector('[data-lynqr-step="2"]');

    if (step3) {
      step3.hidden = !themeSelected;

      step3.setAttribute("aria-hidden", String(!themeSelected));
    }

    /*
    --------------------------------------------------------------------------
    ADD TO CART
    --------------------------------------------------------------------------
    */

    const addToCartButton = document.querySelector("[data-lynqr-add-to-cart]");

    if (addToCartButton) {
      addToCartButton.disabled = !configurationReady;

      addToCartButton.setAttribute(
        "aria-disabled",
        String(!configurationReady),
      );
    }

    /*
    --------------------------------------------------------------------------
    DEBUG
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][STEP 3] STATE", {
      flatStatusSelected,

      themeSelected,
      theme: state.selectedTheme?.handle || null,

      roomPalettesSelected: Object.values(state.roomSelections).every(
        (selection) => Boolean(selection?.palette?.handle),
      ),

      configurationReady,

      step3Found: Boolean(step3),

      step3Hidden: step3 ? step3.hidden : null,

      addToCartFound: Boolean(addToCartButton),

      addToCartDisabled: addToCartButton ? addToCartButton.disabled : null,
    });
  }
  /*==============================================================================
Theme Rendering
==============================================================================*/

  function renderThemes() {
    if (!ui.themeContainer) {
      return;
    }

    ui.themeContainer.innerHTML = "";

    /*
    IMPORTANT:
    state.themes is deep-frozen.
    Never call .sort() directly on it.
    */

    const themes = [...state.themes].sort(
      (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
    );

    themes.forEach((theme) => {
      const button = document.createElement("button");

      button.type = "button";

      button.className = "lynqr-theme";

      button.dataset.handle = theme.handle;

      button.innerHTML = `

            <div class="lynqr-theme__image">

                <img
                    src="${theme.thumbnail || ""}"
                    alt="${theme.name || ""}"
                    width="300"
                    height="200"
                    loading="lazy"
                    decoding="async"
                >

            </div>

            <div class="lynqr-theme__name">

                ${theme.name || ""}

            </div>

            ${
              theme.description
                ? `<div class="lynqr-theme__description">${escapeHtml(theme.description)}</div>`
                : ""
            }

        `;

      button.addEventListener("click", () => {
        selectTheme(theme.handle);
      });

      ui.themeContainer.appendChild(button);
    });
  }
  /*==============================================================================
Theme Changed
==============================================================================*/

  on("theme.changed", ({ detail }) => {
    /*
        ----------------------------------------------------------------------
        Active Theme
        ----------------------------------------------------------------------
        */

    document.querySelectorAll(".lynqr-theme").forEach((button) => {
      const buttonHandle = String(button.dataset.handle || "")
        .trim()
        .toLowerCase();

      const themeHandle = String(detail?.handle || "")
        .trim()
        .toLowerCase();

      button.classList.toggle("is-active", buttonHandle === themeHandle);
    });

    /*
        ----------------------------------------------------------------------
        Validate existing palette
        ----------------------------------------------------------------------

        IMPORTANT:
        Changing theme does NOT automatically select a palette.

        If the current palette does not belong to the new theme,
        clear it instead.
        ----------------------------------------------------------------------
        */

    Object.values(state.roomSelections).forEach((selection) => {
      const palettes = availablePalettes(selection.room.handle);
      const currentPaletteHandle = String(selection.palette?.handle || "")
        .trim()
        .toLowerCase();
      selection.palette =
        palettes.find(
          (palette) =>
            String(palette.handle || "")
              .trim()
              .toLowerCase() === currentPaletteHandle,
        ) || null;
    });

    clearUnavailableRoomSelections();

    state.selectedPalette = null;

    /*
        ----------------------------------------------------------------------
        Re-render palette cards
        ----------------------------------------------------------------------
        */

    renderRooms();

    /*
        ----------------------------------------------------------------------
        Variant cache
        ----------------------------------------------------------------------
        */

    /*
        ----------------------------------------------------------------------
        Refresh
        ----------------------------------------------------------------------
        */

    refreshHeroImage();

    scheduleRender();
    refreshStep3State();
  });
  /*==============================================================================
Material Palette Rendering
==============================================================================*/
  function renderMaterialPalettes() {
    if (!ui.paletteContainer) {
      return;
    }

    const palettes = availablePalettes();

    console.groupCollapsed("[Lynqr][PALETTE] renderMaterialPalettes()");

    console.log("[Lynqr][PALETTE] Rendering palettes for theme", {
      theme: state.selectedTheme?.handle || null,

      themeName: state.selectedTheme?.name || null,

      paletteCount: palettes.length,

      palettes: palettes.map((palette) => ({
        handle: palette.handle,

        name: palette.name,

        theme: palette.theme,

        image: palette.thumbnail || palette.image || null,
      })),
    });

    console.groupEnd();

    // rest unchanged

    ui.paletteContainer.replaceChildren();

    if (!palettes.length) {
      const empty = document.createElement("div");

      empty.className = "lynqr-palette-empty";

      empty.textContent = "No material palettes are currently available.";

      ui.paletteContainer.appendChild(empty);

      return;
    }

    /*
    --------------------------------------------------------------------------
    Current selected palette
    --------------------------------------------------------------------------
    */

    const selectedHandle = String(state.selectedPalette?.handle || "")
      .trim()
      .toLowerCase();

    /*
    --------------------------------------------------------------------------
    Render
    --------------------------------------------------------------------------
    */

    palettes.forEach((palette) => {
      const paletteHandle = String(palette.handle || "")
        .trim()
        .toLowerCase();

      const selected = paletteHandle === selectedHandle;

      const card = document.createElement("button");

      card.type = "button";

      card.className = "lynqr-palette";

      card.dataset.handle = palette.handle;

      card.dataset.selected = selected ? "true" : "false";

      card.setAttribute("aria-pressed", String(selected));

      const image = palette.image || palette.thumbnail || "";

      const description =
        palette.description ||
        palette.shortDescription ||
        palette.subtitle ||
        "";

      const tagline = palette.tagline || palette.positioning || "";

      const badge = palette.badge || palette.label || "";

      card.innerHTML = `

                <span class="lynqr-palette__image">

                    ${
                      image
                        ? `
                                <img
                                    src="${image}"
                                    alt="${palette.name || "Material palette"}"
                                    width="1200"
                                    height="800"
                                    loading="lazy"
                                    decoding="async"
                                >
                            `
                        : ""
                    }

                    <span
                        class="lynqr-palette__selected-badge"
                        aria-hidden="true"
                    >
                        ✓
                    </span>

                </span>


                <span class="lynqr-palette__content">

                    <span class="lynqr-palette__title-row">

                        <strong class="lynqr-palette__name">
                            ${palette.name || "Material Palette"}
                        </strong>

                        ${
                          badge
                            ? `
                                    <span class="lynqr-palette__badge">
                                        ${badge}
                                    </span>
                                `
                            : ""
                        }

                    </span>


                    ${
                      tagline
                        ? `
                                <span class="lynqr-palette__tagline">
                                    ${tagline}
                                </span>
                            `
                        : ""
                    }


                    ${
                      description
                        ? `
                                <span class="lynqr-palette__description">
                                    ${description}
                                </span>
                            `
                        : ""
                    }


                    <span class="lynqr-palette__footer">

                        <span class="lynqr-palette__action">
                            ${selected ? "✓ Selected" : "Select Palette"}
                        </span>

                    </span>

                </span>

            `;

      /*
            ------------------------------------------------------------------
            Explicit visual state
            ------------------------------------------------------------------
            */

      card.classList.toggle("is-active", selected);

      const selectedBadge = card.querySelector(
        ".lynqr-palette__selected-badge",
      );

      if (selectedBadge) {
        selectedBadge.hidden = !selected;

        /*
                Explicit inline display prevents a stylesheet from
                accidentally showing every badge.
                */

        selectedBadge.style.display = selected ? "" : "none";
      }

      /*
            ------------------------------------------------------------------
            Click
            ------------------------------------------------------------------
            */

      card.addEventListener("click", (event) => {
        event.preventDefault();

        selectPalette(palette.handle);
      });

      /*
            ------------------------------------------------------------------
            Keyboard
            ------------------------------------------------------------------
            */

      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();

        selectPalette(palette.handle);
      });

      ui.paletteContainer.appendChild(card);
    });

    refreshPaletteSelection();
  }
  /*==============================================================================
Palette Selection UI
==============================================================================*/

  function refreshPaletteSelection() {
    console.groupCollapsed("[Lynqr][PALETTE] refreshPaletteSelection()");

    if (!ui.paletteContainer) {
      console.warn("[Lynqr][PALETTE] Palette container not found.");

      console.groupEnd();

      return;
    }

    const selectedHandle = String(state.selectedPalette?.handle || "")
      .trim()
      .toLowerCase();

    console.log("[Lynqr][PALETTE] CURRENT STATE", {
      selectedPalette: state.selectedPalette,

      selectedHandle,

      paletteContainer: ui.paletteContainer,

      paletteCardCount:
        ui.paletteContainer.querySelectorAll(".lynqr-palette").length,
    });

    ui.paletteContainer
      .querySelectorAll(".lynqr-palette")
      .forEach((card, index) => {
        const handle = String(card.dataset.handle || "")
          .trim()
          .toLowerCase();

        const selected = Boolean(selectedHandle && handle === selectedHandle);

        console.log("[Lynqr][PALETTE] CARD", {
          index,

          card,

          rawHandle: card.dataset.handle,

          normalizedHandle: handle,

          selectedHandle,

          selected,

          before: {
            className: card.className,

            datasetSelected: card.dataset.selected,

            ariaPressed: card.getAttribute("aria-pressed"),
          },
        });

        /*
            ------------------------------------------------------------------
            Card state
            ------------------------------------------------------------------
            */

        card.classList.toggle("is-active", selected);

        card.dataset.selected = selected ? "true" : "false";

        card.setAttribute("aria-pressed", String(selected));

        console.log("[Lynqr][PALETTE] CARD STATE UPDATED", {
          index,

          handle,

          selected,

          className: card.className,

          datasetSelected: card.dataset.selected,

          ariaPressed: card.getAttribute("aria-pressed"),
        });

        /*
            ------------------------------------------------------------------
            Selection badge
            ------------------------------------------------------------------
            */

        const badge = card.querySelector(".lynqr-palette__selected-badge");

        if (badge) {
          console.log("[Lynqr][PALETTE] BADGE FOUND", {
            index,

            handle,

            selected,

            before: {
              hidden: badge.hidden,

              display: badge.style.display,
            },
          });

          badge.hidden = !selected;

          badge.style.display = selected ? "" : "none";

          console.log("[Lynqr][PALETTE] BADGE UPDATED", {
            index,

            handle,

            hidden: badge.hidden,

            display: badge.style.display,
          });
        } else {
          console.warn("[Lynqr][PALETTE] SELECTED BADGE NOT FOUND", {
            index,
            handle,
          });
        }

        /*
            ------------------------------------------------------------------
            Action text
            ------------------------------------------------------------------
            */

        const action = card.querySelector(".lynqr-palette__action");

        if (action) {
          console.log("[Lynqr][PALETTE] ACTION BEFORE", {
            index,

            handle,

            text: action.textContent?.trim(),

            selected,
          });

          action.textContent = selected ? "✓ Selected" : "Select Palette";

          console.log("[Lynqr][PALETTE] ACTION AFTER", {
            index,

            handle,

            text: action.textContent?.trim(),

            selected,
          });
        } else {
          console.warn("[Lynqr][PALETTE] ACTION ELEMENT NOT FOUND", {
            index,
            handle,
          });
        }
      });

    console.log("[Lynqr][PALETTE] refreshPaletteSelection() COMPLETE", {
      selectedHandle,

      activeCards: Array.from(
        ui.paletteContainer.querySelectorAll(".lynqr-palette.is-active"),
      ).map((card) => ({
        handle: card.dataset.handle,

        selected: card.dataset.selected,

        ariaPressed: card.getAttribute("aria-pressed"),
      })),
    });

    console.groupEnd();
  }

  /*==============================================================================
Price Updates
==============================================================================*/

  on(
    "price.changed",

    ({ detail }) => {
      if (!ui.totalPrice) return;

      ui.totalPrice.textContent = detail.formatted;
    },
  );

  /*==============================================================================
Validation Events
==============================================================================*/

  on(
    "validation.error",

    ({ detail }) => {
      console.warn(
        "[Lynqr Validation]",

        detail.message,
      );

      // Temporary UI
      alert(detail.message);
    },
  );

  /*==============================================================================
Application Initialisation
==============================================================================*/

  function validateQuotationRequest(customer = {}) {
    const errors = [];
    const details = {
      name: String(customer.name || "").trim(),
      phone: String(customer.phone || "").trim(),
      email: String(customer.email || "").trim(),
      address: String(customer.address || "").trim(),
    };

    if (!details.name) errors.push("Please enter the customer's name.");
    if (!details.phone) errors.push("Please enter a contact number.");
    if (!details.email) {
      errors.push("Please enter an email address.");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) {
      errors.push("Please enter a valid email address.");
    }
    if (!details.address) errors.push("Please enter the customer's address.");

    errors.push(...validateSelections().errors);

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  function buildQuotation(customer = {}) {
    calculateTotal();
    const pricing = pricingBreakdown();
    const rooms = Object.values(state.roomSelections || {}).map((selection) => {
      const includedItems = [];
      const paidItems = [];

      (selection.upgrades || []).forEach((upgrade) => {
        const name = upgradeDisplayName(upgrade) || "Add-on";
        const price = getUpgradeLineTotal(upgrade);
        if (price > 0) paidItems.push({ name, price });
        else includedItems.push(name);
      });

      Object.values(selection.options || {}).forEach(({ group, option }) => {
        const name = roomOptionDisplayName(group, option);
        const price = Number(option?.price || 0);
        if (optionGroupIsPaid(group) && price > 0) {
          paidItems.push({ name, price });
        } else {
          includedItems.push(name);
        }
      });

      return {
        name: selection.room?.name || "Room",
        palette: selection.palette?.name || "Not selected",
        shortDescription: roomShortDescription(selection.room),
        includedItems,
        paidItems,
        additionalTotal: roomAdditionalTotal(selection),
      };
    });

    return {
      customer: {
        name: String(customer.name || "").trim(),
        phone: String(customer.phone || "").trim(),
        email: String(customer.email || "").trim(),
        address: String(customer.address || "").trim(),
      },
      generatedAt: new Date(),
      packageName: state.product?.title || "HDB Package",
      flatStatus: state.selectedFlatStatus || "Not selected",
      theme: state.selectedTheme?.name || "Not selected",
      rooms,
      basePackageValue: pricing.basePackageValue,
      upgradeTotal: pricing.upgradeTotal,
      fullPackageValue: pricing.fullPackageValue,
      depositPercentage: pricing.depositPercentage,
      deposit: pricing.expectedDeposit,
      gstRate: pricing.gstRate,
      depositGst: pricing.depositGst,
      totalPayableNow: pricing.totalPayableNow,
      remainingBalance: pricing.remainingBalance,
    };
  }

  function renderPrintableQuotation(customer) {
    const quotation = buildQuotation(customer);
    const target = document.querySelector("[data-lynqr-print-quotation]");
    if (!target) return false;
    const paymentTermsHtml =
      document.querySelector("[data-lynqr-payment-terms-template]")
        ?.innerHTML || "";

    // Keep the print document outside the responsive summary drawer so its
    // fixed positioning, width and pagination are not constrained on mobile.
    document.body?.appendChild?.(target);

    const paidLineItems = quotation.rooms.flatMap((room) =>
      room.paidItems.map((item) => ({ ...item, room: room.name })),
    );
    const formatItems = (items, includePrices = false) =>
      items.length
        ? `<ul>${items
            .map((item) => {
              const name = typeof item === "string" ? item : item.name;
              const price = typeof item === "string" ? 0 : item.price;
              return `<li>${escapeHtml(name)}${includePrices ? ` <strong>${escapeHtml(currency(price))}</strong>` : ""}</li>`;
            })
            .join("")}</ul>`
        : '<span class="lynqr-print-quotation__muted">None</span>';

    target.innerHTML = `
      <header class="lynqr-print-quotation__header">
        <div>
          <div class="lynqr-print-quotation__brand">LYNQR</div>
          <h1>Package Quotation</h1>
        </div>
        <div class="lynqr-print-quotation__date">
          <span>Date</span>
          <strong>${escapeHtml(quotation.generatedAt.toLocaleDateString("en-SG"))}</strong>
        </div>
      </header>

      <section class="lynqr-print-quotation__customer">
        <h2>Customer Details</h2>
        <dl>
          <div><dt>Name</dt><dd>${escapeHtml(quotation.customer.name)}</dd></div>
          <div><dt>Phone</dt><dd>${escapeHtml(quotation.customer.phone)}</dd></div>
          <div><dt>Email</dt><dd>${escapeHtml(quotation.customer.email)}</dd></div>
          <div class="lynqr-print-quotation__address"><dt>Address</dt><dd>${escapeHtml(quotation.customer.address)}</dd></div>
        </dl>
      </section>

      <section>
        <h2>Package Details</h2>
        <div class="lynqr-print-quotation__package">
          <div><span>Package</span><strong>${escapeHtml(quotation.packageName)}</strong></div>
          <div><span>Flat status</span><strong>${escapeHtml(quotation.flatStatus)}</strong></div>
          <div><span>Theme</span><strong>${escapeHtml(quotation.theme)}</strong></div>
        </div>
      </section>

      ${
        quotation.rooms.some((room) => room.shortDescription)
          ? `<section>
        <h2>Base Package Inclusions</h2>
        <div class="lynqr-print-quotation__inclusions">
          ${quotation.rooms
            .filter((room) => room.shortDescription)
            .map(
              (room) => `<article>
                <h3>${escapeHtml(room.name)}</h3>
                <p>${escapeHtml(room.shortDescription)}</p>
              </article>`,
            )
            .join("")}
        </div>
      </section>`
          : ""
      }

      <section>
        <h2>Room Configuration</h2>
        <table class="lynqr-print-quotation__rooms">
          <thead><tr><th>Room</th><th>Material palette</th><th>Included selections</th><th>Paid upgrades</th><th>Amount</th></tr></thead>
          <tbody>
            ${quotation.rooms
              .map(
                (room) => `<tr>
                  <td><strong>${escapeHtml(room.name)}</strong></td>
                  <td>${escapeHtml(room.palette)}</td>
                  <td>${formatItems(room.includedItems)}</td>
                  <td>${formatItems(room.paidItems, true)}</td>
                  <td>${room.additionalTotal > 0 ? escapeHtml(currency(room.additionalTotal)) : "Included"}</td>
                </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Price Breakdown</h2>
        <table class="lynqr-print-quotation__pricing">
          <tbody>
            <tr><td>${escapeHtml(quotation.packageName)} – base package</td><td>${escapeHtml(currency(quotation.basePackageValue))}</td></tr>
            ${paidLineItems
              .map(
                (item) =>
                  `<tr><td>${escapeHtml(item.room)} – ${escapeHtml(item.name)}</td><td>${escapeHtml(currency(item.price))}</td></tr>`,
              )
              .join("")}
            <tr class="is-total"><th>Total package value</th><th>${escapeHtml(currency(quotation.fullPackageValue))}</th></tr>
          </tbody>
        </table>
      </section>

      <section class="lynqr-print-quotation__payment">
        <h2>Payment Summary</h2>
        <div class="is-total"><span>Appointment Fee payable</span><strong>${escapeHtml(currency(500))}</strong></div>
        <p>The SGD 500 Appointment Fee secures an appointment for a site visit prior to confirmation.</p>
      </section>

      <section class="lynqr-print-quotation__terms">
        ${paymentTermsHtml}
      </section>

      <section class="lynqr-print-quotation__acceptance">
        <h2>Customer Acceptance</h2>
        <p>I acknowledge the selections, pricing, payment terms and terms and conditions stated in this quotation.</p>
        <div class="lynqr-print-quotation__signatures">
          <div><span>Customer signature</span></div>
          <div><span>Date</span></div>
        </div>
      </section>
    `;
    target.setAttribute("aria-hidden", "false");
    return quotation;
  }

  function startOver() {
    state.selectedTheme = null;
    state.selectedPalette = null;
    state.selectedFlatStatus = null;
    initialiseRooms();

    state.totalPrice = Number(state.product?.price || 0);

    try {
      localStorage.removeItem?.(STORAGE_KEY);
    } catch (error) {
      console.warn("[Lynqr] Unable to clear the saved configuration.", error);
    }

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("cfg")) {
        url.searchParams.delete("cfg");
        window.history?.replaceState?.({}, "", url.toString());
      }
    } catch (_error) {
      // A relative or unavailable URL does not prevent the in-page reset.
    }

    renderThemes();
    renderRooms();
    refreshFlatStatusSelection();
    refreshConfiguration();
    refreshStep3State();
    refreshCartButtonState();
    setMobileSummaryDrawerState(false);
    document.querySelector("[data-lynqr-quotation-form]")?.reset();
    const printableQuotation = document.querySelector(
      "[data-lynqr-print-quotation]",
    );
    if (printableQuotation) {
      printableQuotation.replaceChildren();
      printableQuotation.setAttribute("aria-hidden", "true");
    }
    clearDirty();
    emit("configuration.reset", { state });
    return true;
  }

  let utilityControlsBound = false;

  function bindUtilityControls() {
    if (utilityControlsBound) return;
    utilityControlsBound = true;

    const startOverButton = document.querySelector("[data-lynqr-start-over]");
    const backToTopButton = document.querySelector("[data-lynqr-back-to-top]");
    const quotationForm = document.querySelector("[data-lynqr-quotation-form]");

    document
      .querySelectorAll("[data-lynqr-flat-status-option]")
      .forEach((input) => {
        input.addEventListener("change", () => {
          if (input.checked) selectFlatStatus(input.value);
        });
      });

    startOverButton?.addEventListener("click", () => {
      const shouldReset =
        typeof window.confirm !== "function" ||
        window.confirm(
          "Start over and clear all theme, palette, option and add-on selections?",
        );

      if (!shouldReset) return;
      startOver();
      window.scrollTo?.({ top: 0, behavior: "smooth" });
    });

    if (backToTopButton) {
      const refreshBackToTopVisibility = () => {
        const visible = Number(window.scrollY || 0) > 500;
        backToTopButton.classList.toggle("is-visible", visible);
        backToTopButton.setAttribute("aria-hidden", String(!visible));
        backToTopButton.tabIndex = visible ? 0 : -1;
      };

      backToTopButton.addEventListener("click", () => {
        window.scrollTo?.({ top: 0, behavior: "smooth" });
      });
      window.addEventListener("scroll", refreshBackToTopVisibility, {
        passive: true,
      });
      refreshBackToTopVisibility();
    }

    quotationForm?.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!quotationForm.reportValidity()) return;

      const formData = new FormData(quotationForm);
      const customer = {
        name: formData.get("customer_name"),
        phone: formData.get("customer_phone"),
        email: formData.get("customer_email"),
        address: formData.get("customer_address"),
      };
      const validation = validateQuotationRequest(customer);
      if (!validation.valid) {
        emit("validation.error", {
          message: validation.errors[0],
          errors: validation.errors,
        });
        return;
      }
      if (!validateBusinessRules()) return;

      const quotation = renderPrintableQuotation(customer);
      if (!quotation) return;

      requestAnimationFrame(() => window.print());
    });

    window.addEventListener("afterprint", () => {
      document
        .querySelector("[data-lynqr-print-quotation]")
        ?.setAttribute("aria-hidden", "true");
    });
  }

  let isInitialised = false;

  const APP_VERSION = "1.0.0";
  function initialise() {
    if (isInitialised) {
      return;
    }

    isInitialised = true;

    console.groupCollapsed("[Lynqr] Initialisation");
    console.time("[Lynqr] Initialisation");

    emit("configurator.beforeInit");

    try {
      /*
        ----------------------------------------------------------------------
        Validate Bootstrap
        ----------------------------------------------------------------------
        */

      if (!window.Lynqr) {
        throw new Error("Bootstrap data was not found.");
      }

      /*
        ----------------------------------------------------------------------
        Cache DOM Elements
        ----------------------------------------------------------------------
        */

      cacheDom();

      /*
        ----------------------------------------------------------------------
        Initialise Mobile / Tablet Summary Drawer
        ----------------------------------------------------------------------
        */

      initialiseMobileSummaryDrawer();
      initialiseRoomImageLightbox();
      initialiseUpgradeGalleryLightbox();

      /*
        ----------------------------------------------------------------------
        Initialise Application State
        ----------------------------------------------------------------------
        */

      initialiseRooms();

      // Production galleries are theme-scoped and loaded after Step 1.
      // The optional bootstrap collection exists only for isolated tests and
      // gradual migration; Shopify does not serialize it on the product page.
      loadInitialRoomGalleries();

      /*
        ----------------------------------------------------------------------
        Render Static UI
        ----------------------------------------------------------------------
        */

      renderThemes();
      renderRooms();
      initialiseResponsiveNavigation();

      /*
        ----------------------------------------------------------------------
        Upgrade DOM Diagnostic
        ----------------------------------------------------------------------
        */

      console.groupCollapsed("[Lynqr][UPGRADE] DOM DIAGNOSTIC");

      const upgradeCheckboxes = document.querySelectorAll(
        ".lynqr-upgrade__checkbox",
      );

      console.log("Upgrade checkbox count:", upgradeCheckboxes.length);

      upgradeCheckboxes.forEach((checkbox, index) => {
        console.log(`Checkbox ${index + 1}:`, {
          element: checkbox,
          room: checkbox.dataset.room,
          upgrade: checkbox.dataset.upgrade,
          checked: checkbox.checked,
          disabled: checkbox.disabled,
          type: checkbox.type,
          name: checkbox.name,
          value: checkbox.value,
        });
      });

      console.groupEnd();

      /*
        ----------------------------------------------------------------------
        Bind UI Events
        ----------------------------------------------------------------------
        */

      bindUpgradeEvents();
      bindCartButton();
      bindUtilityControls();

      /*
        ----------------------------------------------------------------------
        Restore Previous Configuration
        ----------------------------------------------------------------------
        */

      restoreConfiguration();

      restoreFromURL();

      /*
        ----------------------------------------------------------------------
        Apply Defaults
        ----------------------------------------------------------------------
        */

      if (!state.selectedTheme) {
        initialiseDefaults();
      }

      /*
        ----------------------------------------------------------------------
        Refresh Entire Configurator
        ----------------------------------------------------------------------
        */

      refreshConfiguration();

      refreshStep3State();

      /*
        ----------------------------------------------------------------------
        Reset Dirty State
        ----------------------------------------------------------------------
        */

      state.isDirty = false;

      /*
        ----------------------------------------------------------------------
        Refresh Mobile / Tablet Summary Drawer
        ----------------------------------------------------------------------
        */

      if (ui.mobileSummaryTotal) {
        ui.mobileSummaryTotal.textContent = currency(state.totalPrice);
      }

      /*
        ----------------------------------------------------------------------
        Ready
        ----------------------------------------------------------------------
        */

      emit("configurator.ready", {
        state,
        version: APP_VERSION,
      });

      console.info("[Lynqr] Configurator Ready");
    } catch (error) {
      console.error("[Lynqr] Failed to initialise configurator.", error);

      emit("configurator.error", {
        error,
      });
    } finally {
      console.timeEnd("[Lynqr] Initialisation");
      console.groupEnd();
    }
  }

  document.addEventListener("DOMContentLoaded", initialise);

  /*==============================================================================
Build Room Gallery Context
==============================================================================*/

  function buildRoomGalleryContext(roomHandle, overrides = {}) {
    const handle =
      typeof roomHandle === "string" ? roomHandle : roomHandle?.handle;

    const normalisedRoomHandle = String(handle || "")
      .trim()
      .toLowerCase();

    if (!normalisedRoomHandle) {
      return {
        theme: null,
        palette: null,
        configuration: "standard",
      };
    }

    const selection =
      typeof roomSelection === "function"
        ? roomSelection(normalisedRoomHandle)
        : null;

    const room =
      selection?.room ||
      state.rooms?.find(
        (item) =>
          String(item?.handle || "")
            .trim()
            .toLowerCase() === normalisedRoomHandle,
      ) ||
      null;

    /*
    --------------------------------------------------------------------------
    THEME

    Current configurator state is authoritative.
    Room data is only a fallback.
    --------------------------------------------------------------------------
    */

    const theme =
      overrides.theme ||
      state.selectedTheme?.handle ||
      room?.theme ||
      selection?.room?.theme ||
      null;

    /*
    --------------------------------------------------------------------------
    PALETTE

    Current configurator state is authoritative.
    Room data is only a fallback.
    --------------------------------------------------------------------------
    */

    const palette =
      overrides.palette ||
      selection?.palette?.handle ||
      state.selectedPalette?.handle ||
      room?.palette ||
      selection?.room?.palette ||
      null;

    /*
    --------------------------------------------------------------------------
    CONFIGURATION

    IMPORTANT:
    This MUST come from the room's current selected upgrades.

    Do not rely on room.configuration because that may be stale/static.
    --------------------------------------------------------------------------
    */

    const configuration =
      overrides.configuration ||
      configurationKey(normalisedRoomHandle) ||
      room?.configuration ||
      selection?.room?.configuration ||
      "standard";

    return {
      theme,
      palette,
      configuration,
    };
  }

  /*==============================================================================
Room Rendering
==============================================================================*/

  function renderRooms() {
    if (!ui.roomContainer) {
      console.warn("[Lynqr][ROOM] Room container not found");

      return;
    }

    /*
    --------------------------------------------------------------------------
    SORT PRODUCT ROOMS
    --------------------------------------------------------------------------
    */

    const rooms = [...state.rooms].sort(
      (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
    );

    console.log("[Lynqr][ROOM] Rendering product rooms", {
      roomCount: rooms.length,
      rooms: rooms.map((room) => ({
        handle: room.handle,
        name: room.name,
        sortOrder: room.sortOrder,
      })),
    });

    /*
    --------------------------------------------------------------------------
    CLEAR EXISTING ROOM CARDS
    --------------------------------------------------------------------------
    
    renderRooms() is the authoritative function for creating the
    room-card DOM.

    It is only called during initialisation, so rebuilding the cards
    here is safe.
    --------------------------------------------------------------------------
    */

    ui.roomContainer.replaceChildren();

    /*
    --------------------------------------------------------------------------
    CREATE ONE CARD FOR EVERY PRODUCT ROOM
    --------------------------------------------------------------------------
    */

    rooms.forEach((room) => {
      const roomCard = createRoomCard(room);

      ui.roomContainer.appendChild(roomCard);

      if (state.selectedTheme) {
        renderRoomPlaceholder(room.handle, roomDisplayState(room.handle));
      }
    });

    /*
|--------------------------------------------------------------------------
| ROOM CARDS ONLY
|--------------------------------------------------------------------------
|
| Do NOT resolve Room Galleries here.
|
| renderRooms() runs before restoreConfiguration(),
| restoreFromURL() and initialiseDefaults().
|
| Therefore state.selectedTheme / state.selectedPalette may
| legitimately still be null here.
|
| Gallery rendering is performed later by refreshConfiguration()
| once the configurator state is ready.
|--------------------------------------------------------------------------
*/

    console.log(
      "[Lynqr][ROOM] Room cards created — gallery resolution deferred",
      {
        roomCount: rooms.length,

        theme: state.selectedTheme?.handle || null,

        palette: state.selectedPalette?.handle || null,
      },
    );

    if (roomNavigationInitialised) refreshRoomNavigation();
  }
  /*==============================================================================
Room Card
==============================================================================*/

  function createRoomCard(room) {
    const card = document.createElement("section");

    const variant = resolveRoomGallery(room.handle);
    card.className = "lynqr-room-card";

    card.dataset.room = room.handle;
    card.id = `lynqr-room-${room.handle}`;

    /*
    --------------------------------------------------------------------------
    ROOM DESCRIPTION
    --------------------------------------------------------------------------
    */

    const roomDescription = variant?.description || room.description || "";

    /*
    ==========================================================================
    ROOM CARD
    ==========================================================================

    IMPORTANT:

    Room previews use the selected Theme's Room images, then Default Image,
    then the exact Room Gallery record or the Room icon.

    refreshRoomImage() resolves the Room Gallery after the
    Theme + Palette + Configuration have been established.
    ==========================================================================
    */

    card.innerHTML = `

        <!-- ============================================================ -->
        <!-- ROOM PREVIEW                                                 -->
        <!-- ============================================================ -->

        <div
            class="lynqr-room-card__preview"
            data-room-preview="${room.handle}"
            role="button"
            tabindex="0"
            aria-label="Open ${escapeHtml(room.name || "room")} image"
        >

            <div class="lynqr-room-card__empty">
                <span>
                    Room Configuration Not Available
                </span>
            </div>


            <!-- ======================================================== -->
            <!-- IMAGE OVERLAY                                             -->
            <!-- ======================================================== -->

            <div class="lynqr-room-card__preview-overlay">

                <span class="lynqr-room-card__preview-label">
                    ${room.name || ""}
                </span>

            </div>

        </div>


        <!-- ============================================================ -->
        <!-- ROOM INFORMATION                                             -->
        <!-- ============================================================ -->

        <div class="lynqr-room-card__information">

            <div class="lynqr-room-card__information-main">

                ${
                  roomDescription
                    ? `
                            <div class="lynqr-room-card__description rte">
                                ${roomDescription}
                            </div>
                        `
                    : ""
                }

            </div>


            <div class="lynqr-room-card__included">

                <span
                    class="lynqr-room-card__included-icon"
                    aria-hidden="true"
                >
                    ✓
                </span>

                <span>
                    Included
                </span>

            </div>

        </div>


        <!-- ============================================================ -->
        <!-- ROOM GALLERY                                                 -->
        <!-- ============================================================ -->

        <section class="lynqr-room-card__gallery-section">

            <div class="lynqr-room-card__gallery-heading">
                Image Gallery
            </div>

            <div
                class="lynqr-room-gallery"
                data-room-gallery="${room.handle}"
            ></div>

        </section>


        <!-- ============================================================ -->
        <!-- ROOM CONFIGURATION                                           -->
        <!-- ============================================================ -->

        <div class="lynqr-room-card__config">

            <div
                class="lynqr-room-card__controls"
                data-room-controls
            ></div>

            <details class="lynqr-room-control lynqr-room-control--addons" data-room-addons>
                <summary>
                    <span>
                        <strong>Select Add-ons</strong>
                        <small>Optional - select any that apply</small>
                    </span>
                    <span class="lynqr-room-control__status" data-room-addon-status>None selected</span>
                </summary>
                <div class="lynqr-room-control__body">
                    <div
                        class="lynqr-room-card__upgrades"
                        data-room-upgrades
                    ></div>
                </div>
            </details>

        </div>

    `;

    /*
    --------------------------------------------------------------------------
    RENDER UPGRADE SELECTOR
    --------------------------------------------------------------------------

    IMPORTANT:

    Leave the existing upgrade rendering completely untouched.

    Room Gallery availability controls whether the gallery UI is
    subsequently shown/hidden.
    --------------------------------------------------------------------------
    */

    renderRoomControls(card, room);
    renderUpgradeSelector(card, {
      ...room,
      upgrades: getEffectiveRoomConfiguration(room).upgrades,
    });

    /*
    --------------------------------------------------------------------------
    RETURN
    --------------------------------------------------------------------------
    */

    return card;
  }

  /*==============================================================================
Restore / Rebuild Room Card
==============================================================================*/

  function restoreRoomCard(roomHandle) {
    console.group("[Lynqr][ROOM][RESTORE] restoreRoomCard()");

    /*
    --------------------------------------------------------------------------
    NORMALISE ROOM HANDLE
    --------------------------------------------------------------------------
    */

    const normalisedRoomHandle =
      typeof roomHandle === "string" ? roomHandle : roomHandle?.handle;

    if (!normalisedRoomHandle) {
      console.warn("[Lynqr][ROOM][RESTORE] ABORT — invalid room handle", {
        roomHandle,
      });

      console.groupEnd();

      return null;
    }

    /*
    --------------------------------------------------------------------------
    FIND ROOM DATA
    --------------------------------------------------------------------------
    */

    const room = state.rooms?.find(
      (item) =>
        String(item?.handle || "")
          .trim()
          .toLowerCase() === String(normalisedRoomHandle).trim().toLowerCase(),
    );

    if (!room) {
      console.warn("[Lynqr][ROOM][RESTORE] ABORT — room not found in state", {
        roomHandle: normalisedRoomHandle,

        availableRooms: state.rooms?.map((item) => item?.handle),
      });

      console.groupEnd();

      return null;
    }

    /*
    --------------------------------------------------------------------------
    FIND EXISTING CARD
    --------------------------------------------------------------------------
    */

    const existingCard = document.querySelector(
      `.lynqr-room-card[data-room="${normalisedRoomHandle}"]`,
    );

    console.log("[Lynqr][ROOM][RESTORE] EXISTING CARD", {
      roomHandle: normalisedRoomHandle,

      found: !!existingCard,

      existingCard,
    });

    /*
    --------------------------------------------------------------------------
    CREATE COMPLETE NORMAL ROOM CARD
    --------------------------------------------------------------------------

    IMPORTANT:

    createRoomCard() already creates:

        - preview
        - room description
        - Included indicator
        - gallery section
        - gallery container
        - configuration container
        - upgrade selector
        - upgrade summary

    So do NOT duplicate that HTML here.
    --------------------------------------------------------------------------
    */

    const newCard = createRoomCard(room);

    if (!newCard) {
      console.error(
        "[Lynqr][ROOM][RESTORE] FAILED — createRoomCard() returned null",
        {
          roomHandle: normalisedRoomHandle,
        },
      );

      console.groupEnd();

      return null;
    }

    /*
    --------------------------------------------------------------------------
    REPLACE PLACEHOLDER CARD
    --------------------------------------------------------------------------
    */

    if (existingCard) {
      existingCard.replaceWith(newCard);
    } else {
      /*
        Fallback:
        If the card does not exist, append it to the room container.
        */

      if (ui.roomContainer) {
        ui.roomContainer.appendChild(newCard);
      } else {
        console.error(
          "[Lynqr][ROOM][RESTORE] Cannot append — room container missing",
          {
            roomHandle: normalisedRoomHandle,
          },
        );

        console.groupEnd();

        return null;
      }
    }

    /*
    --------------------------------------------------------------------------
    VERIFY NORMAL CARD
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][ROOM][RESTORE] CARD REBUILT", {
      roomHandle: normalisedRoomHandle,

      card: newCard,

      preview: !!newCard.querySelector("[data-room-preview]"),

      information: !!newCard.querySelector(".lynqr-room-card__information"),

      gallery: !!newCard.querySelector(".lynqr-room-card__gallery-section"),

      galleryContainer: !!newCard.querySelector("[data-room-gallery]"),

      configuration: !!newCard.querySelector(".lynqr-room-card__config"),

      upgrades: !!newCard.querySelector("[data-room-upgrades]"),

      unavailable: newCard.classList.contains("lynqr-room-card--unavailable"),
    });

    /*
    --------------------------------------------------------------------------
    COMPLETE
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][ROOM][RESTORE] COMPLETE", {
      roomHandle: normalisedRoomHandle,
    });

    console.groupEnd();

    return newCard;
  }
  /*==============================================================================
Hero Image
==============================================================================*/

  function refreshHeroImage() {
    if (!state.selectedTheme) return;

    const hero = document.querySelector(".lynqr-product-image");

    if (!hero) return;

    const imageUrl = state.selectedTheme.heroImage || "";

    if (!imageUrl) return;

    hero.src = imageUrl;

    hero.width = 1200;
    hero.height = 800;

    hero.loading = "eager";
    hero.decoding = "async";

    hero.style.aspectRatio = "3 / 2";
  }

  function roomPaletteThumbnail(roomHandle, themeHandle, palette) {
    const gallery = roomGalleries.get(
      roomGalleryKey(themeHandle, palette?.handle, roomHandle),
    );
    // Registered `image` is the exact record's serialized hero_image. Do not
    // use the main resolver's Room fallbacks or the Gallery Images list here.
    return String(gallery?.image || "").trim() || palette?.thumbnail || "";
  }

  function refreshRoomPaletteThumbnails(registeredTheme) {
    const theme = String(state.selectedTheme?.handle || "").trim().toLowerCase();
    if (!theme || String(registeredTheme || "").trim().toLowerCase() !== theme)
      return;

    const palettes = new Map(
      state.materialPalettes.map((palette) => [palette.handle, palette]),
    );
    document.querySelectorAll(".lynqr-room-control--palette").forEach((details) => {
      details.querySelectorAll(".lynqr-room-choice-card").forEach((label) => {
        const palette = palettes.get(label.querySelector("input")?.value);
        const preview = label.querySelector("img")
          || label.querySelector(".lynqr-room-choice-card__swatch");
        if (!palette || !preview) return;
        const source = roomPaletteThumbnail(details.dataset.roomPalette, theme, palette);
        const isImage = preview.tagName.toLowerCase() === "img";
        if (source && isImage) {
          if (galleryImageKey(preview.src) !== galleryImageKey(source))
            preview.src = source;
        } else if (Boolean(source) !== isImage) {
          const replacement = document.createElement(source ? "img" : "span");
          if (source) {
            replacement.src = source;
            replacement.alt = "";
            replacement.loading = "lazy";
            replacement.decoding = "async";
          } else {
            replacement.className = "lynqr-room-choice-card__swatch";
            replacement.setAttribute("aria-hidden", "true");
          }
          label.insertBefore(replacement, preview);
          preview.remove();
        }
      });
    });
  }

  function renderRoomControls(roomCard, room) {
    if (!roomCard || !room) return;

    const container = roomCard.querySelector("[data-room-controls]");
    const selection = roomSelection(room.handle);
    if (!container || !selection) return;

    container.replaceChildren();

    const paletteDetails = document.createElement("details");
    paletteDetails.className = "lynqr-room-control lynqr-room-control--palette";
    paletteDetails.dataset.roomPalette = room.handle;
    paletteDetails.open = !selection.palette;

    const paletteName = selection.palette?.name || "Choose a palette";
    paletteDetails.innerHTML = `
      <summary>
        <span>
          <strong>Select Material Palette</strong>
          <small>Included - no price difference</small>
        </span>
        <span class="lynqr-room-control__status">${escapeHtml(paletteName)}</span>
      </summary>
      <div class="lynqr-room-control__body">
        <div class="lynqr-room-palette-grid" role="radiogroup" aria-label="${escapeHtml(room.name)} material palette"></div>
      </div>
    `;

    const paletteGrid = paletteDetails.querySelector(
      ".lynqr-room-palette-grid",
    );
    const palettes = availablePalettes(room.handle);

    if (!palettes.length) {
      paletteGrid.innerHTML =
        '<p class="lynqr-room-control__empty">No palette is configured for this room and theme.</p>';
    } else {
      palettes.forEach((palette) => {
        const checked = selection.palette?.handle === palette.handle;
        const thumbnail = roomPaletteThumbnail(room.handle, state.selectedTheme?.handle, palette);
        const label = document.createElement("label");
        label.className = `lynqr-room-choice-card${checked ? " is-selected" : ""}`;
        label.innerHTML = `
          <input type="radio" name="palette-${escapeHtml(room.handle)}" value="${escapeHtml(palette.handle)}" ${checked ? "checked" : ""}>
          ${
            thumbnail
              ? `<img src="${escapeHtml(thumbnail)}" alt="" loading="lazy" decoding="async">`
              : '<span class="lynqr-room-choice-card__swatch" aria-hidden="true"></span>'
          }
          <span>
            <strong>${escapeHtml(palette.name)}</strong>
          </span>
        `;
        label.querySelector("input").addEventListener("change", () => {
          if (selectRoomPalette(room.handle, palette.handle)) {
            const current = document.querySelector(
              `.lynqr-room-control--palette[data-room-palette="${room.handle}"]`,
            );
            if (current) current.open = false;
          }
        });
        paletteGrid.appendChild(label);
      });
    }

    container.appendChild(paletteDetails);

    renderRoomOptionControls(container, room, selection);
  }

  function roomOptionPresentationKey(selection) {
    return JSON.stringify([
      optionGalleryIdentity(selection?.palette),
      ...activeOptionGroups(selection).map((group) => [
        group.handle || group.slug,
        (group.options || [])
          .filter((option) => roomOptionIsEligible(option, selection))
          .map((option) => option.id || option.handle || option.slug),
        roomOptionSubgroupDependency(selection, group),
        (group.options || [])
          .filter((option) => option.subgroup)
          .map((option) => optionGalleryIdentity(
            roomOptionSubgroupContextOption(selection, group, option.subgroup),
          )),
      ]),
    ]);
  }

  function renderRoomOptionControls(container, room, selection) {
    const openGroups = new Map();
    container
      .querySelectorAll("[data-room-option-group]")
      .forEach((details) => {
        openGroups.set(details.dataset.roomOptionGroup, details.open);
        details.remove();
      });
    container._roomOptionPresentationKey = roomOptionPresentationKey(selection);
    container._roomOptionTheme = state.selectedTheme?.handle || "";
    container._roomOptionThemeData =
      state.themeData[container._roomOptionTheme];

    const sortedOptionGroups = [...activeOptionGroups(selection)].sort(
      (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
    );
    sortedOptionGroups.forEach((group) => {
      const groupHandle = group.handle || group.slug;
      const selected = selectedRoomOption(selection, groupHandle)?.option;
      const isPaid = optionGroupIsPaid(group);
      const details = document.createElement("details");
      details.className = `lynqr-room-control lynqr-room-control--single${isPaid ? " lynqr-room-control--paid" : ""}`;
      details.dataset.roomOptionGroup = groupHandle;
      details.open = group.required === true && !selected;
      if (openGroups.has(groupHandle))
        details.open = openGroups.get(groupHandle);
      details.innerHTML = `
          <summary>
            <span>
              <strong>${escapeHtml(group.name)}</strong>
              <small>${group.required === true ? "Required" : "Optional"} - choose one${isPaid ? ", price varies" : ", no price difference"}</small>
            </span>
            <span class="lynqr-room-control__status" data-option-status>${escapeHtml(selected ? `${selected.name}${isPaid ? ` - ${currency(selected.price)}` : ""}` : group.required === true ? "Choose one" : "Not selected")}</span>
          </summary>
          <div class="lynqr-room-control__body">
            ${group.description ? `<p class="lynqr-room-control__description">${escapeHtml(group.description)}</p>` : ""}
            <div class="lynqr-room-option-subgroups" role="radiogroup" aria-label="${escapeHtml(group.name)}"></div>
          </div>
        `;

      const optionSubgroups = details.querySelector(
        ".lynqr-room-option-subgroups",
      );
      if (group.required !== true) {
        const noneContainer = document.createElement("div");
        noneContainer.className =
          "lynqr-room-segmented lynqr-room-option-subgroup__options lynqr-room-option-none";
        const noneLabel = document.createElement("label");
        noneLabel.className = `lynqr-room-segment${selected ? "" : " is-selected"}`;
        noneLabel.innerHTML = `
            <input type="radio" name="option-${escapeHtml(room.handle)}-${escapeHtml(groupHandle)}" value="" ${selected ? "" : "checked"}>
            <span><strong>No upgrade</strong><small>— keep standard package</small></span>
          `;
        noneLabel.querySelector("input").addEventListener("change", () => {
          clearRoomOption(room.handle, groupHandle);
        });
        noneContainer.appendChild(noneLabel);
        optionSubgroups.appendChild(noneContainer);
      }

      const eligibleOptions = (group.options || []).filter((option) =>
        roomOptionIsEligible(option, selection),
      );
      const isSingleOption = eligibleOptions.length === 1;
      if (!eligibleOptions.length && (group.options || []).length) {
        const message = document.createElement("p");
        message.className = "lynqr-room-option-compatibility-message";
        message.setAttribute("role", "status");
        message.textContent =
          "Complete the required selection above to see compatible options.";
        optionSubgroups.appendChild(message);
      }
      const displayGroups = groupRoomOptionsForDisplay(eligibleOptions);
      const hasNamedDisplayGroup = displayGroups.some(
        (displayGroup) => displayGroup.name,
      );
      displayGroups.forEach((displayGroup) => {
        const subgroup = document.createElement("section");
        subgroup.className = "lynqr-room-option-subgroup";
        const subgroupEntity = displayGroup.options[0]?.subgroup;
        const subgroupGallery = resolveRoomOptionGallery(
          subgroupEntity,
          state.selectedTheme,
          roomOptionSubgroupDependency(selection, group),
          { selection, group, targetType: "subgroup" },
        );
        let subgroupHeader = subgroup;
        if (subgroupGallery.length) {
          subgroupHeader = document.createElement("header");
          subgroupHeader.className = "lynqr-room-option-subgroup__media-header";
          subgroup.appendChild(subgroupHeader);
          subgroupHeader.appendChild(
            createRoomOptionGalleryButton(
              subgroupEntity,
              subgroupGallery,
              null,
              true,
              { selection, group, targetType: "subgroup" },
            ),
          );
        }
        const displayGroupTitle =
          displayGroup.name || (hasNamedDisplayGroup ? "Other options" : "");
        if (displayGroupTitle) {
          const heading = document.createElement("h4");
          heading.className = "lynqr-room-option-subgroup__title";
          heading.textContent = displayGroupTitle;
          subgroupHeader.appendChild(heading);
        }

        if (displayGroup.description) {
          const description = document.createElement("div");
          description.className = "lynqr-room-option-subgroup__description rte";
          description.innerHTML = displayGroup.description;
          subgroupHeader.appendChild(description);
        }

        const optionContainer = document.createElement("div");
        optionContainer.className =
          "lynqr-room-segmented lynqr-room-option-subgroup__options";

        let imageGrid = null;
        displayGroup.options.forEach((option) => {
          const optionHandle = option.handle || option.slug;
          const checked = roomOptionIsSelected(
            room.handle,
            groupHandle,
            optionHandle,
          );
          const gallery = resolveRoomOptionGallery(
            option, state.selectedTheme, "", { selection },
          );
          const isOptionalImageOption =
            group.required !== true && gallery.length > 0;
          const label = document.createElement("label");
          label.className = `lynqr-room-segment${checked ? " is-selected" : ""}`;
          const optionInputName = `option-${escapeHtml(room.handle)}-${escapeHtml(groupHandle)}`;
          const description = renderShopifyRichText(option.description);
          label.innerHTML = isOptionalImageOption
            ? `
                <input type="radio" name="${optionInputName}" value="${escapeHtml(optionHandle)}" ${checked ? "checked" : ""}>
                <span class="lynqr-room-option-upgrade-card__details">
                  <span class="lynqr-room-option-upgrade-card__header">
                    <strong class="lynqr-room-option-upgrade-card__name">${escapeHtml(option.name)}</strong>
                    ${isPaid ? `<small class="lynqr-room-option-upgrade-card__price">+ ${escapeHtml(currency(option.price))}</small>` : ""}
                  </span>
                  ${description ? `<div class="lynqr-room-option-upgrade-card__description lynqr-rich-text">${description}</div>` : ""}
                  <small class="lynqr-room-option-upgrade-card__meta">Optional Upgrade <span>${checked ? "Selected" : "Select to add"}</span></small>
                </span>
              `
            : `
                <input type="radio" name="${optionInputName}" value="${escapeHtml(optionHandle)}" ${checked ? "checked" : ""}>
                <span><strong>${escapeHtml(option.name)}</strong>${isPaid ? `<small class="lynqr-room-segment__price">+ ${escapeHtml(currency(option.price))}</small>` : ""}${description ? `<div class="lynqr-room-option-description lynqr-rich-text">${description}</div>` : ""}</span>
              `;
          label.querySelector("input").addEventListener("change", () => {
            selectRoomOption(room.handle, groupHandle, optionHandle);
          });
          if (isSingleOption) {
            const card = document.createElement("div");
            card.className = isOptionalImageOption
              ? "lynqr-room-option-image-card lynqr-room-option-upgrade-card"
              : "lynqr-room-option-image-card lynqr-room-option-featured-card";
            if (gallery.length) {
              card.appendChild(
                createRoomOptionGalleryButton(
                  option,
                  gallery,
                  isPaid ? option.price : null,
                  false,
                  { selection },
                ),
              );
            }
            if (!isOptionalImageOption) {
              const action = document.createElement("span");
              action.className = "lynqr-room-option-featured-card__action";
              action.innerHTML =
                '<span class="lynqr-room-option-featured-card__select">Select</span><span class="lynqr-room-option-featured-card__selected">Selected</span>';
              label.querySelector("span").appendChild(action);
            }
            card.appendChild(label);
            optionContainer.appendChild(card);
          } else if (gallery.length) {
            if (isOptionalImageOption) {
              const card = document.createElement("div");
              card.className =
                "lynqr-room-option-image-card lynqr-room-option-upgrade-card";
              card.appendChild(
                createRoomOptionGalleryButton(
                  option,
                  gallery,
                  isPaid ? option.price : null,
                  false,
                  { selection },
                ),
              );
              card.appendChild(label);
              optionContainer.appendChild(card);
            } else {
              if (!imageGrid) {
                imageGrid = document.createElement("div");
                imageGrid.className = "lynqr-room-option-image-grid";
                optionContainer.appendChild(imageGrid);
              }
              const card = document.createElement("div");
              card.className = "lynqr-room-option-image-card";
              card.appendChild(
                createRoomOptionGalleryButton(
                  option,
                  gallery,
                  isPaid ? option.price : null,
                  false,
                  { selection },
                ),
              );
              card.appendChild(label);
              imageGrid.appendChild(card);
            }
          } else {
            imageGrid = null;
            optionContainer.appendChild(label);
          }
        });
        subgroup.appendChild(optionContainer);
        optionSubgroups.appendChild(subgroup);
      });

      container.appendChild(details);
    });
  }

  function refreshRoomOptionSelection(roomHandle) {
    const selection = roomSelection(roomHandle);
    const card = document.querySelector(
      `.lynqr-room-card[data-room="${roomHandle}"]`,
    );
    if (!selection || !card) return;

    const container = card.querySelector("[data-room-controls]");
    const themeHandle = state.selectedTheme?.handle || "";
    if (
      container &&
      (container._roomOptionPresentationKey !==
        roomOptionPresentationKey(selection) ||
        container._roomOptionTheme !== themeHandle ||
        container._roomOptionThemeData !== state.themeData[themeHandle])
    ) {
      renderRoomOptionControls(container, selection.room, selection);
    }

    getEffectiveRoomConfiguration(selection).optionGroups.forEach((group) => {
      const groupHandle = group.handle || group.slug;
      const selected = selectedRoomOption(selection, groupHandle)?.option;
      const details = card.querySelector(
        `[data-room-option-group="${groupHandle}"]`,
      );
      if (!details) return;
      const status = details.querySelector("[data-option-status]");
      if (status) {
        status.textContent = selected
          ? `${selected.name}${optionGroupIsPaid(group) ? ` - ${currency(selected.price)}` : ""}`
          : group.required === true
            ? "Choose one"
            : "Not selected";
      }
      details.querySelectorAll(".lynqr-room-segment").forEach((label) => {
        const input = label.querySelector("input");
        const checked = input?.value
          ? roomOptionIsSelected(roomHandle, groupHandle, input.value)
          : !selected;
        if (input) input.checked = checked;
        label.classList.toggle("is-selected", checked);
      });
    });
  }

  function roomOptionSubgroupDependency(selection, group) {
    const referencedValues = new Set(
      (group?.options || []).flatMap((option) =>
        normalizeRoomOptionCompatibility(
          option?.compatibleWith ?? option?.compatible_with,
        ).flatMap((reference) =>
          roomOptionReferences(
            typeof reference === "string" ? { id: reference } : reference,
          ),
        ),
      ),
    );
    for (const { option } of Object.values(selection?.options || {})) {
      const dependency = [option?.slug, option?.handle, option?.system?.handle]
        .map((value) =>
          String(value?.value ?? value ?? "")
            .trim()
            .toLowerCase(),
        )
        .find(
          (handle) => handle === "kompacplus" || handle === "sintered-stone",
        );
      if (
        dependency &&
        roomOptionReferences(option).some((id) => referencedValues.has(id))
      ) {
        return dependency;
      }
    }
    return "";
  }

  // Context is explicit: only selected Values referenced by this Subgroup's
  // compatibility data qualify. Ambiguous/missing context uses the generic tier.
  function roomOptionSubgroupContextOption(selection, group, subgroup) {
    const subgroupIds = new Set(roomOptionReferences(subgroup));
    const references = new Set(
      (group?.options || [])
        .filter((option) => roomOptionReferences(option.subgroup).some((id) => subgroupIds.has(id)))
        .flatMap((option) => normalizeRoomOptionCompatibility(option.compatibleWith ?? option.compatible_with))
        .flatMap((reference) => roomOptionReferences(typeof reference === "string" ? { id: reference } : reference)),
    );
    const selected = Object.values(selection?.options || {})
      .map(({ option }) => option)
      .filter((option) => roomOptionReferences(option).some((id) => references.has(id)));
    return selected.length === 1 ? selected[0] : null;
  }

  function resolveRoomOptionGallery(entity, selectedTheme, dependencyHandle = "", context = {}) {
    return inspectRoomOptionGallery(entity, selectedTheme, dependencyHandle, context).images;
  }

  function inspectRoomOptionGallery(entity, selectedTheme, dependencyHandle = "", context = {}) {
    const theme = optionGalleryIdentity(selectedTheme);
    const palette = optionGalleryIdentity(
      context.palette || context.selection?.palette,
    );
    const target = optionGalleryIdentity(entity);
    const subgroup = context.targetType === "subgroup";
    const optionValue = subgroup
      ? context.optionValue || roomOptionSubgroupContextOption(context.selection, context.group, entity)
      : entity;
    const value = optionGalleryIdentity(optionValue);
    const result = {
      targetType: subgroup ? "subgroup" : "value",
      targetHandle: target,
      subgroupHandle: subgroup ? target : "",
      optionValueHandle: value,
      theme,
      materialPalette: palette,
      source: "none",
      optionGalleryHandle: null,
      images: [],
    };
    const index = registerOptionGalleries(state.themeData[theme]);
    const keys = subgroup
      ? [
          value && optionGalleryKey("subgroup-value", target, value, theme, palette),
          optionGalleryKey("subgroup", target, theme, palette),
        ]
      : [optionGalleryKey("value", target, theme, palette)];
    if (entity && theme && palette && target) {
      for (const key of keys) {
        const record = key && index?.get(key);
        if (!record?.images?.length) continue;
        return {
          ...result,
          images: record.images,
          optionGalleryHandle: record.handle,
          source: key.startsWith("subgroup-value|")
            ? "option-gallery-subgroup-exact"
            : subgroup ? "option-gallery-subgroup-generic" : "option-gallery-exact",
        };
      }
    }
    const images = resolveExistingRoomOptionGallery(entity, selectedTheme, dependencyHandle);
    const loaded = state.themeData[theme]?.roomOptionGalleries?.[entity?.id || entity?.handle];
    const sources = state.themeData[theme]?.roomOptionGallerySources?.[entity?.id || entity?.handle];
    const usesDependency = Array.isArray(loaded?.dependencyGalleries?.[dependencyHandle])
      && loaded.dependencyGalleries[dependencyHandle].length > 0;
    return {
      ...result,
      images,
      source: !images.length ? "none"
        : (usesDependency ? sources?.dependencies?.[dependencyHandle] : sources?.gallery)
          || "existing-gallery-unresolved",
    };
  }

  function resolveExistingRoomOptionGallery(
    entity,
    selectedTheme,
    dependencyHandle = "",
  ) {
    if (!entity) return [];
    const themeHandle = String(selectedTheme?.handle || selectedTheme || "")
      .trim()
      .toLowerCase();
    // Liquid resolves selected Theme -> generic -> [] before sending the lazy payload.
    const loaded =
      state.themeData[themeHandle]?.roomOptionGalleries?.[
        entity.id || entity.handle
      ] || [];
    const dependencyImages = loaded.dependencyGalleries?.[dependencyHandle];
    const images =
      Array.isArray(dependencyImages) && dependencyImages.length
        ? dependencyImages
        : Array.isArray(loaded)
          ? loaded
          : loaded.galleryImages || [];
    const seen = new Set();
    const optionImages = Array.isArray(images)
      ? images.flatMap((raw) =>
          normalizeGalleryImages([raw]).map((item) => ({
            ...item,
            alt: String(raw?.alt || "").trim(),
          })),
        )
      : [];
    return optionImages.filter((item) => {
      const key = galleryImageKey(item.image);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function createRoomOptionGalleryButton(
    entity,
    gallery,
    price = null,
    richDescription = false,
    subgroupContext = null,
  ) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "lynqr-room-option-gallery-preview";
    button.dataset.roomOptionGalleryOpen = "";
    button.setAttribute("aria-label", `View gallery for ${entity.name}`);
    button._roomOptionEntity = entity;
    button._roomOptionPrice = price;
    button._roomOptionRichDescription = richDescription;
    button._roomOptionGalleryContext = subgroupContext;
    if (subgroupContext?.targetType === "subgroup")
      button._roomOptionSubgroupContext = subgroupContext;
    button.innerHTML = `<img src="${escapeHtml(gallery[0].thumbnail || gallery[0].image)}" alt="${escapeHtml(entity.name)}" loading="lazy" decoding="async">`;
    initialiseRoomOptionGalleryLightbox();
    return button;
  }

  function initialiseRoomOptionGalleryLightbox() {
    if (document.querySelector("[data-lynqr-room-option-lightbox]")) return;
    const lightbox = document.createElement("div");
    lightbox.className = "lynqr-room-option-lightbox";
    lightbox.dataset.lynqrRoomOptionLightbox = "";
    lightbox.hidden = true;
    lightbox.setAttribute("role", "dialog");
    lightbox.setAttribute("aria-modal", "true");
    lightbox.setAttribute(
      "aria-labelledby",
      "lynqr-room-option-lightbox-title",
    );
    lightbox.innerHTML = `
      <button type="button" class="lynqr-room-option-lightbox__backdrop" data-room-option-lightbox-close aria-label="Close option gallery"></button>
      <div class="lynqr-room-option-lightbox__panel">
        <button type="button" class="lynqr-room-option-lightbox__close" data-room-option-lightbox-close aria-label="Close option gallery">&times;</button>
        <div class="lynqr-room-option-lightbox__stage">
          <button type="button" class="lynqr-room-option-lightbox__nav lynqr-room-option-lightbox__nav--previous" data-room-option-gallery-previous aria-label="Previous image">&#8249;</button>
          <img class="lynqr-room-option-lightbox__image" alt="">
          <button type="button" class="lynqr-room-option-lightbox__nav lynqr-room-option-lightbox__nav--next" data-room-option-gallery-next aria-label="Next image">&#8250;</button>
        </div>
        <div class="lynqr-room-option-lightbox__content">
          <div class="lynqr-room-option-lightbox__heading">
            <h3 id="lynqr-room-option-lightbox-title"></h3>
            <span class="lynqr-room-option-lightbox__price"></span>
          </div>
          <div class="lynqr-room-option-lightbox__description rte lynqr-rich-text"></div>
          <div class="lynqr-room-option-lightbox__count" aria-live="polite"></div>
        </div>
      </div>`;
    document.body.appendChild(lightbox);
    let activeImages = [];
    let activeIndex = 0;
    let previousFocus = null;
    let activeTheme = "";
    let activeEntity = null;
    let richDescription = false;
    const navigation = lightbox.querySelectorAll(
      ".lynqr-room-option-lightbox__nav",
    );
    const image = lightbox.querySelector(".lynqr-room-option-lightbox__image");
    const close = () => {
      lightbox.hidden = true;
      document.body.classList.remove("lynqr-room-option-lightbox-open");
      image.removeAttribute("src");
      activeImages = [];
      previousFocus?.focus?.();
    };
    const renderImage = () => {
      if (!activeImages[activeIndex]) return;
      image.src = activeImages[activeIndex].image;
      const caption = activeImages[activeIndex].alt;
      const description = lightbox.querySelector(
        ".lynqr-room-option-lightbox__description",
      );
      const fallback = activeEntity?.description || "";
      if (caption) {
        description.textContent = caption;
        description.hidden = false;
      } else {
        const html = richDescription ? fallback : renderShopifyRichText(fallback);
        description.innerHTML = html;
        description.hidden = !String(html).trim();
      }
      image.alt = caption || "";
      navigation.forEach((button) => {
        button.hidden = activeImages.length < 2;
      });
      lightbox.querySelector(".lynqr-room-option-lightbox__count").textContent =
        activeImages.length > 1
          ? `${activeIndex + 1} / ${activeImages.length}`
          : "";
    };
    const navigate = (direction) => {
      if (activeImages.length < 2) return;
      activeIndex =
        (activeIndex + direction + activeImages.length) % activeImages.length;
      renderImage();
    };
    document.addEventListener("click", (event) => {
      const trigger = event.target?.closest?.(
        "[data-room-option-gallery-open]",
      );
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        const subgroupContext = trigger._roomOptionSubgroupContext;
        activeImages = resolveRoomOptionGallery(
          trigger._roomOptionEntity,
          state.selectedTheme,
          subgroupContext
            ? roomOptionSubgroupDependency(
                subgroupContext.selection,
                subgroupContext.group,
              )
            : "",
          trigger._roomOptionGalleryContext || {},
        );
        if (!activeImages.length) return;
        previousFocus = trigger;
        activeTheme = state.selectedTheme?.handle || "";
        activeIndex = 0;
        const entity = trigger._roomOptionEntity;
        activeEntity = entity;
        richDescription = trigger._roomOptionRichDescription;
        lightbox.querySelector("h3").textContent = entity.name;
        const price = lightbox.querySelector(
          ".lynqr-room-option-lightbox__price",
        );
        price.hidden = trigger._roomOptionPrice === null;
        price.textContent = price.hidden
          ? ""
          : `+ ${currency(trigger._roomOptionPrice)}`;
        renderImage();
        lightbox.hidden = false;
        document.body.classList.add("lynqr-room-option-lightbox-open");
        lightbox.querySelector(".lynqr-room-option-lightbox__close").focus();
        return;
      }
      if (lightbox.hidden) return;
      if (event.target?.closest?.("[data-room-option-lightbox-close]")) close();
      else if (event.target?.closest?.("[data-room-option-gallery-previous]"))
        navigate(-1);
      else if (event.target?.closest?.("[data-room-option-gallery-next]"))
        navigate(1);
    });
    document.addEventListener("keydown", (event) => {
      if (lightbox.hidden) return;
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        navigate(event.key === "ArrowLeft" ? -1 : 1);
      }
      if (event.key === "Tab") {
        const controls = [...lightbox.querySelectorAll("button")].filter(
          (button) =>
            !button.hidden &&
            !button.classList.contains("lynqr-room-option-lightbox__backdrop"),
        );
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
    on("theme.changed", () => {
      if (
        !lightbox.hidden &&
        activeTheme !== (state.selectedTheme?.handle || "")
      )
        close();
    });
  }

  function refreshRoomConfiguration(roomHandle) {
    const context = buildRoomGalleryContext(roomHandle);
    const variant = resolveRoomGallery(roomHandle, context);
    refreshRoomImage(roomHandle, variant, context);
    if (variant) renderRoomGallery(roomHandle, variant);
    renderConfigurationSummary();
  }

  function initialiseRoomImageLightbox() {
    if (document.querySelector("[data-lynqr-room-lightbox]")) return;

    const lightbox = document.createElement("div");
    lightbox.className = "lynqr-room-lightbox";
    lightbox.dataset.lynqrRoomLightbox = "";
    lightbox.hidden = true;
    lightbox.setAttribute("role", "dialog");
    lightbox.setAttribute("aria-modal", "true");
    lightbox.setAttribute("aria-label", "Room image gallery");
    lightbox.innerHTML = `
      <button type="button" class="lynqr-room-lightbox__backdrop" data-lightbox-close aria-label="Close expanded image"></button>
      <div class="lynqr-room-lightbox__panel">
        <button type="button" class="lynqr-room-lightbox__close" data-lightbox-close aria-label="Close expanded image">&times;</button>
        <div class="lynqr-room-lightbox__stage">
          <button type="button" class="lynqr-room-lightbox__nav lynqr-room-lightbox__nav--previous" data-room-gallery-previous aria-label="Previous room image">&#8249;</button>
          <img class="lynqr-room-lightbox__image" alt="">
          <button type="button" class="lynqr-room-lightbox__nav lynqr-room-lightbox__nav--next" data-room-gallery-next aria-label="Next room image">&#8250;</button>
        </div>
        <div class="lynqr-room-lightbox__footer">
          <div class="lynqr-room-lightbox__thumbnails" data-room-lightbox-thumbnails></div>
          <div class="lynqr-room-lightbox__count" aria-live="polite"></div>
        </div>
      </div>
    `;
    document.body.appendChild(lightbox);

    let activeImages = [];
    let activeIndex = 0;
    let previousFocus = null;

    const renderImage = () => {
      const current = activeImages[activeIndex];
      if (!current?.image) return;
      const image = lightbox.querySelector(".lynqr-room-lightbox__image");
      image.src = current.image;
      image.alt = `Room image ${activeIndex + 1}`;
      const hasMultiple = activeImages.length > 1;
      lightbox
        .querySelectorAll(".lynqr-room-lightbox__nav")
        .forEach((button) => {
          button.hidden = !hasMultiple;
        });
      lightbox.querySelector(".lynqr-room-lightbox__count").textContent =
        hasMultiple ? `${activeIndex + 1} / ${activeImages.length}` : "";
      lightbox
        .querySelectorAll("[data-room-lightbox-image]")
        .forEach((button, index) => {
          const selected = index === activeIndex;
          button.classList.toggle("is-active", selected);
          button.setAttribute("aria-current", selected ? "true" : "false");
        });
    };

    const close = () => {
      lightbox.hidden = true;
      document.body.classList.remove("lynqr-lightbox-open");
      lightbox
        .querySelector(".lynqr-room-lightbox__image")
        .removeAttribute("src");
      lightbox
        .querySelector("[data-room-lightbox-thumbnails]")
        .replaceChildren();
      activeImages = [];
      previousFocus?.focus?.();
    };

    const open = (preview) => {
      const source = preview.querySelector(".lynqr-room-image");
      if (!source?.src) return;
      const roomHandle = preview.dataset.roomPreview || "";
      const variant = resolveRoomGallery(roomHandle);
      const displayedImage = source.currentSrc || source.src;
      activeImages = roomLightboxImages(variant, displayedImage);
      if (!activeImages.length) return;
      const displayedKey = galleryImageKey(displayedImage);
      activeIndex = Math.max(
        0,
        activeImages.findIndex(
          (item) => galleryImageKey(item.image) === displayedKey,
        ),
      );
      previousFocus = preview;
      const thumbnails = lightbox.querySelector(
        "[data-room-lightbox-thumbnails]",
      );
      thumbnails.innerHTML = activeImages
        .map(
          (item, index) => `
            <button type="button" class="lynqr-room-lightbox__thumbnail" data-room-lightbox-image="${index}" aria-label="View room image ${index + 1}">
              <img src="${escapeHtml(item.thumbnail || item.image)}" alt="" loading="lazy" decoding="async">
            </button>
          `,
        )
        .join("");
      thumbnails.hidden = activeImages.length < 2;
      renderImage();
      lightbox.hidden = false;
      document.body.classList.add("lynqr-lightbox-open");
      lightbox.querySelector(".lynqr-room-lightbox__close")?.focus();
    };

    let touchStartX = null;
    const stage = lightbox.querySelector(".lynqr-room-lightbox__stage");
    stage?.addEventListener(
      "touchstart",
      (event) => {
        touchStartX = event.touches?.[0]?.clientX ?? null;
      },
      { passive: true },
    );
    stage?.addEventListener(
      "touchend",
      (event) => {
        if (touchStartX === null || activeImages.length < 2) return;
        const endX = event.changedTouches?.[0]?.clientX;
        const distance = Number(endX) - touchStartX;
        touchStartX = null;
        if (Math.abs(distance) < 40) return;
        activeIndex =
          distance > 0
            ? (activeIndex - 1 + activeImages.length) % activeImages.length
            : (activeIndex + 1) % activeImages.length;
        renderImage();
      },
      { passive: true },
    );

    document.addEventListener("click", (event) => {
      const closeControl = event.target?.closest?.("[data-lightbox-close]");
      if (closeControl) {
        close();
        return;
      }
      if (event.target?.closest?.("[data-room-gallery-previous]")) {
        activeIndex =
          (activeIndex - 1 + activeImages.length) % activeImages.length;
        renderImage();
        return;
      }
      if (event.target?.closest?.("[data-room-gallery-next]")) {
        activeIndex = (activeIndex + 1) % activeImages.length;
        renderImage();
        return;
      }
      const thumbnail = event.target?.closest?.("[data-room-lightbox-image]");
      if (thumbnail) {
        activeIndex = Number(thumbnail.dataset.roomLightboxImage || 0);
        renderImage();
        return;
      }
      const preview = event.target?.closest?.("[data-room-preview]");
      if (preview?.querySelector(".lynqr-room-image")) open(preview);
    });
    document.addEventListener("keydown", (event) => {
      if (!lightbox.hidden && event.key === "Escape") close();
      if (
        !lightbox.hidden &&
        event.key === "ArrowLeft" &&
        activeImages.length > 1
      ) {
        activeIndex =
          (activeIndex - 1 + activeImages.length) % activeImages.length;
        renderImage();
      }
      if (
        !lightbox.hidden &&
        event.key === "ArrowRight" &&
        activeImages.length > 1
      ) {
        activeIndex = (activeIndex + 1) % activeImages.length;
        renderImage();
      }
      if (
        (event.key === "Enter" || event.key === " ") &&
        event.target.matches?.("[data-room-preview]")
      ) {
        event.preventDefault();
        open(event.target);
      }
    });
  }

  function initialiseUpgradeGalleryLightbox() {
    if (document.querySelector("[data-lynqr-upgrade-lightbox]")) return;

    const lightbox = document.createElement("div");
    lightbox.className = "lynqr-upgrade-lightbox";
    lightbox.dataset.lynqrUpgradeLightbox = "";
    lightbox.hidden = true;
    lightbox.setAttribute("role", "dialog");
    lightbox.setAttribute("aria-modal", "true");
    lightbox.setAttribute("aria-labelledby", "lynqr-upgrade-lightbox-title");
    lightbox.innerHTML = `
      <button type="button" class="lynqr-upgrade-lightbox__backdrop" data-upgrade-lightbox-close aria-label="Close upgrade gallery"></button>
      <div class="lynqr-upgrade-lightbox__panel">
        <button type="button" class="lynqr-upgrade-lightbox__close" data-upgrade-lightbox-close aria-label="Close upgrade gallery">&times;</button>
        <div class="lynqr-upgrade-lightbox__stage">
          <button type="button" class="lynqr-upgrade-lightbox__nav lynqr-upgrade-lightbox__nav--previous" data-upgrade-gallery-previous aria-label="Previous image">&#8249;</button>
          <img class="lynqr-upgrade-lightbox__image" alt="">
          <button type="button" class="lynqr-upgrade-lightbox__nav lynqr-upgrade-lightbox__nav--next" data-upgrade-gallery-next aria-label="Next image">&#8250;</button>
        </div>
        <div class="lynqr-upgrade-lightbox__content">
          <div class="lynqr-upgrade-lightbox__heading">
            <h3 id="lynqr-upgrade-lightbox-title"></h3>
            <span class="lynqr-upgrade-lightbox__price"></span>
          </div>
          <div class="lynqr-upgrade-lightbox__description rte lynqr-rich-text"></div>
          <div class="lynqr-upgrade-lightbox__count" aria-live="polite"></div>
        </div>
      </div>
    `;
    document.body.appendChild(lightbox);

    let activeImages = [];
    let activeIndex = 0;
    let previousFocus = null;

    const renderImage = () => {
      const current = activeImages[activeIndex];
      const image = lightbox.querySelector(".lynqr-upgrade-lightbox__image");
      if (!current?.image) return;
      // Only the currently viewed full-size image receives a src. Other
      // gallery assets therefore remain unloaded until the customer navigates.
      image.src = current.image;
      image.alt = lightbox.querySelector("h3")?.textContent || "Upgrade image";
      const hasMultiple = activeImages.length > 1;
      lightbox
        .querySelectorAll(".lynqr-upgrade-lightbox__nav")
        .forEach((button) => {
          button.hidden = !hasMultiple;
        });
      lightbox.querySelector(".lynqr-upgrade-lightbox__count").textContent =
        hasMultiple ? `${activeIndex + 1} / ${activeImages.length}` : "";
    };

    const close = () => {
      lightbox.hidden = true;
      document.body.classList.remove("lynqr-lightbox-open");
      lightbox
        .querySelector(".lynqr-upgrade-lightbox__image")
        .removeAttribute("src");
      activeImages = [];
      previousFocus?.focus?.();
    };

    const open = (trigger) => {
      const card = trigger.closest(".lynqr-upgrade");
      const roomHandle = card?.dataset.room || "";
      const upgradeHandle = card?.dataset.upgrade || "";
      const selection = roomSelection(roomHandle);
      const roomUpgrade = getEffectiveRoomConfiguration(selection).upgrades.find(
        (item) =>
          getUpgradeHandle(item) === normalizeUpgradeHandle(upgradeHandle),
      );
      if (!roomUpgrade) return;

      const loaded =
        upgradeGalleries.get(upgradeGalleryKey(roomHandle, upgradeHandle)) ||
        {};
      const thumbnail =
        loaded.thumbnail ||
        roomUpgrade.upgrade?.image ||
        roomUpgrade.upgrade?.icon ||
        trigger.querySelector("img")?.currentSrc ||
        trigger.querySelector("img")?.src ||
        "";
      activeImages = normalizeGalleryImages(loaded.galleryImages);
      if (!activeImages.length && thumbnail) {
        activeImages = [{ image: thumbnail, thumbnail }];
      }
      if (!activeImages.length) return;

      activeIndex = 0;
      previousFocus = trigger;
      const name = loaded.name || roomUpgrade.upgrade?.name || "Upgrade";
      const descriptionHtml = renderShopifyRichText(loaded.description) ||
        roomUpgradeDescription(roomUpgrade);
      lightbox.querySelector("h3").textContent = name;
      lightbox.querySelector(".lynqr-upgrade-lightbox__price").textContent =
        `+${currency(getRoomUpgradePrice(roomUpgrade))}`;
      const descriptionElement = lightbox.querySelector(
        ".lynqr-upgrade-lightbox__description",
      );
      descriptionElement.innerHTML = descriptionHtml;
      descriptionElement.hidden = !descriptionHtml;
      renderImage();
      lightbox.hidden = false;
      document.body.classList.add("lynqr-lightbox-open");
      lightbox.querySelector(".lynqr-upgrade-lightbox__close")?.focus();
    };

    document.addEventListener("click", (event) => {
      const trigger = event.target?.closest?.("[data-upgrade-gallery-open]");
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        open(trigger);
        return;
      }
      if (event.target?.closest?.("[data-upgrade-lightbox-close]")) {
        close();
        return;
      }
      if (event.target?.closest?.("[data-upgrade-gallery-previous]")) {
        activeIndex =
          (activeIndex - 1 + activeImages.length) % activeImages.length;
        renderImage();
        return;
      }
      if (event.target?.closest?.("[data-upgrade-gallery-next]")) {
        activeIndex = (activeIndex + 1) % activeImages.length;
        renderImage();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !lightbox.hidden) close();
      const trigger = event.target?.closest?.("[data-upgrade-gallery-open]");
      if (trigger && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        event.stopPropagation();
        open(trigger);
      }
      if (!lightbox.hidden && event.key === "ArrowLeft") {
        activeIndex =
          (activeIndex - 1 + activeImages.length) % activeImages.length;
        renderImage();
      }
      if (!lightbox.hidden && event.key === "ArrowRight") {
        activeIndex = (activeIndex + 1) % activeImages.length;
        renderImage();
      }
    });
  }

  /*==============================================================================
Upgrade Selector
==============================================================================*/

  function renderUpgradeSelector(roomCard, room) {
    const container = roomCard.querySelector("[data-room-upgrades]");

    if (!container) {
      return;
    }

    container.innerHTML = "";

    /*
    --------------------------------------------------------------------------
    Available Room Upgrades
    --------------------------------------------------------------------------
    */

    const upgrades = [...(room.upgrades || [])]
      .filter(
        (upgrade) => upgrade?.enabled !== false && upgrade?.enabled !== "false",
      )
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    /*
    --------------------------------------------------------------------------
    Helper: Render Room Selection Summary
    --------------------------------------------------------------------------
    */

    const renderSummary = () => {
      /*
        Remove an existing summary first so this function is safe
        to call more than once.
        */

      const existingSummary = roomCard.querySelector(
        "[data-room-upgrade-summary]",
      );

      if (existingSummary) {
        existingSummary.remove();
      }

      const summary = document.createElement("footer");

      summary.className = "lynqr-room-card__upgrade-summary";

      summary.dataset.roomUpgradeSummary = room.handle;

      summary.dataset.roomSlug = room.handle;

      summary.innerHTML = `

            <div class="lynqr-room-selection">

                <div class="lynqr-room-selection__label">
                    Selected Add-ons
                </div>

                <div
                    class="lynqr-room-selection__value"
                    data-room-selected-upgrades
                    data-room-summary="${room.handle}"
                >
                    Standard Package
                </div>

            </div>


            <div class="lynqr-room-selection">

                <div class="lynqr-room-selection__label">
                    Upgrade Total
                </div>

                <div
                    class="lynqr-room-selection__price"
                    data-room-upgrade-total
                    data-room-price="${room.handle}"
                    data-price="0"
                >
                    ${currency(0)}
                </div>

            </div>

        `;

      /*
        Put the summary AFTER the upgrade selector,
        matching the previous Liquid UI.
        */

      const config = roomCard.querySelector(".lynqr-room-card__config");

      if (config) {
        config.appendChild(summary);
      } else {
        roomCard.appendChild(summary);
      }
    };

    /*
    --------------------------------------------------------------------------
    No Active Upgrades
    --------------------------------------------------------------------------
    */

    if (!upgrades.length) {
      const addonSection = container.closest("[data-room-addons]");
      if (addonSection) addonSection.hidden = true;
      return;

      container.innerHTML = `

            <div class="lynqr-upgrades__empty">

                <div class="lynqr-upgrades__empty-icon">
                    ✓
                </div>

                <div class="lynqr-upgrades__empty-title">
                    Standard Room Included
                </div>

                <div class="lynqr-upgrades__empty-description">
                    This room is already fully included
                    in your furniture package.
                </div>

            </div>

        `;

      renderSummary();

      refreshRoomUpgradeSummary(room.handle);

      return;
    }

    /*
    --------------------------------------------------------------------------
    Header
    --------------------------------------------------------------------------
    */

    /*
    --------------------------------------------------------------------------
    Upgrade List
    --------------------------------------------------------------------------
    */

    const list = document.createElement("div");

    list.className = "lynqr-room-card__upgrade-list";

    container.appendChild(list);

    /*
    --------------------------------------------------------------------------
    Render Each Upgrade
    --------------------------------------------------------------------------
    */

    upgrades.forEach((roomUpgrade) => {
      const upgradeHandle = getUpgradeHandle(roomUpgrade);

      /*
        Invalid upgrade record
        */

      if (!upgradeHandle) {
        console.warn("[Lynqr] Room upgrade has no usable handle.", roomUpgrade);

        return;
      }

      /*
        ----------------------------------------------------------------------
        Selected State
        ----------------------------------------------------------------------
        */

      const isSelected = roomSelection(room.handle).upgrades.some(
        (selected) => getUpgradeHandle(selected) === upgradeHandle,
      );

      /*
        ----------------------------------------------------------------------
        Card
        ----------------------------------------------------------------------
        */

      const maxQuantity = getUpgradeMaxQuantity(roomUpgrade);
      const label = document.createElement(maxQuantity === null ? "label" : "div");

      label.className = `lynqr-upgrade${isSelected ? " is-selected" : ""}`;

      label.dataset.room = room.handle;

      label.dataset.upgrade = upgradeHandle;

      /*
        ----------------------------------------------------------------------
        Checkbox ID
        ----------------------------------------------------------------------
        */

      const checkboxId = `upgrade-${room.handle}-${upgradeHandle}`;

      /*
        ----------------------------------------------------------------------
        Description
        ----------------------------------------------------------------------
        */

      const description = roomUpgradeDescription(roomUpgrade);

      /*
        ----------------------------------------------------------------------
        Price
        ----------------------------------------------------------------------
        */

      const price = getRoomUpgradePrice(roomUpgrade);

      /*
        ----------------------------------------------------------------------
        Render
        ----------------------------------------------------------------------
        */

      label.innerHTML = `

            ${maxQuantity === null ? `<input
                id="${checkboxId}"
                type="checkbox"
                class="lynqr-upgrade__checkbox"
                value="${upgradeHandle}"
                data-room="${room.handle}"
                data-upgrade="${upgradeHandle}"
                ${isSelected ? "checked" : ""}
                aria-label="${roomUpgrade.upgrade?.name || "Upgrade"}"
            >` : ""}

            <span class="lynqr-upgrade__check" aria-hidden="true">
                ✓
            </span>

            <span
                class="lynqr-upgrade__media"
                ${
                  roomUpgrade.upgrade?.image || roomUpgrade.upgrade?.icon
                    ? `data-upgrade-gallery-open role="button" tabindex="0" aria-label="View ${escapeHtml(roomUpgrade.upgrade?.name || "upgrade")} gallery"`
                    : ""
                }
            >

                ${
                  roomUpgrade.upgrade?.image
                    ? `
                            <img
                                src="${roomUpgrade.upgrade.image}"
                                alt="${roomUpgrade.upgrade.name || "Upgrade"}"
                                loading="lazy"
                                decoding="async"
                            >
                        `
                    : roomUpgrade.upgrade?.icon
                      ? `
                                <img
                                    src="${roomUpgrade.upgrade.icon}"
                                    alt="${roomUpgrade.upgrade.name || "Upgrade"}"
                                    loading="lazy"
                                    decoding="async"
                                >
                            `
                      : ""
                }

            </span>

            <span class="lynqr-upgrade__details">

                <span class="lynqr-upgrade__header">

                    <strong class="lynqr-upgrade__name">
                        ${roomUpgrade.upgrade?.name || "Upgrade"}
                    </strong>

                    <span class="lynqr-upgrade__price">
                        +${currency(price)}
                    </span>

                </span>

                ${
                  description
                    ? `
                            <div class="lynqr-upgrade__description lynqr-rich-text">
                                ${description}
                            </div>
                        `
                    : ""
                }

                <span class="lynqr-upgrade__meta">

                    Optional Upgrade

                    <span>
                        Select to add
                    </span>

                </span>

            </span>

        `;

      if (maxQuantity !== null) {
        label.dataset.upgradeQuantityCard = "";
        const selectedQuantity = getUpgradeQuantity(roomSelection(room.handle).upgrades.find(
          (selected) => getUpgradeHandle(selected) === upgradeHandle,
        ));
        label.querySelector(".lynqr-upgrade__meta span").textContent =
          selectedQuantity > 0 ? `${selectedQuantity} selected` : "None selected";
        const controls = document.createElement("span");
        controls.className = "lynqr-upgrade__quantity";
        controls.setAttribute("role", "group");
        controls.setAttribute(
          "aria-label", `Quantity for ${roomUpgrade.upgrade?.name || "upgrade"}`,
        );
        const heading = document.createElement("span");
        heading.className = "lynqr-upgrade__quantity-label";
        heading.textContent = "Quantity";
        controls.appendChild(heading);
        for (let quantity = 0; quantity <= maxQuantity; quantity += 1) {
          const button = document.createElement("button");
          button.type = "button";
          button.dataset.upgradeQuantity = String(quantity);
          button.setAttribute("aria-pressed", String(quantity === selectedQuantity));
          button.textContent = quantity === 0
            ? "0" : `${quantity} ${quantity === 1 ? "unit" : "units"}`;
          button.addEventListener("click", () => {
            setUpgradeQuantity(room.handle, upgradeHandle, quantity);
          });
          controls.appendChild(button);
        }
        label.querySelector(".lynqr-upgrade__details").insertBefore(
          controls, label.querySelector(".lynqr-upgrade__meta"),
        );
      }

      list.appendChild(label);
    });

    /*
    --------------------------------------------------------------------------
    Summary
    --------------------------------------------------------------------------
    */

    renderSummary();

    /*
    --------------------------------------------------------------------------
    Synchronise Selected Styling + Summary
    --------------------------------------------------------------------------
    */

    refreshUpgradeSelection(room.handle);

    refreshRoomUpgradeSummary(room.handle);
  }
  /*==============================================================================
Upgrade Events
==============================================================================*/

  let upgradeEventsBound = false;

  function bindUpgradeEvents() {
    if (upgradeEventsBound) {
      return;
    }

    upgradeEventsBound = true;

    console.log("[Lynqr][UPGRADE] Binding upgrade events");

    document.addEventListener(
      "change",
      (event) => {
        const target = event.target;

        /*
            ------------------------------------------------------------------
            Only handle upgrade checkboxes.

            Support both current and legacy class names.
            ------------------------------------------------------------------
            */

        if (
          !target ||
          target.tagName !== "INPUT" ||
          target.type !== "checkbox" ||
          !target.matches(".lynqr-upgrade-checkbox, .lynqr-upgrade__checkbox")
        ) {
          return;
        }

        /*
            ------------------------------------------------------------------
            Resolve Room
            ------------------------------------------------------------------
            */

        let roomHandle =
          target.dataset.room ||
          target.closest("[data-room]")?.dataset.room ||
          "";

        /*
            Actual rendered checkbox currently uses:

                name="upgrade-master-bedroom"

            so use the name as a fallback.
            */

        if (!roomHandle && target.name) {
          const match = target.name.match(/^upgrade-(.+)$/);

          if (match) {
            roomHandle = match[1];
          }
        }

        /*
            ------------------------------------------------------------------
            Resolve Upgrade
            ------------------------------------------------------------------
            */

        const upgradeHandle =
          target.dataset.upgrade ||
          target.dataset.upgradeHandle ||
          target.value ||
          "";

        console.groupCollapsed("[Lynqr][UPGRADE] CHANGE");

        console.log("checkbox:", target);

        console.log("roomHandle:", roomHandle);

        console.log("upgradeHandle:", upgradeHandle);

        console.log("checked:", target.checked);

        if (!roomHandle || !upgradeHandle) {
          console.error("[Lynqr][UPGRADE] Unable to resolve upgrade identity", {
            roomHandle,
            upgradeHandle,
            dataset: {
              ...target.dataset,
            },
            name: target.name,
            value: target.value,
          });

          console.groupEnd();

          return;
        }

        /*
            ------------------------------------------------------------------
            Toggle State
            ------------------------------------------------------------------
            */

        const result = toggleUpgrade(roomHandle, upgradeHandle, target.checked);

        console.log("[Lynqr][UPGRADE] toggleUpgrade result:", result);

        console.groupEnd();
      },
      true,
    );

    console.log("[Lynqr][UPGRADE] Upgrade event binding complete");
  }

  /*==============================================================================
Theme-scoped Room Gallery Engine
==============================================================================*/

  const roomGalleries = new Map();
  const upgradeGalleries = new Map();
  const themeDataRequests = new Map();
  const optionGalleryIndexes = new WeakMap();

  function optionGalleryIdentity(entity) {
    return String(typeof entity === "string" ? entity
      : entity?.handle || entity?.system?.handle || entity?.slug || "").trim().toLowerCase();
  }

  function optionGalleryKey(...parts) {
    return parts.map(optionGalleryIdentity).join("|");
  }

  function registerOptionGalleries(payload) {
    if (!payload || typeof payload !== "object") return null;
    if (optionGalleryIndexes.has(payload)) return optionGalleryIndexes.get(payload);
    const index = new Map();
    const duplicates = new Map();
    const theme = optionGalleryIdentity(payload.theme);
    const records = Array.isArray(payload.optionGalleries)
      ? payload.optionGalleries.filter((record) => record && typeof record === "object") : [];
    const order = (record) => record.sortOrder !== null && record.sortOrder !== undefined
      && record.sortOrder !== "" && Number.isFinite(Number(record.sortOrder))
      ? Number(record.sortOrder) : Infinity;
    records.sort((a, b) => {
      const difference = order(a) - order(b);
      if (difference && !Number.isNaN(difference)) return difference;
      const left = optionGalleryIdentity(a), right = optionGalleryIdentity(b);
      return left < right ? -1 : left > right ? 1 : 0;
    });
    const handles = new Set();
    records.forEach((record) => {
      const handle = optionGalleryIdentity(record);
      const recordTheme = optionGalleryIdentity(record?.theme);
      const palette = optionGalleryIdentity(record?.materialPalette);
      const value = optionGalleryIdentity(record?.optionValue);
      const subgroup = optionGalleryIdentity(record?.optionSubgroup);
      if (record?.active !== true || !handle || !theme || recordTheme !== theme || !palette
        || (!value && !subgroup) || handles.has(handle)) return;
      handles.add(handle);
      const key = subgroup
        ? value ? optionGalleryKey("subgroup-value", subgroup, value, theme, palette)
          : optionGalleryKey("subgroup", subgroup, theme, palette)
        : optionGalleryKey("value", value, theme, palette);
      if (!duplicates.has(key)) duplicates.set(key, []);
      duplicates.get(key).push(handle);
      if (index.has(key)) return;
      const seen = new Set();
      const images = [record.heroImage, ...(Array.isArray(record.galleryImages) ? record.galleryImages : [])]
        .flatMap((raw) => normalizeGalleryImages([raw]).map((image) => ({ ...image, alt: String(raw?.alt || "").trim() })))
        .filter((image) => {
          const imageKey = galleryImageKey(image.image);
          if (!imageKey || seen.has(imageKey)) return false;
          seen.add(imageKey);
          return true;
        });
      index.set(key, { handle, images });
    });
    duplicates.forEach((handles, key) => {
      if (handles.length > 1) console.warn("[Lynqr][Option Gallery] Duplicate active exact records", { key, handles, chosen: handles[0] });
    });
    optionGalleryIndexes.set(payload, index);
    return index;
  }

  function roomGalleryKey(themeHandle, paletteHandle, roomHandle) {
    return [themeHandle, paletteHandle, roomHandle]
      .map((value) =>
        String(value || "")
          .trim()
          .toLowerCase(),
      )
      .join("|");
  }

  function upgradeGalleryKey(roomHandle, upgradeHandle) {
    return [roomHandle, upgradeHandle]
      .map((value) =>
        String(value || "")
          .trim()
          .toLowerCase(),
      )
      .join("|");
  }

  function normalizeGalleryImages(images) {
    if (!Array.isArray(images)) return [];
    return images
      .map((item) => {
        if (typeof item === "string") {
          return item ? { image: item, thumbnail: item } : null;
        }
        const image = item?.image || item?.url || "";
        return image ? { image, thumbnail: item?.thumbnail || image } : null;
      })
      .filter(Boolean);
  }

  function roomGalleryImages(variant) {
    return normalizeGalleryImages(variant?.galleryImages);
  }

  function galleryImageKey(url) {
    const source = String(url || "").trim();
    if (!source) return "";
    try {
      return new URL(source, window.location?.href || undefined).href;
    } catch (_error) {
      return source;
    }
  }

  function roomLightboxImages(variant, fallbackImage = "") {
    const images = [];
    if (variant?.image) {
      images.push({
        image: variant.image,
        thumbnail: variant.thumbnail || variant.image,
      });
    }
    images.push(...roomGalleryImages(variant));
    if (fallbackImage) {
      images.push({ image: fallbackImage, thumbnail: fallbackImage });
    }

    const seen = new Set();
    return images.filter((item) => {
      const key = galleryImageKey(item?.image);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function registerThemeData(payload) {
    registerOptionGalleries(payload);
    const galleries = Array.isArray(payload?.galleries)
      ? payload.galleries
      : [];
    galleries.forEach((gallery) => {
      if (!gallery?.theme || !gallery?.materialPalette || !gallery?.room)
        return;
      const normalized = {
        ...gallery,
        galleryImages: normalizeGalleryImages(gallery.galleryImages),
      };
      roomGalleries.set(
        roomGalleryKey(
          normalized.theme,
          normalized.materialPalette,
          normalized.room,
        ),
        normalized,
      );
    });

    const upgrades = Array.isArray(payload?.upgradeGalleries)
      ? payload.upgradeGalleries
      : [];
    upgrades.forEach((upgrade) => {
      if (!upgrade?.room || !upgrade?.handle) return;
      upgradeGalleries.set(upgradeGalleryKey(upgrade.room, upgrade.handle), {
        ...upgrade,
        galleryImages: normalizeGalleryImages(upgrade.galleryImages),
      });
    });

    refreshRoomPaletteThumbnails(payload?.theme);
    return payload;
  }

  function loadInitialRoomGalleries() {
    if (!Array.isArray(data?.roomGalleries) || !data.roomGalleries.length)
      return;
    const grouped = data.roomGalleries.reduce((result, gallery) => {
      const theme = String(gallery?.theme || "")
        .trim()
        .toLowerCase();
      if (!theme) return result;
      if (!result[theme]) result[theme] = [];
      result[theme].push(gallery);
      return result;
    }, {});
    Object.entries(grouped).forEach(([theme, galleries]) => {
      const payload = { theme, galleries, upgradeGalleries: [] };
      state.themeData[theme] = payload;
      registerThemeData(payload);
    });
  }

  function parseThemeDataResponse(markup) {
    const source = String(markup || "").trim();
    if (!source) throw new Error("Theme data response was empty.");
    if (source.startsWith("{")) return JSON.parse(source);
    const match = source.match(
      /<script[^>]*data-lynqr-theme-data[^>]*>([\s\S]*?)<\/script>/i,
    );
    if (!match?.[1]) throw new Error("Theme data JSON was not found.");
    return JSON.parse(match[1]);
  }

  function mergeThemeDataPages(pages) {
    const first = pages[0] || {};
    const galleries = new Map();
    const upgradeGalleries = new Map();
    const optionGalleries = new Map();

    pages.forEach((page) => {
      (Array.isArray(page?.optionGalleries) ? page.optionGalleries : []).forEach((gallery) => {
        if (gallery?.handle) optionGalleries.set(gallery.handle, gallery);
      });
      (Array.isArray(page?.galleries) ? page.galleries : []).forEach(
        (gallery) => {
          const key =
            gallery?.id ||
            gallery?.handle ||
            roomGalleryKey(
              gallery?.theme,
              gallery?.materialPalette,
              gallery?.room,
            );
          if (key) galleries.set(key, gallery);
        },
      );
      (Array.isArray(page?.upgradeGalleries)
        ? page.upgradeGalleries
        : []
      ).forEach((gallery) => {
        const key =
          gallery?.id || upgradeGalleryKey(gallery?.room, gallery?.handle);
        if (key) upgradeGalleries.set(key, gallery);
      });
    });

    return {
      ...first,
      pagination: { currentPage: first.pagination?.pages || 1, pages: first.pagination?.pages || 1 },
      galleries: [...galleries.values()],
      upgradeGalleries: [...upgradeGalleries.values()],
      optionGalleries: [...optionGalleries.values()],
    };
  }

  async function requestThemeDataPage(url) {
    const response = await fetch(url, {
      headers: { Accept: "text/html, application/json" },
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) {
      throw new Error(`Theme data request failed (${response.status}).`);
    }
    return parseThemeDataResponse(await response.text());
  }

  function loadThemeData(themeHandle) {
    const normalizedTheme = String(themeHandle || "")
      .trim()
      .toLowerCase();
    if (!normalizedTheme) return Promise.resolve(null);
    if (state.themeData[normalizedTheme] && !state.themeData[normalizedTheme].optionGalleryLoadFailed) {
      return Promise.resolve(state.themeData[normalizedTheme]);
    }
    if (themeDataRequests.has(normalizedTheme)) {
      return themeDataRequests.get(normalizedTheme);
    }

    const endpoint = String(data?.themeDataEndpoint || "").trim();
    if (!endpoint || typeof fetch !== "function") {
      return Promise.resolve(null);
    }

    const separator = endpoint.includes("?") ? "&" : "?";
    const query = `${normalizedTheme}__lynqr__${data.product.handle}`;
    const requestUrl = `${endpoint}${separator}q=${encodeURIComponent(query)}`;
    const request = requestThemeDataPage(requestUrl)
      .then(async (firstPage) => {
        const pageCount = Math.max(
          1,
          Number(firstPage?.pagination?.pages || 1),
        );
        const pages = [firstPage];
        for (let page = 2; page <= pageCount; page += 1) {
          pages.push(await requestThemeDataPage(`${requestUrl}&page=${page}`));
        }
        const optionPagination = firstPage?.optionGalleryPagination;
        const optionPageParam = String(optionPagination?.pageParam || "");
        let optionGalleryLoadFailed = false;
        try {
          for (let page = 2; page <= Number(optionPagination?.pages || 1); page += 1) {
            // Shopify reference lists can have their own page_<hash> parameter.
            // Reuse a Room Gallery response if both lists share 'page'.
            if (!/^page(?:_[a-z0-9]+)?$/i.test(optionPageParam)) throw new Error("Invalid Option Gallery pagination parameter.");
            const sharedPage = optionPageParam === "page" && page <= pageCount;
            const optionPage = sharedPage ? pages[page - 1]
              : await requestThemeDataPage(`${requestUrl}&${encodeURIComponent(optionPageParam)}=${page}`);
            if (optionGalleryIdentity(optionPage.theme) !== normalizedTheme
              || Number(optionPage.optionGalleryPagination?.currentPage) !== page) {
              throw new Error("Option Gallery response did not match the requested Theme/page.");
            }
            if (!sharedPage) pages.push(optionPage);
          }
        } catch (error) {
          // This optional layer must never discard the existing gallery data.
          optionGalleryLoadFailed = true;
          console.warn(`[Lynqr] Unable to load exact Option Galleries: ${normalizedTheme}`, error);
        }
        const payload = mergeThemeDataPages(pages);
        if (optionGalleryLoadFailed) {
          payload.optionGalleries = [];
          payload.optionGalleryLoadFailed = true;
        }
        return payload;
      })
      .then((payload) => {
        if (
          String(payload?.theme || "")
            .trim()
            .toLowerCase() !== normalizedTheme
        ) {
          throw new Error(
            "Theme data response did not match the selected theme.",
          );
        }
        state.themeData[normalizedTheme] = payload;
        registerThemeData(payload);
        if (state.selectedTheme?.handle === normalizedTheme) {
          refreshConfiguration();
        }
        return payload;
      })
      .catch((error) => {
        console.warn(
          `[Lynqr] Unable to load theme data: ${normalizedTheme}`,
          error,
        );
        return null;
      })
      .finally(() => {
        themeDataRequests.delete(normalizedTheme);
      });

    themeDataRequests.set(normalizedTheme, request);
    return request;
  }

  function resolveRoomGallery(roomHandle, context = {}) {
    const room = String(roomHandle?.handle || roomHandle || "")
      .trim()
      .toLowerCase();
    const selection = roomSelection(room);
    const theme = String(context.theme || state.selectedTheme?.handle || "")
      .trim()
      .toLowerCase();
    const palette = String(context.palette || selection?.palette?.handle || "")
      .trim()
      .toLowerCase();
    if (!room || !theme || !palette) return null;

    const gallery =
      roomGalleries.get(roomGalleryKey(theme, palette, room)) || null;
    if (gallery) return gallery;

    // Liquid resolves only this Theme's Room field -> Default Image -> [].
    const roomImages = normalizeGalleryImages(
      state.themeData[theme]?.roomImages?.[room],
    );
    if (roomImages.length) {
      return {
        ...gallery,
        handle: `${roomGalleryKey(theme, palette, room)}|room-images`,
        theme,
        materialPalette: palette,
        room,
        image: roomImages[0].image,
        thumbnail: roomImages[0].thumbnail,
        galleryImages: roomImages.slice(1),
      };
    }
    // Use the Room icon only after no exact Gallery or Theme image exists.
    if (!gallery?.image && !gallery?.thumbnail && selection?.room?.icon) {
      return {
        ...gallery,
        handle: `${roomGalleryKey(theme, palette, room)}|room-icon`,
        theme,
        materialPalette: palette,
        room,
        image: selection.room.icon,
        thumbnail: selection.room.icon,
        galleryImages: roomGalleryImages(gallery),
      };
    }
    if (!gallery && state.themeData[theme]) {
      const availablePalettes = [
        ...new Set(
          (state.themeData[theme]?.galleries || [])
            .filter(
              (candidate) =>
                String(candidate?.theme || "")
                  .trim()
                  .toLowerCase() === theme &&
                String(candidate?.room || "")
                  .trim()
                  .toLowerCase() === room,
            )
            .map((candidate) =>
              String(candidate?.materialPalette || "")
                .trim()
                .toLowerCase(),
            )
            .filter(Boolean),
        ),
      ];
      if (availablePalettes.length) {
        console.warn(
          `[Lynqr] Room gallery reference mismatch: room=${room}; theme=${theme}; requested palette=${palette}; available palettes=${availablePalettes.join(", ")}`,
        );
      } else {
        console.warn(
          `[Lynqr] Missing room gallery: room=${room} theme=${theme} palette=${palette}`,
        );
      }
    }
    return gallery;
  }

  function initialiseMobileSummaryDrawer() {
    const sidebar =
      ui.configuratorSidebar ||
      document.querySelector(".lynqr-configurator-sidebar");

    if (!sidebar) {
      return;
    }

    /*
    ==========================================================================
    RESPONSIVE BREAKPOINT
    ==========================================================================
    */

    const MOBILE_BREAKPOINT = 1320;

    const isMobileOrTablet = () => window.innerWidth <= MOBILE_BREAKPOINT;

    /*
    ==========================================================================
    AVOID DUPLICATION
    ==========================================================================
    */

    if (document.querySelector("[data-lynqr-mobile-summary-drawer]")) {
      return;
    }

    /*
    ==========================================================================
    ORIGINAL DOM POSITIONS
    ==========================================================================
    */

    const summary =
      ui.summaryContainer ||
      sidebar.querySelector("[data-lynqr-price-summary]");

    if (!summary) {
      return;
    }

    const existingCart = sidebar.querySelector(".lynqr-add-to-cart");
    const existingUtility = sidebar.querySelector(
      ".lynqr-configurator-utility",
    );

    const summaryPlaceholder = document.createComment(
      "LYNQR summary original position",
    );

    const cartPlaceholder = document.createComment(
      "LYNQR cart original position",
    );

    const utilityPlaceholder = document.createComment(
      "LYNQR configurator utility original position",
    );

    summary.parentNode.insertBefore(summaryPlaceholder, summary);

    if (existingCart) {
      existingCart.parentNode.insertBefore(cartPlaceholder, existingCart);
    }

    if (existingUtility) {
      existingUtility.parentNode.insertBefore(
        utilityPlaceholder,
        existingUtility,
      );
    }

    /*
    ==========================================================================
    CREATE MOBILE SUMMARY BAR
    ==========================================================================
    */

    const mobileBar = document.createElement("div");

    mobileBar.className = "lynqr-mobile-summary-bar";

    mobileBar.dataset.lynqrMobileSummaryBar = "";

    mobileBar.innerHTML = `
      <div class="lynqr-mobile-summary-bar__actions">
        <button
            type="button"
            class="lynqr-mobile-rooms-toggle"
            data-lynqr-mobile-rooms-toggle
            aria-expanded="false"
            aria-controls="lynqr-mobile-rooms-drawer"
        >
            <span>Rooms</span>
            <span aria-hidden="true">&#9662;</span>
        </button>
        <button
            type="button"
            class="lynqr-mobile-summary-toggle"
            data-lynqr-mobile-summary-toggle
            aria-expanded="false"
            aria-controls="lynqr-mobile-summary-drawer"
        >
            <span class="lynqr-mobile-summary-toggle__content">
                <span
                    class="lynqr-mobile-summary-toggle__package"
                >
                    Review
                </span>
                <span class="lynqr-mobile-summary-toggle__total">
                    <span class="lynqr-mobile-summary-toggle__separator" aria-hidden="true">&middot;</span>
                    <span
                        data-lynqr-mobile-summary-total
                    >
                        ${currency(state.totalPrice)}
                    </span>
                </span>
            </span>
        </button>
      </div>
    `;

    /*
    ==========================================================================
    CREATE BACKDROP
    ==========================================================================
    */

    const backdrop = document.createElement("div");

    backdrop.className = "lynqr-mobile-summary-backdrop";

    backdrop.dataset.lynqrMobileSummaryBackdrop = "";

    backdrop.hidden = true;

    /*
    ==========================================================================
    CREATE DRAWER
    ==========================================================================
    */

    const drawer = document.createElement("div");

    drawer.className = "lynqr-mobile-summary-drawer";

    drawer.id = "lynqr-mobile-summary-drawer";

    drawer.dataset.lynqrMobileSummaryDrawer = "";

    drawer.setAttribute("aria-hidden", "true");

    /*
    ==========================================================================
    DRAWER HEADER
    ==========================================================================
    */

    const drawerHeader = document.createElement("div");

    drawerHeader.className = "lynqr-mobile-summary-drawer__header";

    drawerHeader.innerHTML = `
        <div class="lynqr-mobile-summary-drawer__handle">
            <span></span>
        </div>

        <div class="lynqr-mobile-summary-drawer__header-row">

            <div>

                <div class="lynqr-mobile-summary-drawer__eyebrow">
                    CONFIGURATION
                </div>

                <div
                    class="lynqr-mobile-summary-drawer__title"
                    data-lynqr-mobile-summary-package
                >
                    Configuration
                </div>

            </div>

            <button
                type="button"
                class="lynqr-mobile-summary-drawer__close"
                data-lynqr-mobile-summary-close
                aria-label="Close configuration summary"
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>

        </div>
    `;

    /*
    ==========================================================================
    DRAWER BODY
    ==========================================================================
    */

    const drawerBody = document.createElement("div");

    drawerBody.className = "lynqr-mobile-summary-drawer__body";

    /*
    ==========================================================================
    DRAWER FOOTER
    ==========================================================================
    */

    const drawerFooter = document.createElement("div");

    drawerFooter.className = "lynqr-mobile-summary-drawer__footer";

    /*
    ==========================================================================
    ASSEMBLE DRAWER
    ==========================================================================
    */

    drawer.appendChild(drawerHeader);

    drawer.appendChild(drawerBody);

    drawer.appendChild(drawerFooter);

    /*
    ==========================================================================
    INSERT INTO APP
    ==========================================================================
    */

    const app =
      document.querySelector("#lynqr-package-app") ||
      document.querySelector(".lynqr-package-app");

    if (!app) {
      return;
    }

    app.appendChild(backdrop);

    app.appendChild(drawer);

    app.appendChild(mobileBar);

    /*
    ==========================================================================
    UPDATE UI REFERENCES
    ==========================================================================
    */

    ui.mobileSummaryBar = mobileBar;

    ui.mobileSummaryToggle = mobileBar.querySelector(
      "[data-lynqr-mobile-summary-toggle]",
    );

    ui.mobileSummaryDrawer = drawer;

    ui.mobileSummaryBackdrop = backdrop;

    ui.mobileSummaryClose = drawer.querySelector(
      "[data-lynqr-mobile-summary-close]",
    );

    ui.mobileSummaryTotal = mobileBar.querySelector(
      "[data-lynqr-mobile-summary-total]",
    );

    ui.mobileSummaryPackage = drawer.querySelector(
      "[data-lynqr-mobile-summary-package]",
    );

    /*
    ==========================================================================
    PACKAGE NAME
    ==========================================================================
    */

    const packageName =
      state.package?.name ||
      state.package?.title ||
      state.product?.title ||
      "Configuration";

    drawer
      .querySelectorAll("[data-lynqr-mobile-summary-package]")
      .forEach((element) => {
        element.textContent = packageName;
      });

    mobileBar
      .querySelectorAll("[data-lynqr-mobile-summary-package]")
      .forEach((element) => {
        element.textContent = "Review";
      });

    /*
    ==========================================================================
    MOVE SUMMARY INTO / OUT OF DRAWER
    ==========================================================================
    */

    let currentMobileState = null;

    const syncSummaryPlacement = () => {
      const shouldBeMobile = isMobileOrTablet();

      if (shouldBeMobile === currentMobileState) {
        return;
      }

      currentMobileState = shouldBeMobile;

      if (shouldBeMobile) {
        /*
            --------------------------------------------------------------
            MOBILE / TABLET
            --------------------------------------------------------------
            */

        drawerBody.appendChild(summary);

        if (existingCart) {
          drawerFooter.appendChild(existingCart);
        }

        if (existingUtility) {
          drawerBody.appendChild(existingUtility);
        }
      } else {
        /*
            --------------------------------------------------------------
            DESKTOP
            --------------------------------------------------------------
            */

        summaryPlaceholder.parentNode.insertBefore(
          summary,
          summaryPlaceholder.nextSibling,
        );

        if (existingCart) {
          cartPlaceholder.parentNode.insertBefore(
            existingCart,
            cartPlaceholder.nextSibling,
          );
        }

        if (existingUtility) {
          utilityPlaceholder.parentNode.insertBefore(
            existingUtility,
            utilityPlaceholder.nextSibling,
          );
        }
      }
    };

    /*
    ==========================================================================
    EVENT HANDLERS
    ==========================================================================
    */

    ui.mobileSummaryToggle.addEventListener("click", () => {
      const isOpen = drawer.classList.contains("is-open");

      setMobileSummaryDrawerState(!isOpen);
    });

    ui.mobileSummaryClose.addEventListener("click", () => {
      setMobileSummaryDrawerState(false);
    });

    backdrop.addEventListener("click", () => {
      setMobileSummaryDrawerState(false);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") {
        return;
      }

      if (!drawer.classList.contains("is-open")) {
        return;
      }

      setMobileSummaryDrawerState(false);
    });

    /*
    ==========================================================================
    RESIZE
    ==========================================================================
    */

    let resizeTimer = null;

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        syncSummaryPlacement();

        if (!isMobileOrTablet()) {
          setMobileSummaryDrawerState(false);
        }
      }, 100);
    });

    /*
    ==========================================================================
    TOUCH / SWIPE TO CLOSE
    ==========================================================================
    */

    let touchStartY = 0;
    let touchCurrentY = 0;

    drawerHeader.addEventListener(
      "touchstart",
      (event) => {
        if (!drawer.classList.contains("is-open")) {
          return;
        }

        touchStartY = event.touches[0].clientY;

        touchCurrentY = touchStartY;
      },
      {
        passive: true,
      },
    );

    drawerHeader.addEventListener(
      "touchmove",
      (event) => {
        if (!drawer.classList.contains("is-open")) {
          return;
        }

        touchCurrentY = event.touches[0].clientY;
      },
      {
        passive: true,
      },
    );

    drawerHeader.addEventListener(
      "touchend",
      () => {
        if (!drawer.classList.contains("is-open")) {
          return;
        }

        const distance = touchCurrentY - touchStartY;

        if (distance > 80) {
          setMobileSummaryDrawerState(false);
        }
      },
      {
        passive: true,
      },
    );

    /*
    ==========================================================================
    INITIAL PLACEMENT
    ==========================================================================
    */

    syncSummaryPlacement();

    /*
    ==========================================================================
    INITIAL STATE
    ==========================================================================
    */

    setMobileSummaryDrawerState(false);
  }
  function setMobileSummaryDrawerState(isOpen, restoreFocus = true) {
    const drawer =
      ui.mobileSummaryDrawer ||
      document.querySelector("[data-lynqr-mobile-summary-drawer]");

    const toggle =
      ui.mobileSummaryToggle ||
      document.querySelector("[data-lynqr-mobile-summary-toggle]");

    const backdrop =
      ui.mobileSummaryBackdrop ||
      document.querySelector("[data-lynqr-mobile-summary-backdrop]");

    const mobileBar =
      ui.mobileSummaryBar ||
      document.querySelector("[data-lynqr-mobile-summary-bar]");

    if (!drawer || !toggle || !backdrop) {
      return;
    }

    /*
    ==========================================================================
    DESKTOP
    ==========================================================================
    */

    if (window.innerWidth > 1320) {
      isOpen = false;

      document.body.classList.remove("lynqr-mobile-summary-open");
    }

    /*
    ==========================================================================
    DRAWER STATE
    ==========================================================================
    */

    drawer.classList.toggle("is-open", isOpen);

    mobileBar?.classList.toggle("is-open", isOpen);

    backdrop.classList.toggle("is-visible", isOpen);

    backdrop.hidden = !isOpen;

    drawer.setAttribute("aria-hidden", String(!isOpen));

    toggle.setAttribute("aria-expanded", String(isOpen));

    /*
    ==========================================================================
    BODY SCROLL
    ==========================================================================
    */

    document.body.classList.toggle("lynqr-mobile-summary-open", isOpen);

    /*
    ==========================================================================
    FOCUS
    ==========================================================================
    */

    if (isOpen) {
      const closeButton =
        ui.mobileSummaryClose ||
        drawer.querySelector("[data-lynqr-mobile-summary-close]");

      requestAnimationFrame(() => {
        closeButton?.focus();
      });
    } else if (restoreFocus) {
      requestAnimationFrame(() => {
        toggle.focus();
      });
    }
  }

  /*==============================================================================
  Responsive Room Navigation
  ==============================================================================*/

  let roomNavigationObserver = null;
  let roomNavigationInitialised = false;
  let activeRoomHandle = "";
  let attentionRoomHandle = "";
  let roomNavigationThemeHandle = "";
  let roomSheetPreviousFocus = null;
  let roomNavigationResizeFrame = null;

  function orderedPackageRooms() {
    return [...(state.rooms || [])].sort(
      (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0),
    );
  }

  function roomNavigationStatus(roomHandle) {
    const selection = roomSelection(roomHandle);
    const requiredGroups = activeOptionGroups(selection).filter(
      (group) => group?.required === true,
    );
    const paletteSelected = Boolean(selection?.palette?.handle);
    const requiredOptionsSelected = requiredGroups.every((group) =>
      Boolean(selection?.options?.[group.handle]),
    );
    const complete = paletteSelected && requiredOptionsSelected;
    const started =
      paletteSelected ||
      Object.keys(selection?.options || {}).length > 0 ||
      (selection?.upgrades || []).length > 0;

    if (roomHandle === activeRoomHandle) {
      return { key: "current", icon: "●", label: "Current", complete };
    }
    if (complete) {
      return { key: "complete", icon: "✓", label: "Complete", complete };
    }
    if (roomHandle === attentionRoomHandle || started) {
      return {
        key: "attention",
        icon: "!",
        label: "Needs attention",
        complete,
      };
    }
    return { key: "not-started", icon: "○", label: "Not started", complete };
  }

  function firstIncompleteRoomHandle() {
    return (
      orderedPackageRooms().find(
        (room) => !roomNavigationStatus(room.handle).complete,
      )?.handle || ""
    );
  }

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches,
    );
  }

  function scrollToConfiguratorStart() {
    const target =
      document.getElementById("lynqr-theme-step") ||
      document.querySelector("[data-lynqr-theme-selector]") ||
      document.querySelector("#lynqr-package-app");
    target?.scrollIntoView?.({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
  }

  function scrollToRoom(roomHandle, focusRoom = false) {
    const card = document.getElementById(`lynqr-room-${roomHandle}`);
    if (!card) return false;
    card.scrollIntoView?.({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
    setActiveRoom(roomHandle);
    if (focusRoom) {
      card.setAttribute("tabindex", "-1");
      window.setTimeout(() => card.focus?.({ preventScroll: true }), 350);
    }
    return true;
  }

  function scrollToFirstValidationIssue(detail = {}) {
    const rawMessage = String(detail?.message || "");

    if (rawMessage.includes("Flat Status")) {
      const target = document.getElementById("lynqr-flat-status-step");
      if (!target) return false;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        target.querySelector("[data-lynqr-flat-status-option]")?.focus?.({
          preventScroll: true,
        });
      }, 350);
      return true;
    }

    const message = rawMessage.toLowerCase();
    if (/select a theme/.test(message)) {
      scrollToConfiguratorStart();
      return true;
    }

    const explicitRoom = String(detail?.room || "")
      .trim()
      .toLowerCase();
    const matchedRoom = orderedPackageRooms().find(
      (room) =>
        room.handle === explicitRoom ||
        message.includes(String(room.name || "").toLowerCase()),
    );
    const handle = matchedRoom?.handle || firstIncompleteRoomHandle();
    if (!handle) {
      scrollToConfiguratorStart();
      return false;
    }

    attentionRoomHandle = handle;
    refreshRoomNavigationState();
    return scrollToRoom(handle, true);
  }

  function roomNavigationItem(room) {
    return `
      <li class="lynqr-room-navigation__item">
        <button type="button" class="lynqr-room-navigation__button" data-lynqr-room-jump="${escapeHtml(room.handle)}">
          <span class="lynqr-room-navigation__marker" data-room-navigation-icon aria-hidden="true">○</span>
          <span class="lynqr-room-navigation__copy">
            <strong>${escapeHtml(room.name)}</strong>
            <small data-room-navigation-status>Not started</small>
          </span>
        </button>
      </li>`;
  }

  function setActiveRoom(roomHandle) {
    if (!roomHandle || activeRoomHandle === roomHandle) return;
    activeRoomHandle = roomHandle;
    refreshRoomNavigationState();
  }

  function refreshRoomNavigationState() {
    document.querySelectorAll("[data-lynqr-room-jump]").forEach((button) => {
      const handle = button.dataset.lynqrRoomJump || "";
      const status = roomNavigationStatus(handle);
      button.dataset.roomStatus = status.key;
      button.classList.toggle("is-active", handle === activeRoomHandle);
      if (handle === activeRoomHandle) {
        button.setAttribute("aria-current", "location");
      } else {
        button.removeAttribute("aria-current");
      }
      const icon = button.querySelector("[data-room-navigation-icon]");
      const label = button.querySelector("[data-room-navigation-status]");
      if (icon) icon.textContent = status.icon;
      if (label) label.textContent = status.label;
    });
  }

  function activeRoomAtViewportPosition(cards, activationLine) {
    const positions = [...cards]
      .map((card) => ({ card, rect: card.getBoundingClientRect() }))
      .filter(
        ({ rect }) => Number.isFinite(rect.top) && Number.isFinite(rect.bottom),
      );
    if (!positions.length) return "";

    const containing = positions.find(
      ({ rect }) => rect.top <= activationLine && rect.bottom > activationLine,
    );
    if (containing) return containing.card.dataset.room || "";

    const nearest = positions.sort(
      (a, b) =>
        Math.abs(a.rect.top - activationLine) -
        Math.abs(b.rect.top - activationLine),
    )[0];
    return nearest?.card?.dataset?.room || "";
  }

  function roomNavigationActivationLine() {
    const viewportHeight = Number(window.innerHeight || 800);
    return Math.min(360, Math.max(160, viewportHeight * 0.28));
  }

  function observeRoomSections() {
    roomNavigationObserver?.disconnect?.();
    roomNavigationObserver = null;
    const cards = orderedPackageRooms()
      .map((room) => document.getElementById(`lynqr-room-${room.handle}`))
      .filter(Boolean);
    if (!cards.length) return;
    if (typeof IntersectionObserver !== "function") {
      setActiveRoom(cards[0].dataset.room || "");
      return;
    }
    const activationLine = Math.round(roomNavigationActivationLine());
    const viewportHeight = Number(window.innerHeight || 800);
    const bottomMargin = Math.max(0, viewportHeight - activationLine - 1);
    roomNavigationObserver = new IntersectionObserver(
      () => {
        const handle = activeRoomAtViewportPosition(
          cards,
          roomNavigationActivationLine(),
        );
        if (handle) setActiveRoom(handle);
      },
      {
        rootMargin: `-${activationLine}px 0px -${bottomMargin}px 0px`,
        threshold: 0,
      },
    );
    cards.forEach((card) => roomNavigationObserver.observe(card));
    if (!activeRoomHandle) setActiveRoom(cards[0].dataset.room || "");
  }

  function refreshRoomNavigation() {
    const navigation = document.querySelector("[data-lynqr-room-navigation]");
    const desktopList = document.querySelector(
      "[data-lynqr-room-navigation-list]",
    );
    const mobileList = document.querySelector("[data-lynqr-mobile-room-list]");
    const roomsToggle = document.querySelector(
      "[data-lynqr-mobile-rooms-toggle]",
    );
    const rooms = orderedPackageRooms();
    const selectedThemeHandle = String(state.selectedTheme?.handle || "");
    if (selectedThemeHandle !== roomNavigationThemeHandle) {
      roomNavigationThemeHandle = selectedThemeHandle;
      activeRoomHandle = "";
      attentionRoomHandle = "";
    }
    const available = Boolean(selectedThemeHandle) && rooms.length > 0;
    if (navigation) navigation.hidden = !available;
    if (roomsToggle) roomsToggle.disabled = !available;
    const markup = available ? rooms.map(roomNavigationItem).join("") : "";
    if (desktopList) desktopList.innerHTML = markup;
    if (mobileList) mobileList.innerHTML = markup;
    if (!available) {
      activeRoomHandle = "";
      attentionRoomHandle = "";
      roomNavigationObserver?.disconnect?.();
      return;
    }
    if (!rooms.some((room) => room.handle === activeRoomHandle)) {
      activeRoomHandle = rooms[0].handle;
    }
    refreshRoomNavigationState();
    observeRoomSections();
  }

  function setMobileRoomsDrawerState(isOpen, restoreFocus = true) {
    const drawer = document.querySelector("[data-lynqr-mobile-rooms-drawer]");
    const backdrop = document.querySelector(
      "[data-lynqr-mobile-rooms-backdrop]",
    );
    const toggle = document.querySelector("[data-lynqr-mobile-rooms-toggle]");
    if (!drawer || !backdrop || !toggle) return;
    if (window.innerWidth > 1320) isOpen = false;
    drawer.classList.toggle("is-open", isOpen);
    backdrop.classList.toggle("is-visible", isOpen);
    backdrop.hidden = !isOpen;
    drawer.setAttribute("aria-hidden", String(!isOpen));
    toggle.setAttribute("aria-expanded", String(isOpen));
    document.body.classList.toggle("lynqr-mobile-rooms-open", isOpen);
    if (isOpen) {
      roomSheetPreviousFocus = document.activeElement;
      drawer.querySelector("[data-lynqr-mobile-rooms-close]")?.focus();
    } else if (restoreFocus) {
      roomSheetPreviousFocus?.focus?.();
    }
  }

  function scrollToCheckout() {
    const checkout = document.querySelector("[data-lynqr-add-to-cart]");
    if (!checkout) return false;
    checkout.scrollIntoView?.({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "center",
    });
    return true;
  }

  function initialiseResponsiveNavigation() {
    if (roomNavigationInitialised) return;
    roomNavigationInitialised = true;
    const app = document.querySelector("#lynqr-package-app");
    if (!app) return;

    const backdrop = document.createElement("div");
    backdrop.className = "lynqr-mobile-rooms-backdrop";
    backdrop.dataset.lynqrMobileRoomsBackdrop = "";
    backdrop.hidden = true;

    const drawer = document.createElement("section");
    drawer.id = "lynqr-mobile-rooms-drawer";
    drawer.className = "lynqr-mobile-rooms-drawer";
    drawer.dataset.lynqrMobileRoomsDrawer = "";
    drawer.setAttribute("role", "dialog");
    drawer.setAttribute("aria-modal", "true");
    drawer.setAttribute("aria-labelledby", "lynqr-mobile-rooms-title");
    drawer.setAttribute("aria-hidden", "true");
    drawer.innerHTML = `
      <div class="lynqr-mobile-rooms-drawer__header">
        <div>
          <span class="lynqr-mobile-rooms-drawer__eyebrow">Navigation</span>
          <h2 id="lynqr-mobile-rooms-title">Jump to room</h2>
        </div>
        <button type="button" class="lynqr-mobile-rooms-drawer__close" data-lynqr-mobile-rooms-close aria-label="Close room navigation">&times;</button>
      </div>
      <ol class="lynqr-mobile-rooms-drawer__list" data-lynqr-mobile-room-list></ol>
    `;
    app.appendChild(backdrop);
    app.appendChild(drawer);

    const roomsToggle = document.querySelector(
      "[data-lynqr-mobile-rooms-toggle]",
    );
    roomsToggle?.addEventListener("click", () => {
      if (ui.mobileSummaryDrawer?.classList.contains("is-open")) {
        setMobileSummaryDrawerState(false, false);
      }
      setMobileRoomsDrawerState(!drawer.classList.contains("is-open"));
    });
    ui.mobileSummaryToggle?.addEventListener("click", () => {
      if (drawer.classList.contains("is-open")) {
        setMobileRoomsDrawerState(false, false);
      }
    });
    drawer
      .querySelector("[data-lynqr-mobile-rooms-close]")
      ?.addEventListener("click", () => setMobileRoomsDrawerState(false));
    backdrop.addEventListener("click", () => setMobileRoomsDrawerState(false));

    document.addEventListener("click", (event) => {
      const jump = event.target?.closest?.("[data-lynqr-room-jump]");
      if (!jump) return;
      const handle = jump.dataset.lynqrRoomJump || "";
      const fromDrawer = Boolean(
        jump.closest("[data-lynqr-mobile-rooms-drawer]"),
      );
      if (fromDrawer) setMobileRoomsDrawerState(false, false);
      scrollToRoom(handle, true);
    });

    document.addEventListener("keydown", (event) => {
      if (!drawer.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        setMobileRoomsDrawerState(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...drawer.querySelectorAll("button:not([disabled])")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document
      .querySelector("[data-lynqr-desktop-checkout-jump]")
      ?.addEventListener("click", scrollToCheckout);

    on("validation.error", ({ detail }) => {
      scrollToFirstValidationIssue(detail);
    });

    window.addEventListener("resize", () => {
      if (window.innerWidth > 1320) setMobileRoomsDrawerState(false, false);
      if (state.selectedTheme && orderedPackageRooms().length) {
        window.cancelAnimationFrame?.(roomNavigationResizeFrame);
        roomNavigationResizeFrame = window.requestAnimationFrame?.(() => {
          roomNavigationResizeFrame = null;
          observeRoomSections();
        });
      }
    });
    refreshRoomNavigation();
  }
  /*==============================================================================
Render Room Gallery
==============================================================================*/
  function renderRoomGallery(roomHandle, variant = null) {
    console.groupCollapsed("[Lynqr][GALLERY] renderRoomGallery()");

    /*
    ==========================================================================
    INPUT
    ==========================================================================
    */

    console.log("[Lynqr][GALLERY] INPUT", {
      roomHandle,
      variant,
    });

    /*
    ==========================================================================
    NORMALISE ROOM HANDLE
    ==========================================================================
    */

    const handle =
      typeof roomHandle === "string" ? roomHandle : roomHandle?.handle;

    const normalisedRoomHandle = String(handle || "")
      .trim()
      .toLowerCase();

    console.log("[Lynqr][GALLERY] NORMALISED ROOM", {
      original: roomHandle,

      handle,

      normalisedRoomHandle,
    });

    if (!normalisedRoomHandle) {
      console.warn("[Lynqr][GALLERY] ABORT — invalid room handle", {
        roomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
    ==========================================================================
    FIND GALLERY
    ==========================================================================
    */

    const gallery =
      document.querySelector(
        `[data-room-gallery="room-${normalisedRoomHandle}"]`,
      ) ||
      document.querySelector(`[data-room-gallery="${normalisedRoomHandle}"]`);

    console.log("[Lynqr][GALLERY] GALLERY LOOKUP", {
      selector1: `[data-room-gallery="room-${normalisedRoomHandle}"]`,

      selector2: `[data-room-gallery="${normalisedRoomHandle}"]`,

      found: !!gallery,

      gallery,
    });

    if (!gallery) {
      console.warn("[Lynqr][GALLERY] Gallery element not found", {
        roomHandle: normalisedRoomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
    ==========================================================================
    FIND PRODUCT ROOM
    ==========================================================================

    We need the product room because the product room is where the
    theme / palette / configuration context should come from.
    ==========================================================================
    */

    const room =
      state.rooms?.find(
        (item) =>
          String(item?.handle || "")
            .trim()
            .toLowerCase() === normalisedRoomHandle,
      ) || null;

    console.log("[Lynqr][GALLERY] PRODUCT ROOM", {
      found: !!room,

      room,
    });

    /*
    ==========================================================================
    ROOM SELECTION
    ==========================================================================
    */

    const selection =
      typeof roomSelection === "function"
        ? roomSelection(normalisedRoomHandle)
        : null;

    console.log("[Lynqr][GALLERY] ROOM SELECTION", {
      selection,
    });

    /*
    ==========================================================================
    RESOLVE GALLERY CONTEXT
    ==========================================================================

    Priority:

    1. Explicit variant context
    2. Product room
    3. Room selection
    4. Global state

    The important point is that renderRoomGallery() must pass the actual
    room's theme/palette/configuration to resolveRoomGallery().
    ==========================================================================
    */

    const context = buildRoomGalleryContext(normalisedRoomHandle, {
      theme: variant?.theme || null,

      palette: variant?.palette || null,

      configuration: variant?.configuration || null,
    });

    console.log("[Lynqr][GALLERY] GALLERY CONTEXT", {
      context,

      sources: {
        theme: variant?.theme
          ? "variant"
          : room?.theme
            ? "room"
            : selection?.room?.theme
              ? "selection.room"
              : state.selectedTheme?.handle
                ? "state"
                : "none",

        palette: variant?.palette
          ? "variant"
          : room?.palette
            ? "room"
            : selection?.room?.palette
              ? "selection.room"
              : state.selectedPalette?.handle
                ? "state"
                : "none",

        configuration: variant?.configuration
          ? "variant"
          : room?.configuration
            ? "room"
            : selection?.room?.configuration
              ? "selection.room"
              : "default",
      },
    });

    /*
    ==========================================================================
    RESOLVE VARIANT
    ==========================================================================

    If a valid variant was explicitly supplied, use it.

    Otherwise resolve it using the room-specific gallery context.
    ==========================================================================
    */

    let currentVariant = variant || null;

    if (currentVariant) {
      console.log("[Lynqr][GALLERY] USING SUPPLIED VARIANT", {
        variant: currentVariant,
      });
    } else {
      console.log("[Lynqr][GALLERY] NO SUPPLIED GALLERY — RESOLVING", {
        roomHandle: normalisedRoomHandle,

        context,
      });

      currentVariant = resolveRoomGallery(normalisedRoomHandle, context);
    }

    /*
    ==========================================================================
    VARIANT RESULT
    ==========================================================================
    */

    console.log("[Lynqr][GALLERY] CURRENT VARIANT", {
      found: !!currentVariant,

      variant: currentVariant,
    });

    /*
    ==========================================================================
    IMPORTANT:
    NO ROOM GALLERY FOUND
    ==========================================================================

    Do not render an empty gallery and then let another function guess
    what should happen.

    The room renderer should show the room placeholder when no gallery exists.
    ==========================================================================
    */

    if (!currentVariant) {
      console.warn("[Lynqr][GALLERY] NO ROOM GALLERY — RENDER PLACEHOLDER", {
        roomHandle: normalisedRoomHandle,

        context,
      });

      /*
        Clear gallery thumbnails only.
        The placeholder function controls the main room card.
        */

      gallery.innerHTML = "";
      delete gallery.dataset.roomGalleryKey;

      if (typeof renderRoomPlaceholder === "function") {
        renderRoomPlaceholder(normalisedRoomHandle);
      } else {
        console.warn("[Lynqr][GALLERY] renderRoomPlaceholder() unavailable", {
          roomHandle: normalisedRoomHandle,
        });
      }

      console.groupEnd();

      return;
    }

    const activeGalleryKey =
      currentVariant.handle ||
      roomGalleryKey(
        currentVariant.theme,
        currentVariant.materialPalette || currentVariant.palette,
        currentVariant.room || normalisedRoomHandle,
      );
    if (
      activeGalleryKey &&
      gallery.dataset.roomGalleryKey === activeGalleryKey &&
      gallery.childElementCount > 0
    ) {
      // Add-on and paid-option changes do not alter or reset the base room
      // gallery. Only Theme/Room/Palette changes produce a different key.
      console.groupEnd();
      return;
    }

    /*
    ==========================================================================
    BUILD IMAGE LIST
    ==========================================================================

    Expected Room Gallery structure:

        variant.image
        variant.thumbnail
        variant.galleryImages[]
    ==========================================================================
    */

    // The hero image is displayed in the room preview above. The thumbnail
    // gallery must mirror only the Room Gallery metaobject's gallery_images
    // field, otherwise a record with one gallery image renders two items.
    const images = roomGalleryImages(currentVariant);

    console.log("[Lynqr][GALLERY] RAW GALLERY IMAGES", {
      galleryImages: currentVariant?.galleryImages,

      isArray: Array.isArray(currentVariant?.galleryImages),

      count: Array.isArray(currentVariant?.galleryImages)
        ? currentVariant.galleryImages.length
        : 0,
    });

    /*
    ==========================================================================
    RAW IMAGE RESULT
    ==========================================================================
    */

    console.log("[Lynqr][GALLERY] RAW IMAGES", {
      count: images.length,

      images,
    });

    /*
    ==========================================================================
    REMOVE DUPLICATE IMAGES
    ==========================================================================
    */

    const uniqueImages = [];

    const seen = new Set();

    images.forEach((item, index) => {
      if (!item?.image) {
        console.warn("[Lynqr][GALLERY] SKIP IMAGE — NO URL", {
          index,
          item,
        });

        return;
      }

      if (seen.has(item.image)) {
        console.log("[Lynqr][GALLERY] SKIP DUPLICATE IMAGE", {
          index,
          image: item.image,
        });

        return;
      }

      seen.add(item.image);

      uniqueImages.push(item);
    });

    console.log("[Lynqr][GALLERY] UNIQUE GALLERY IMAGES", {
      count: uniqueImages.length,

      images: uniqueImages,
    });

    /*
    ==========================================================================
    ROOM GALLERY EXISTS BUT HAS NO IMAGE
    ==========================================================================

    This is different from:

        NO ROOM GALLERY

    We have already established that the Room Gallery record exists.

    For now, leave the gallery empty and let refreshRoomImage() handle
    the hero image state.
    ==========================================================================
    */

    if (!uniqueImages.length) {
      console.warn("[Lynqr][GALLERY] ROOM GALLERY HAS NO IMAGES", {
        roomHandle: normalisedRoomHandle,

        variant: currentVariant,
      });

      gallery.innerHTML = "";

      refreshRoomImage(normalisedRoomHandle, currentVariant);

      console.groupEnd();

      return;
    }

    /*
    ==========================================================================
    RENDER GALLERY
    ==========================================================================
    */

    console.log("[Lynqr][GALLERY] RENDERING GALLERY", {
      roomHandle: normalisedRoomHandle,

      imageCount: uniqueImages.length,
    });

    gallery.innerHTML = uniqueImages
      .map(
        (item, index) => `

                    <button
                        type="button"
                        class="lynqr-room-gallery__thumb${
                          index === 0 ? " is-active" : ""
                        }"
                        data-gallery-room="${normalisedRoomHandle}"
                        data-gallery-image="${item.image}"
                        aria-label="View room image ${index + 1}"
                    >

                        <img
                            src="${item.thumbnail || item.image}"
                            alt=""
                            width="120"
                            height="80"
                            loading="${index === 0 ? "eager" : "lazy"}"
                            decoding="async"
                        >

                    </button>

                `,
      )
      .join("");

    gallery.dataset.roomGalleryKey = activeGalleryKey;

    /*
    ==========================================================================
    REFRESH HERO IMAGE
    ==========================================================================
    */

    console.log("[Lynqr][GALLERY] REFRESH HERO IMAGE", {
      roomHandle: normalisedRoomHandle,

      variant: currentVariant,
    });

    refreshRoomImage(normalisedRoomHandle, currentVariant);

    /*
    ==========================================================================
    COMPLETE
    ==========================================================================
    */

    console.log("[Lynqr][GALLERY] COMPLETE", {
      roomHandle: normalisedRoomHandle,

      hasVariant: !!currentVariant,

      imageCount: uniqueImages.length,

      context,
    });

    console.groupEnd();
  }

  /* ================================================================
   GALLERY IMAGE CLICK
   ================================================================ */

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-gallery-image]");

    if (!button) {
      return;
    }

    const roomHandle = button.dataset.galleryRoom;

    const imageUrl = button.dataset.galleryImage;

    if (!roomHandle || !imageUrl) {
      return;
    }

    /*
        ----------------------------------------------------------------
        FIND ROOM CARD
        ----------------------------------------------------------------
        */

    const card =
      document.querySelector(`.lynqr-room-card[data-room="${roomHandle}"]`) ||
      document.querySelector(
        `.lynqr-room-card[data-room="room-${roomHandle}"]`,
      );

    if (!card) {
      console.warn("[Lynqr][GALLERY] Room card not found", {
        roomHandle,
      });

      return;
    }

    /*
        ----------------------------------------------------------------
        FIND HERO IMAGE
        ----------------------------------------------------------------
        */

    const image = card.querySelector(".lynqr-room-image");

    if (!image) {
      console.warn("[Lynqr][GALLERY] Hero image not found", {
        roomHandle,
        card,
      });

      return;
    }

    /*
        ----------------------------------------------------------------
        CHANGE HERO IMAGE
        ----------------------------------------------------------------
        */

    image.src = imageUrl;

    image.removeAttribute("hidden");

    image.dataset.src = imageUrl;

    /*
        ----------------------------------------------------------------
        UPDATE ACTIVE THUMBNAIL
        ----------------------------------------------------------------
        */

    const gallery = button.closest("[data-room-gallery]");

    if (gallery) {
      gallery
        .querySelectorAll(".lynqr-room-gallery__thumb")
        .forEach((thumb) => {
          thumb.classList.remove("is-active");
        });

      button.classList.add("is-active");
    }

    console.log("[Lynqr][GALLERY] HERO IMAGE CHANGED", {
      roomHandle,
      imageUrl,
    });
  });

  /*==============================================================================
Price Summary Renderer
==============================================================================*/

  function refreshPriceSummary() {
    console.groupCollapsed("[Lynqr][SUMMARY] refreshPriceSummary");

    /*
    ==========================================================================
    BASE PACKAGE PRICE
    ==========================================================================
    */

    const basePrice = Number(state.product?.price || 0);

    /*
    ==========================================================================
    TOTAL UPGRADE PRICE
    ==========================================================================
    */

    const upgradeTotal = Object.values(state.roomSelections).reduce(
      (total, selection) => total + roomAdditionalTotal(selection),
      0,
    );

    const total = basePrice + upgradeTotal;

    state.totalPrice = total;

    /*
    ==========================================================================
    BASE PACKAGE PRICE
    ==========================================================================
    */

    const basePriceElement = document.querySelector("[data-lynqr-base-price]");

    if (basePriceElement) {
      basePriceElement.textContent = currency(basePrice);

      basePriceElement.dataset.price = String(basePrice);
    }

    /*
    ==========================================================================
    TOTAL UPGRADE PRICE
    ==========================================================================
    */

    const upgradeElement = document.querySelector("[data-lynqr-upgrade-total]");

    if (upgradeElement) {
      upgradeElement.textContent = currency(upgradeTotal);

      upgradeElement.dataset.price = String(upgradeTotal);
    }

    /*
    ==========================================================================
    GRAND TOTAL
    ==========================================================================
    */

    const totalElement = document.querySelector("[data-lynqr-total-price]");

    if (totalElement) {
      totalElement.textContent = currency(total);

      totalElement.dataset.price = String(total);
    }

    /*
    ==========================================================================
    INDIVIDUAL ROOM SUMMARIES
    ==========================================================================

    IMPORTANT

    The Liquid summary is rendered as:

        .lynqr-summary-room
        data-room-summary="{{ room.slug.value }}"

    Inside it:

        [data-room-price]
        [data-room-selection]

    We therefore locate the ROOM ARTICLE first.

    We do NOT directly assume that room.handle and room.slug are identical.
    ==========================================================================
    */

    Object.values(state.roomSelections).forEach((selection) => {
      if (!selection?.room) {
        return;
      }

      const room = selection.room;

      /*
        ----------------------------------------------------------------------
        ROOM IDENTIFIERS
        ----------------------------------------------------------------------
        */

      const identifiers = [room.handle, room.slug, room.slug?.value, room.id]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());

      /*
        ----------------------------------------------------------------------
        FIND ACTUAL RIGHT-HAND ROOM CARD
        ----------------------------------------------------------------------
        */

      const summaryRooms = Array.from(
        document.querySelectorAll(".lynqr-summary-room[data-room-summary]"),
      );

      const summaryRoom = summaryRooms.find((element) => {
        const summaryHandle = String(element.dataset.roomSummary || "")
          .trim()
          .toLowerCase();

        return identifiers.includes(summaryHandle);
      });

      if (!summaryRoom) {
        console.warn("[Lynqr][SUMMARY] Room summary element not found", {
          room,
          identifiers,
          availableRooms: summaryRooms.map(
            (element) => element.dataset.roomSummary,
          ),
        });

        return;
      }

      /*
        ----------------------------------------------------------------------
        SELECTED ADD-ONS AND ROOM TOTAL
        ----------------------------------------------------------------------
        */

      const { names: selectedAddonNames, total: roomTotal } =
        roomSelectedAddonSummary(selection);
      const selectedNames = [
        selection.palette?.name || "",
        ...selectedAddonNames,
      ].filter(Boolean);

      /*
        ----------------------------------------------------------------------
        ROOM PRICE
        ----------------------------------------------------------------------
        */

      const roomPriceElement = summaryRoom.querySelector("[data-room-price]");

      if (roomPriceElement) {
        roomPriceElement.textContent =
          roomTotal > 0 ? `+ ${currency(roomTotal)}` : "-";

        roomPriceElement.dataset.price = String(roomTotal);
      }

      /*
        ----------------------------------------------------------------------
        ROOM SELECTION
        ----------------------------------------------------------------------
        */

      const selectionElement = summaryRoom.querySelector(
        "[data-room-selection]",
      );

      if (selectionElement) {
        selectionElement.textContent = selectedNames.length
          ? selectedNames.join(", ")
          : "Standard Configuration";
      }

      /*
        ----------------------------------------------------------------------
        ROOM CARD SUMMARY
        ----------------------------------------------------------------------
        */

      const roomHandle = String(
        room.handle || room.slug || room.slug?.value || "",
      )
        .trim()
        .toLowerCase();

      if (roomHandle) {
        refreshRoomUpgradeSummary(roomHandle);
      }

      /*
        ----------------------------------------------------------------------
        DEBUG
        ----------------------------------------------------------------------
        */

      console.log("[Lynqr][SUMMARY] Room updated", {
        room: room.name,

        identifiers,

        selectedNames,

        roomTotal,

        total,

        summaryRoom,
      });
    });

    /*
    ==========================================================================
    COMPLETE
    ==========================================================================
    */

    console.log("[Lynqr][SUMMARY] COMPLETE", {
      basePrice,
      upgradeTotal,
      total,
    });

    console.groupEnd();
  }
  /*==============================================================================
Default Theme
==============================================================================*/

  function initialiseDefaults() {
    /*
    ------------------------------------------------------------
    Theme
    ------------------------------------------------------------
    */

    // Theme selection must always be an explicit customer action.
    state.selectedTheme = null;

    /*
    ------------------------------------------------------------
    Palette
    ------------------------------------------------------------

    Do NOT automatically select the first palette.

    Palette selection must be explicit so that:
    - no palette is pre-selected
    - only the user's chosen palette becomes active
    - room galleries are not resolved against an unintended palette
    ------------------------------------------------------------
    */

    state.selectedPalette = null;
  }

  function roomDisplayState(roomHandle) {
    const selection = roomSelection(roomHandle);
    const hasTheme = Boolean(state.selectedTheme?.handle);
    const hasPalette = Boolean(selection?.palette?.handle);
    const gallery =
      hasTheme && hasPalette ? resolveRoomGallery(roomHandle) : null;
    const enabledUpgrades = getEffectiveRoomConfiguration(selection).upgrades.filter(
      (upgrade) => upgrade?.enabled !== false,
    );

    return {
      step2Visible: hasTheme,
      placeholder: !hasTheme
        ? "Select an Interior Theme to begin"
        : !hasPalette
          ? "Select a Material Palette in the options below"
          : gallery
            ? ""
            : "Room Gallery Not Available",
      showPalette: hasTheme,
      showOptionsAndAddons: hasTheme && hasPalette,
      showAddonDropdown: hasTheme && hasPalette && enabledUpgrades.length > 0,
      showGallery: Boolean(gallery),
    };
  }

  function applyRoomDisplayState(card, displayState) {
    if (!card || !displayState) return;
    const information = card.querySelector(".lynqr-room-card__information");
    const gallery = card.querySelector(".lynqr-room-card__gallery-section");
    const configuration = card.querySelector(".lynqr-room-card__config");
    const palette = card.querySelector("[data-room-palette-group]");
    const addon = card.querySelector("[data-room-addon-group]");

    card.hidden = !displayState.step2Visible;
    if (information) information.hidden = !displayState.showOptionsAndAddons;
    if (gallery) gallery.hidden = !displayState.showGallery;
    if (configuration) configuration.hidden = !displayState.showPalette;
    if (palette) palette.hidden = !displayState.showPalette;
    if (addon) addon.hidden = !displayState.showAddonDropdown;
    card.classList.toggle(
      "lynqr-room-card--unavailable",
      !displayState.showGallery,
    );
  }

  function renderRoomPlaceholder(roomHandle, displayState = null) {
    console.group("[Lynqr][ROOM][PLACEHOLDER] renderRoomPlaceholder()");

    /*
    --------------------------------------------------------------------------
    NORMALISE ROOM HANDLE
    --------------------------------------------------------------------------
    */

    const normalisedRoomHandle =
      typeof roomHandle === "string" ? roomHandle : roomHandle?.handle;

    if (!normalisedRoomHandle) {
      console.warn("[Lynqr][ROOM][PLACEHOLDER] ABORT — invalid room handle", {
        roomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
    --------------------------------------------------------------------------
    FIND ROOM CARD
    --------------------------------------------------------------------------
    */

    const selector1 = `.lynqr-room-card[data-room="${normalisedRoomHandle}"]`;

    const selector2 = `.lynqr-room-card[data-room="room-${normalisedRoomHandle}"]`;

    console.log("[Lynqr][ROOM][PLACEHOLDER] CARD SELECTORS", {
      selector1,
      selector2,
    });

    const card =
      document.querySelector(selector1) || document.querySelector(selector2);

    console.log("[Lynqr][ROOM][PLACEHOLDER] CARD RESULT", {
      found: !!card,

      card,
    });

    if (!card) {
      console.warn("[Lynqr][ROOM][PLACEHOLDER] ABORT — card not found", {
        roomHandle: normalisedRoomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
    --------------------------------------------------------------------------
    ROOM DATA
    --------------------------------------------------------------------------
    */

    const room = state.rooms?.find(
      (item) =>
        String(item?.handle || "")
          .trim()
          .toLowerCase() === String(normalisedRoomHandle).trim().toLowerCase(),
    );

    const roomName = room?.name || "Room";
    const resolvedDisplayState =
      displayState || roomDisplayState(normalisedRoomHandle);

    console.log("[Lynqr][ROOM][PLACEHOLDER] ROOM", {
      roomHandle: normalisedRoomHandle,

      roomName,

      room,
    });

    /*
    --------------------------------------------------------------------------
    MARK CARD UNAVAILABLE
    --------------------------------------------------------------------------
    */

    card.classList.add("lynqr-room-card--unavailable");

    /*
    --------------------------------------------------------------------------
    FIND EXISTING NORMAL CARD STRUCTURE
    --------------------------------------------------------------------------
    */

    let preview = card.querySelector(".lynqr-room-card__preview");

    const information = card.querySelector(".lynqr-room-card__information");

    const gallery = card.querySelector(".lynqr-room-card__gallery-section");

    const configuration = card.querySelector(".lynqr-room-card__config");

    /*
    --------------------------------------------------------------------------
    PREVIEW
    --------------------------------------------------------------------------
    */

    if (!preview) {
      preview = document.createElement("div");

      preview.className = "lynqr-room-card__preview";

      preview.dataset.roomPreview = normalisedRoomHandle;

      const insertBefore = information || gallery || configuration || null;

      if (insertBefore) {
        card.insertBefore(preview, insertBefore);
      } else {
        card.appendChild(preview);
      }
    }

    preview.dataset.roomPreview = normalisedRoomHandle;

    /*
    --------------------------------------------------------------------------
    REPLACE ONLY PREVIEW CONTENT
    --------------------------------------------------------------------------
    */

    preview.innerHTML = `

        <div class="lynqr-room__unavailable">

            <div class="lynqr-room-card__empty">

                <span>
                    ${escapeHtml(resolvedDisplayState.placeholder)}
                </span>

            </div>

        </div>


        <div class="lynqr-room-card__preview-overlay">

            <span class="lynqr-room-card__preview-label">
                ${roomName}
            </span>

        </div>

    `;

    /*
    --------------------------------------------------------------------------
    HIDE ALL ROOM DATA / ADD-ON SECTIONS
    --------------------------------------------------------------------------

    IMPORTANT:

    When no Room Gallery exists, the room must not expose:

        - Room information
        - Image gallery
        - Furniture upgrades / add-ons

    Keep the elements in the DOM so the card structure remains consistent,
    but force them completely hidden.
    --------------------------------------------------------------------------
    */

    applyRoomDisplayState(card, resolvedDisplayState);

    /*
    --------------------------------------------------------------------------
    VERIFY RESULT
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][ROOM][PLACEHOLDER] RENDERED", {
      roomHandle: normalisedRoomHandle,

      roomName,

      previewFound: !!preview,

      informationHidden: information ? information.hidden : null,

      galleryHidden: gallery ? gallery.hidden : null,

      configurationHidden: configuration ? configuration.hidden : null,

      informationDisplay: information ? information.style.display : null,

      galleryDisplay: gallery ? gallery.style.display : null,

      configurationDisplay: configuration ? configuration.style.display : null,

      cardClassList: [...card.classList],
    });

    /*
    --------------------------------------------------------------------------
    COMPLETE
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][ROOM][PLACEHOLDER] COMPLETE", {
      roomHandle: normalisedRoomHandle,

      roomName,

      unavailable: card.classList.contains("lynqr-room-card--unavailable"),

      addonsVisible: configuration
        ? configuration.hidden === false &&
          configuration.style.display !== "none"
        : false,
    });

    console.groupEnd();
  }

  /*==============================================================================
Refresh Room Hero Image
==============================================================================*/

  function refreshRoomImage(roomHandle, variant = null, context = {}) {
    console.group("[Lynqr][ROOM][IMAGE] refreshRoomImage()");

    /*
    --------------------------------------------------------------------------
    INPUT
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][ROOM][IMAGE] INPUT", {
      roomHandle,
      suppliedVariant: variant,
      context,
    });

    /*
    --------------------------------------------------------------------------
    NORMALISE ROOM HANDLE
    --------------------------------------------------------------------------
    */

    const handle =
      typeof roomHandle === "string" ? roomHandle : roomHandle?.handle;

    const normalisedRoomHandle = String(handle || "")
      .trim()
      .toLowerCase();

    console.log("[Lynqr][ROOM][IMAGE] NORMALISED ROOM", {
      original: roomHandle,
      handle,
      normalisedRoomHandle,
    });

    if (!normalisedRoomHandle) {
      console.warn("[Lynqr][ROOM][IMAGE] ABORT — invalid room handle", {
        roomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
    --------------------------------------------------------------------------
    FIND ROOM CARD
    --------------------------------------------------------------------------
    */

    const selector1 = `.lynqr-room-card[data-room="room-${normalisedRoomHandle}"]`;

    const selector2 = `.lynqr-room-card[data-room="${normalisedRoomHandle}"]`;

    console.log("[Lynqr][ROOM][IMAGE] CARD SELECTORS", {
      selector1,
      selector2,
    });

    const card =
      document.querySelector(selector1) || document.querySelector(selector2);

    console.log("[Lynqr][ROOM][IMAGE] CARD RESULT", {
      found: !!card,
      card,
    });

    if (!card) {
      console.warn("[Lynqr][ROOM][IMAGE] ABORT — room card not found", {
        roomHandle: normalisedRoomHandle,
        selector1,
        selector2,
      });

      console.groupEnd();

      return;
    }

    /*
    --------------------------------------------------------------------------
    PREVIEW CONTAINER
    --------------------------------------------------------------------------
    */

    const preview = card.querySelector("[data-room-preview]");

    console.log("[Lynqr][ROOM][IMAGE] PREVIEW RESULT", {
      found: !!preview,
      preview,
    });

    if (!preview) {
      console.warn("[Lynqr][ROOM][IMAGE] ABORT — preview not found", {
        roomHandle: normalisedRoomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
    --------------------------------------------------------------------------
    RESOLVE ROOM INFORMATION
    --------------------------------------------------------------------------
    */

    const selection = roomSelection(normalisedRoomHandle);

    const room =
      selection?.room ||
      state.rooms?.find(
        (item) =>
          String(item?.handle || "")
            .trim()
            .toLowerCase() === normalisedRoomHandle,
      );

    const roomName = room?.name || "Room";

    console.log("[Lynqr][ROOM][IMAGE] ROOM DATA", {
      roomHandle: normalisedRoomHandle,

      roomName,

      room,

      selection,
    });

    const displayState = roomDisplayState(normalisedRoomHandle);

    if (!displayState.showPalette || !selection?.palette?.handle) {
      renderRoomPlaceholder(normalisedRoomHandle, displayState);
      console.groupEnd();
      return;
    }

    /*
    --------------------------------------------------------------------------
    BUILD GALLERY CONTEXT
    --------------------------------------------------------------------------

    Explicit context wins.

    This is important because state.selectedTheme / selectedPalette
    may be null during gallery rendering.
    */

    const galleryContext = buildRoomGalleryContext(normalisedRoomHandle, {
      theme: context?.theme || null,

      palette: context?.palette || null,

      configuration: context?.configuration || null,
    });

    console.log("[Lynqr][ROOM][IMAGE] GALLERY CONTEXT", {
      galleryContext,

      contextTheme: context?.theme,

      contextPalette: context?.palette,

      contextConfiguration: context?.configuration,

      roomTheme: room?.theme,

      roomPalette: room?.palette,

      roomConfiguration: room?.configuration,
    });

    /*
    --------------------------------------------------------------------------
    RESOLVE ROOM GALLERY
    --------------------------------------------------------------------------

    If a variant was explicitly supplied, trust it.

    Otherwise resolve using the room-specific gallery context.
    */

    let currentVariant = variant || null;

    if (currentVariant) {
      console.log("[Lynqr][ROOM][IMAGE] USING SUPPLIED ROOM GALLERY", {
        roomHandle: normalisedRoomHandle,

        variant: currentVariant,
      });
    } else {
      console.log("[Lynqr][ROOM][IMAGE] NO SUPPLIED GALLERY — RESOLVING", {
        roomHandle: normalisedRoomHandle,

        galleryContext,
      });

      currentVariant = resolveRoomGallery(normalisedRoomHandle, galleryContext);

      console.log("[Lynqr][ROOM][IMAGE] RESOLVED GALLERY RESULT", {
        roomHandle: normalisedRoomHandle,

        found: !!currentVariant,

        variant: currentVariant,
      });
    }

    /*
    ==========================================================================
    NO ROOM GALLERY
    ==========================================================================

    This is the ONLY condition that means:

        Room Configuration Not Available

    Do NOT attempt to render image/gallery/add-ons here.
    */

    if (!currentVariant) {
      console.warn(
        "[Lynqr][ROOM][IMAGE] GALLERY NOT FOUND — RENDER PLACEHOLDER",
        {
          roomHandle: normalisedRoomHandle,

          roomName,

          galleryContext,
        },
      );

      renderRoomPlaceholder(normalisedRoomHandle, displayState);

      console.log("[Lynqr][ROOM][IMAGE] PLACEHOLDER COMPLETE", {
        roomHandle: normalisedRoomHandle,
      });

      console.groupEnd();

      return;
    }

    /*
==============================================================================
ROOM GALLERY EXISTS
==============================================================================

The room is available.

However, the card may currently be a placeholder because the room
previously had no Room Gallery.

In that case the placeholder has removed the normal:

    information
    gallery
    configuration

sections.

Therefore we must rebuild the complete card before continuing.
==============================================================================
*/

    console.log("[Lynqr][ROOM][IMAGE] ROOM GALLERY EXISTS", {
      roomHandle: normalisedRoomHandle,

      variant: currentVariant,
    });

    /*
------------------------------------------------------------------------------
CHECK WHETHER NORMAL CARD STRUCTURE STILL EXISTS
------------------------------------------------------------------------------
*/

    let information = card.querySelector(".lynqr-room-card__information");

    let gallerySection = card.querySelector(
      ".lynqr-room-card__gallery-section",
    );

    let configuration = card.querySelector(".lynqr-room-card__config");

    const cardNeedsRebuild = !information || !gallerySection || !configuration;

    console.log("[Lynqr][ROOM][IMAGE] CARD STRUCTURE CHECK", {
      roomHandle: normalisedRoomHandle,

      information: !!information,

      gallerySection: !!gallerySection,

      configuration: !!configuration,

      cardNeedsRebuild,
    });

    /*
------------------------------------------------------------------------------
REBUILD PLACEHOLDER CARD
------------------------------------------------------------------------------
*/

    if (cardNeedsRebuild) {
      console.log("[Lynqr][ROOM][IMAGE] NORMAL CARD MISSING — REBUILDING", {
        roomHandle: normalisedRoomHandle,
      });

      const rebuiltCard = restoreRoomCard(normalisedRoomHandle);

      if (!rebuiltCard) {
        console.error("[Lynqr][ROOM][IMAGE] CARD REBUILD FAILED", {
          roomHandle: normalisedRoomHandle,
        });

        console.groupEnd();

        return;
      }

      /*
    IMPORTANT:

    createRoomCard() replaced the old DOM element.
    We must now use the NEW card.
    */

      card = rebuiltCard;

      /*
    Re-query all normal room sections.
    */

      information = card.querySelector(".lynqr-room-card__information");

      gallerySection = card.querySelector(".lynqr-room-card__gallery-section");

      configuration = card.querySelector(".lynqr-room-card__config");

      console.log("[Lynqr][ROOM][IMAGE] CARD REBUILD COMPLETE", {
        roomHandle: normalisedRoomHandle,

        information: !!information,

        gallerySection: !!gallerySection,

        configuration: !!configuration,
      });
    }
    /*
|--------------------------------------------------------------------------
| RE-QUERY PREVIEW AFTER CARD REBUILD
|--------------------------------------------------------------------------
|
| restoreRoomCard() replaces the old DOM card.
| Therefore the original `preview` reference may point to
| the detached old card.
|
*/

    const currentPreview = card.querySelector("[data-room-preview]");

    /*
|--------------------------------------------------------------------------
| ROOM GALLERY DESCRIPTION
|--------------------------------------------------------------------------
*/

    const descriptionElement = card.querySelector(
      ".lynqr-room-card__description",
    );

    const galleryDescription = String(currentVariant?.description || "").trim();

    if (descriptionElement) {
      descriptionElement.innerHTML = galleryDescription;

      descriptionElement.hidden = !galleryDescription;
    } else if (galleryDescription && information) {
      const informationMain = information.querySelector(
        ".lynqr-room-card__information-main",
      );

      if (informationMain) {
        const description = document.createElement("div");

        description.className = "lynqr-room-card__description rte";

        description.innerHTML = galleryDescription;

        informationMain.prepend(description);
      }
    }

    /*
|--------------------------------------------------------------------------
| REMOVE INITIAL "CONFIGURATION NOT AVAILABLE" PLACEHOLDER
|--------------------------------------------------------------------------
*/

    if (currentPreview) {
      const unavailablePlaceholder = currentPreview.querySelector(
        ".lynqr-room-card__empty",
      );

      if (unavailablePlaceholder) {
        unavailablePlaceholder.remove();
      }
    }

    /*
------------------------------------------------------------------------------
RESTORE NORMAL CARD STATE
------------------------------------------------------------------------------
*/

    applyRoomDisplayState(card, roomDisplayState(normalisedRoomHandle));

    /*
------------------------------------------------------------------------------
REMOVE UNAVAILABLE STATE
------------------------------------------------------------------------------
*/

    card.classList.remove("lynqr-room-card--unavailable");

    console.log("[Lynqr][ROOM][IMAGE] NORMAL ROOM UI RESTORED", {
      roomHandle: normalisedRoomHandle,

      classList: [...card.classList],
    });

    /*
    --------------------------------------------------------------------------
    RESTORE GALLERY-DEPENDENT CONTENT
    --------------------------------------------------------------------------
    */

    /* const information =
        card.querySelector(
            '.lynqr-room-card__information'
        );


    const gallerySection =
        card.querySelector(
            '.lynqr-room-card__gallery-section'
        );


    const configuration =
        card.querySelector(
            '.lynqr-room-card__config'
        );
*/

    console.log("[Lynqr][ROOM][IMAGE] GALLERY SECTIONS", {
      information: {
        found: !!information,

        hidden: information?.hidden,
      },

      gallerySection: {
        found: !!gallerySection,

        hidden: gallerySection?.hidden,
      },

      configuration: {
        found: !!configuration,

        hidden: configuration?.hidden,
      },
    });

    if (information) {
      information.hidden = false;
    }

    if (gallerySection) {
      gallerySection.hidden = false;
    }

    if (configuration) {
      configuration.hidden = false;
    }

    /*
    --------------------------------------------------------------------------
    REMOVE UNAVAILABLE STATE
    --------------------------------------------------------------------------
    */

    card.classList.remove("lynqr-room-card--unavailable");

    console.log("[Lynqr][ROOM][IMAGE] ROOM MARKED AVAILABLE", {
      roomHandle: normalisedRoomHandle,

      classList: [...card.classList],
    });

    const placeholder = currentPreview?.querySelector(
      ".lynqr-room__image-placeholder",
    );

    if (placeholder) {
      placeholder.remove();
    }

    /*
    --------------------------------------------------------------------------
    GET ROOM GALLERY IMAGE
    --------------------------------------------------------------------------
    */

    const imageUrl = currentVariant.image || currentVariant.thumbnail || "";

    console.log("[Lynqr][ROOM][IMAGE] IMAGE RESOLUTION", {
      roomHandle: normalisedRoomHandle,

      image: currentVariant.image,

      thumbnail: currentVariant.thumbnail,

      resolvedImageUrl: imageUrl,

      hasImage: !!imageUrl,
    });

    /*
    ==========================================================================
    ROOM GALLERY EXISTS BUT IMAGE IS MISSING
    ==========================================================================

    This is NOT configuration unavailable.

    The Room Gallery record exists.

    Therefore:

        ✓ Room remains available
        ✓ Add-ons remain available
        ✓ Gallery remains available
        ✓ Information remains available

    Only the hero image is absent.
    ==========================================================================
    */

    if (!imageUrl) {
      console.warn("[Lynqr][ROOM][IMAGE] ROOM GALLERY EXISTS BUT NO IMAGE", {
        roomHandle: normalisedRoomHandle,

        variant: currentVariant,
      });

      const existingImage = preview.querySelector(".lynqr-room-image");

      if (existingImage) {
        console.log("[Lynqr][ROOM][IMAGE] REMOVING OLD IMAGE", {
          roomHandle: normalisedRoomHandle,
        });

        existingImage.remove();
      }

      console.log(
        "[Lynqr][ROOM][IMAGE] COMPLETE — ROOM AVAILABLE WITHOUT IMAGE",
        {
          roomHandle: normalisedRoomHandle,
        },
      );

      console.groupEnd();

      return;
    }

    /*
    ==========================================================================
    ROOM GALLERY EXISTS + IMAGE EXISTS
    ==========================================================================
    */

    console.log("[Lynqr][ROOM][IMAGE] ROOM GALLERY IMAGE FOUND", {
      roomHandle: normalisedRoomHandle,

      imageUrl,
    });

    /*
    --------------------------------------------------------------------------
    FIND EXISTING IMAGE
    --------------------------------------------------------------------------
    */

    let img = preview.querySelector(".lynqr-room-image");

    console.log("[Lynqr][ROOM][IMAGE] EXISTING IMAGE", {
      found: !!img,

      img,
    });

    /*
    --------------------------------------------------------------------------
    CREATE IMAGE IF REQUIRED
    --------------------------------------------------------------------------
    */

    if (!img) {
      console.log("[Lynqr][ROOM][IMAGE] CREATING IMAGE ELEMENT", {
        roomHandle: normalisedRoomHandle,
      });

      img = document.createElement("img");

      img.className = "lynqr-room-image";

      const overlay = preview.querySelector(
        ".lynqr-room-card__preview-overlay",
      );

      console.log("[Lynqr][ROOM][IMAGE] OVERLAY", {
        found: !!overlay,

        overlay,
      });

      if (overlay) {
        preview.insertBefore(img, overlay);
      } else {
        preview.appendChild(img);
      }
    }

    /*
    --------------------------------------------------------------------------
    UPDATE IMAGE
    --------------------------------------------------------------------------
    */

    img.src = imageUrl;

    img.alt = roomName;

    img.width = 1200;
    img.height = 800;

    img.loading = "lazy";
    img.decoding = "async";

    img.style.aspectRatio = "3 / 2";

    img.dataset.roomImage = `room-${normalisedRoomHandle}`;

    console.log("[Lynqr][ROOM][IMAGE] IMAGE UPDATED", {
      roomHandle: normalisedRoomHandle,

      roomName,

      imageUrl,

      src: img.src,

      dataset: img.dataset.roomImage,
    });

    /*
    --------------------------------------------------------------------------
    FINAL STATE
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][ROOM][IMAGE] FINAL CARD STATE", {
      roomHandle: normalisedRoomHandle,

      available: true,

      hasVariant: true,

      hasImage: !!imageUrl,

      informationHidden: information?.hidden,

      galleryHidden: gallerySection?.hidden,

      configurationHidden: configuration?.hidden,

      unavailableClass: card.classList.contains("lynqr-room-card--unavailable"),

      placeholderPresent: !!preview.querySelector(
        ".lynqr-room__image-placeholder",
      ),

      imagePresent: !!preview.querySelector(".lynqr-room-image"),
    });

    console.log("[Lynqr][ROOM][IMAGE] COMPLETE", {
      roomHandle: normalisedRoomHandle,

      roomName,

      hasVariant: true,

      hasImage: !!imageUrl,
    });

    console.groupEnd();
  }
  /*==============================================================================
Refresh Upgrade Selection UI
==============================================================================*/

  function refreshUpgradeSelection(roomHandle) {
    const selection = roomSelection(roomHandle);

    if (!selection) {
      return;
    }

    const normalizedRoom = String(selection.room.handle || "")
      .trim()
      .toLowerCase();

    document.querySelectorAll("[data-upgrade-quantity-card]").forEach((card) => {
      if (card.dataset.room !== selection.room.handle) return;
      const upgrade = selection.upgrades.find((candidate) =>
        getUpgradeHandle(candidate) === card.dataset.upgrade,
      );
      const quantity = getUpgradeQuantity(upgrade);
      card.classList.toggle("is-selected", quantity > 0);
      card.querySelectorAll("[data-upgrade-quantity]").forEach((button) => {
        button.setAttribute(
          "aria-pressed", String(Number(button.dataset.upgradeQuantity) === quantity),
        );
      });
      const meta = card.querySelector(".lynqr-upgrade__meta span");
      if (meta) meta.textContent = quantity > 0 ? `${quantity} selected` : "None selected";
    });

    document
      .querySelectorAll(".lynqr-upgrade__checkbox")
      .forEach((checkbox) => {
        const checkboxRoom = String(checkbox.dataset.room || "")
          .trim()
          .toLowerCase();

        if (checkboxRoom !== normalizedRoom) {
          return;
        }

        const upgradeHandle = normalizeUpgradeHandle(checkbox.dataset.upgrade);

        const selected = selection.upgrades.some(
          (upgrade) => getUpgradeHandle(upgrade) === upgradeHandle,
        );

        checkbox.checked = selected;

        const card = checkbox.closest(".lynqr-upgrade");

        if (card) {
          card.classList.toggle("is-selected", selected);

          card.setAttribute("aria-checked", String(selected));
        }
      });
  }

  function refreshRoomUpgradeSummary(roomHandle) {
    const selection = roomSelection(roomHandle);

    if (!selection) {
      return false;
    }

    const card = document.querySelector(
      `.lynqr-room-card[data-room="${roomHandle}"]`,
    );

    if (!card) {
      return false;
    }

    const summaryFooter = card.querySelector("[data-room-upgrade-summary]");

    if (!summaryFooter) {
      return false;
    }

    const selectedElement = summaryFooter.querySelector(
      "[data-room-selected-upgrades]",
    );

    const totalElement = summaryFooter.querySelector(
      "[data-room-upgrade-total]",
    );

    if (!selectedElement || !totalElement) {
      return false;
    }

    const { names: selectedNames, total: upgradeTotal } =
      roomSelectedAddonSummary(selection);

    selectedElement.textContent = selectedNames.length
      ? selectedNames.join(", ")
      : "Standard Package";

    totalElement.textContent = currency(upgradeTotal);

    totalElement.dataset.price = String(upgradeTotal);

    summaryFooter.dataset.upgradeTotal = String(upgradeTotal);

    summaryFooter.dataset.selectedUpgrades = String(selectedNames.length);

    const addonStatus = card.querySelector("[data-room-addon-status]");
    if (addonStatus) {
      const selectedAddonCount = selection.upgrades.length;
      addonStatus.textContent = selectedAddonCount
        ? `${selectedAddonCount} selected`
        : "None selected";
    }

    return true;
  }

  /*==============================================================================
Lynqr Money Helpers
==============================================================================*/

  /**
   * Room Upgrade prices are stored in cents.
   *
   * Example:
   * 20000 => SGD 200.00
   * 60000 => SGD 600.00
   */
  function getRoomUpgradePrice(upgrade) {
    const rawPrice = Number(upgrade?.price || 0);

    const normalizedPrice = rawPrice / 100;

    console.log("[Lynqr][PRICE] Room upgrade price normalised", {
      name: upgrade?.upgrade?.name || upgrade?.name || null,

      handle: getUpgradeHandle(upgrade),

      rawPrice,

      normalizedPrice,
    });

    return normalizedPrice;
  }

  /*==============================================================================
Right-Hand Room Summary
==============================================================================*/
  function updateRightHandRoomSummary(
    room,
    selectedNames = [],
    upgradeTotal = 0,
  ) {
    if (!room) {
      return false;
    }

    /*
    ==========================================================================
    RESOLVE ALL POSSIBLE ROOM IDENTIFIERS
    ==========================================================================
    */

    const identifiers = [room.handle, room.slug, room.slug?.value, room.id]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase());

    /*
    ==========================================================================
    FIND THE ACTUAL RIGHT-PANEL ROOM ARTICLE
    ==========================================================================

    Liquid generates:

        <article
            class="lynqr-summary-room"
            data-room-summary="{{ room.slug.value }}"
        >

    Find that article first.
    ==========================================================================
    */

    const roomElements = Array.from(
      document.querySelectorAll(".lynqr-summary-room[data-room-summary]"),
    );

    const roomElement = roomElements.find((element) => {
      const summaryHandle = String(element.dataset.roomSummary || "")
        .trim()
        .toLowerCase();

      return identifiers.includes(summaryHandle);
    });

    /*
    ==========================================================================
    ROOM NOT FOUND
    ==========================================================================
    */

    if (!roomElement) {
      console.error("[Lynqr][RIGHT SUMMARY] ROOM ARTICLE NOT FOUND", {
        room,
        identifiers,
        availableRooms: roomElements.map(
          (element) => element.dataset.roomSummary,
        ),
      });

      return false;
    }

    /*
    ==========================================================================
    FIND FIELDS INSIDE THE CORRECT ROOM
    ==========================================================================
    */

    const selectionElement = roomElement.querySelector("[data-room-selection]");

    const priceElement = roomElement.querySelector("[data-room-price]");

    const descriptionElement = roomElement.querySelector(
      "[data-room-short-description]",
    );

    if (!selectionElement) {
      console.error("[Lynqr][RIGHT SUMMARY] SELECTION FIELD NOT FOUND", {
        room,
        roomElement,
      });

      return false;
    }

    if (!priceElement) {
      console.error("[Lynqr][RIGHT SUMMARY] PRICE FIELD NOT FOUND", {
        room,
        roomElement,
      });

      return false;
    }

    /*
    ==========================================================================
    SELECTED UPGRADE TEXT
    ==========================================================================
    */

    const selectionText = selectedNames.length
      ? selectedNames.join(" · ")
      : "Configuration incomplete";

    /*
    ==========================================================================
    PRICE
    ==========================================================================
    */

    const numericUpgradeTotal = Number(upgradeTotal) || 0;

    const formattedPrice =
      numericUpgradeTotal > 0 ? `+ ${currency(numericUpgradeTotal)}` : "-";

    /*
    ==========================================================================
    UPDATE RIGHT PANEL
    ==========================================================================
    */

    selectionElement.textContent = selectionText;

    if (descriptionElement) {
      const shortDescription = roomShortDescription(room);
      descriptionElement.hidden = !shortDescription;
      descriptionElement.innerHTML = shortDescription
        ? `<strong>Base package</strong><span>${escapeHtml(roomShortDescription(room))}</span>`
        : "";
    }

    priceElement.textContent = formattedPrice;

    priceElement.dataset.price = String(numericUpgradeTotal);

    /*
    ==========================================================================
    RETURN SUCCESS
    ==========================================================================
    */

    return true;
  }

  /*==============================================================================
Gallery Refresh
==============================================================================*/

  function refreshGallery() {
    console.groupCollapsed("[Lynqr][GALLERY] refreshGallery()");

    state.rooms.forEach((room) => {
      const selection = roomSelection(room.handle);
      if (!selection?.palette) {
        return;
      }

      /*
        --------------------------------------------------------------------------
        BUILD ROOM GALLERY CONTEXT
        --------------------------------------------------------------------------

        The resolver needs the complete gallery lookup context:

            theme
            palette
            configuration

        configuration MUST come from configurationKey(room.handle)
        because it reflects the room's currently selected upgrades.
        --------------------------------------------------------------------------
        */

      const galleryContext = buildRoomGalleryContext(room.handle);

      console.log("[Lynqr][GALLERY] RESOLVE ROOM GALLERY", {
        roomHandle: room.handle,

        galleryContext,

        lookupKey: [
          galleryContext.theme,
          galleryContext.palette,
          room.handle,
          galleryContext.configuration,
        ].join("|"),
      });

      /*
        --------------------------------------------------------------------------
        RESOLVE VARIANT
        --------------------------------------------------------------------------
        */

      const variant = resolveRoomGallery(room.handle, galleryContext);

      /*
        --------------------------------------------------------------------------
        LOG RESULT
        --------------------------------------------------------------------------
        */

      console.log("[Lynqr][GALLERY] ROOM", {
        room: room.handle,

        theme: galleryContext.theme,

        palette: galleryContext.palette,

        configuration: galleryContext.configuration,

        variant: variant?.handle || null,

        image: variant?.image || null,

        found: !!variant,
      });

      /*
        --------------------------------------------------------------------------
        REFRESH ROOM IMAGE
        --------------------------------------------------------------------------
        */

      refreshRoomImage(room.handle, variant);

      /*
        --------------------------------------------------------------------------
        RENDER GALLERY
        --------------------------------------------------------------------------

        Gallery only exists when a valid Room Gallery record exists.
        --------------------------------------------------------------------------
        */

      if (variant) {
        renderRoomGallery(room.handle, variant);
      }
    });

    console.groupEnd();
  }
  /*==============================================================================
Debug Snapshot
==============================================================================*/

  function snapshot() {
    console.table({
      Theme: state.selectedTheme?.name || "-",

      Palette: state.selectedPalette?.name || "-",

      Total: state.totalPrice,
    });

    console.log(buildConfiguration());
  }
  function updateRightHandRoomSummaryElements(
    selectionElement,
    priceElement,
    selectedNames = [],
    upgradeTotal = 0,
  ) {
    /*
    ==========================================================================
    SELECTION TEXT
    ==========================================================================
    */

    const selectionText = selectedNames.length
      ? selectedNames.join(" · ")
      : "Configuration incomplete";

    /*
    ==========================================================================
    PRICE TEXT
    ==========================================================================
    */

    const formattedPrice =
      Number(upgradeTotal) > 0 ? `+ ${currency(upgradeTotal)}` : "-";

    /*
    ==========================================================================
    UPDATE SELECTION
    ==========================================================================
    */

    selectionElement.textContent = selectionText;

    /*
    ==========================================================================
    UPDATE PRICE
    ==========================================================================
    */

    priceElement.textContent = formattedPrice;

    priceElement.dataset.price = String(Number(upgradeTotal) || 0);

    return true;
  }
  /*==============================================================================
Configuration Summary Engine
==============================================================================*/

  function buildConfiguration() {
    const rooms = [];
    const pricing = pricingBreakdown();

    Object.values(state.roomSelections).forEach((selection) => {
      rooms.push({
        room: selection.room.name,

        roomHandle: selection.room.handle,

        shortDescription: roomShortDescription(selection.room),

        palette: selection.palette
          ? {
              id: selection.palette.id,
              handle: selection.palette.handle,
              name: selection.palette.name,
            }
          : null,

        options: Object.values(selection.options || {}).map(
          ({ group, option }) => ({
            group: group.name,
            groupHandle: group.handle,
            name: option.name,
            handle: option.handle,
            price: Number(option.price || 0),
            subgroup: option.subgroup
              ? {
                  handle: option.subgroup.handle,
                  name: option.subgroup.name,
                }
              : null,
          }),
        ),

        configuration: configurationKey(selection.room.handle),

        upgrades: selection.upgrades.map((upgrade) => ({
          name: upgrade.upgrade.name,

          handle: getUpgradeHandle(upgrade),

          price: upgrade.price,
          ...(getUpgradeMaxQuantity(upgrade) !== null ? { quantity: getUpgradeQuantity(upgrade) } : {}),
        })),
      });
    });

    return {
      productId: state.product.id,

      packageType: state.package?.hdbType || state.product?.title || "",

      flatStatus: state.selectedFlatStatus,

      theme: state.selectedTheme,

      fullConfiguredPackageValue: pricing.fullPackageValue,

      depositPercentage: pricing.depositPercentage,

      depositAmountCharged: pricing.shopifyDeposit,

      remainingBalance: pricing.remainingBalance,

      upgradeTotal: pricing.upgradeTotal,

      rooms,
    };
  }

  /*==============================================================================
Shopify Line Item Properties
==============================================================================*/
  function buildLineItemProperties() {
    const properties = {};
    const pricing = pricingBreakdown();
    const configuration = buildConfiguration();
    const configurationId = lynqrConfigurationId();

    /*
    --------------------------------------------------------------------------
    THEME
    --------------------------------------------------------------------------
    */

    if (state.selectedTheme) {
      properties.Theme = state.selectedTheme.name;
    }

    properties["Purchase Type"] =
      `${pricing.depositPercentage}% renovation-package deposit`;

    properties["Package Type"] =
      state.package?.hdbType ||
      state.product?.title ||
      "HDB renovation package";

    if (state.selectedFlatStatus) {
      properties["Flat Status"] = state.selectedFlatStatus;
    }

    properties["Full Configured Package Value"] = currency(
      pricing.fullPackageValue,
    );

    properties["Deposit Percentage"] = `${pricing.depositPercentage}%`;

    properties["Deposit Amount Charged"] = currency(pricing.shopifyDeposit);

    properties["Remaining Balance"] = currency(pricing.remainingBalance);

    properties["Upgrade Total"] = currency(pricing.upgradeTotal);

    properties._Configuration_JSON = JSON.stringify(configuration);

    properties["_Full Configured Package Value Cents"] = String(
      Math.round(pricing.fullPackageValue * 100),
    );

    properties["_Deposit Amount Cents"] = String(pricing.expectedDepositCents);
    properties["_lynqr_configuration_id"] = configurationId;
    properties["_lynqr_component_type"] = "base-deposit";
    properties["_lynqr_full_value_cents"] = String(
      pricing.configuredPackageValueCents,
    );

    /*
    --------------------------------------------------------------------------
    ROOM CONFIGURATIONS
    --------------------------------------------------------------------------
    */

    Object.values(state.roomSelections).forEach((selection) => {
      const includedUpgrades = (selection.upgrades || [])
        .filter((upgrade) => Number(upgrade?.price || 0) === 0)
        .map(upgradeDisplayName)
        .filter(Boolean);
      const includedOptions = Object.values(selection.options || {})
        .filter(({ option }) => Number(option?.price || 0) === 0)
        .map(({ group, option }) => roomOptionDisplayName(group, option))
        .filter(Boolean);
      const details = [
        `Material: ${selection.palette?.name || "Not selected"}`,
      ];

      if (includedUpgrades.length) {
        details.push(`Included upgrades: ${includedUpgrades.join(", ")}`);
      }

      if (includedOptions.length) {
        details.push(`Included options: ${includedOptions.join(", ")}`);
      }

      properties[`Room - ${selection.room.name}`] = details.join(" | ");

      const shortDescription = roomShortDescription(selection.room);
      if (shortDescription) {
        properties[`Includes - ${selection.room.name}`] = shortDescription;
      }
    });

    /*
|--------------------------------------------------------------------------
| PACKAGE TOTAL BREAKDOWN
|--------------------------------------------------------------------------
*/

    const basePrice = Number(state.product?.price) || 0;

    const packageTotal = Number(state.totalPrice) || 0;

    const addOnTotal = Math.max(0, packageTotal - basePrice);

    /*
properties['Package Total Breakdown'] =
    '──────────────';

properties['Base Package'] =
    currency(basePrice);

properties['+ Add On'] =
    currency(addOnTotal);

properties['Grand Total'] =
    currency(packageTotal);

*/
    console.log("[Lynqr][CART] PACKAGE TOTAL BREAKDOWN", {
      basePrice,
      addOnTotal,
      packageTotal,
    });

    /*
    --------------------------------------------------------------------------
    DEBUG
    --------------------------------------------------------------------------
    */

    console.log("[Lynqr][CART] LINE ITEM PROPERTIES BUILT", {
      properties,
      totalPrice: state.totalPrice,
    });

    return properties;
  }

  /*==============================================================================
Validation
==============================================================================*/

  function validateSelections() {
    const errors = [];

    if (!state.selectedFlatStatus) {
      errors.push("Please select a Flat Status.");
    }
    if (!state.selectedTheme) {
      errors.push("Please select a Theme.");
    }

    orderedPackageRooms().forEach((room) => {
      const selection = roomSelection(room.handle);
      if (!selection?.palette) {
        errors.push(`Please select a Material Palette for ${room.name}.`);
      }
      activeOptionGroups(selection)
        .filter((group) => group?.required === true)
        .forEach((group) => {
          if (!selection?.options?.[group.handle]) {
            errors.push(`Please select ${group.name} for ${room.name}.`);
          }
        });
    });

    return { valid: errors.length === 0, errors };
  }

  function validateConfiguration() {
    const result = validateSelections();

    if (!result.valid) {
      emit("validation.error", {
        message: result.errors[0],
        errors: result.errors,
      });

      return false;
    }

    if (!validateBusinessRules()) {
      return false;
    }

    return true;
  }

  /*==============================================================================
Configuration Summary Renderer
==============================================================================*/

  const paletteElement = document.querySelector("[data-lynqr-palette]");

  if (paletteElement) {
    const palette = state.selectedPalette;

    paletteElement.textContent = palette
      ? palette.name ||
        palette.label ||
        palette.title ||
        palette.handle ||
        "Not Selected"
      : "Not Selected";
  }

  /*==============================================================================
Configuration Summary Renderer
==============================================================================*/

  function renderConfigurationSummary() {
    console.groupCollapsed("[Lynqr][SUMMARY] renderConfigurationSummary");

    /*
    ==========================================================================
    PACKAGE
    ==========================================================================
    */

    const packageName =
      state.package?.name ||
      state.package?.title ||
      state.product?.title ||
      "Configuration";

    document
      .querySelectorAll("[data-lynqr-mobile-summary-package]")
      .forEach((element) => {
        element.textContent = packageName;
      });

    /*
    ==========================================================================
    DESIGN SUMMARY
    ==========================================================================
    */

    const themeElement = document.querySelector("[data-lynqr-theme]");

    if (themeElement) {
      themeElement.textContent =
        state.selectedTheme?.name ||
        state.selectedTheme?.label ||
        "Not Selected";
    }

    const paletteElement = document.querySelector("[data-lynqr-palette]");

    if (paletteElement) {
      paletteElement.textContent =
        state.selectedPalette?.name ||
        state.selectedPalette?.label ||
        "Not Selected";
    }

    const hdbElement = document.querySelector("[data-lynqr-flat-status]");

    if (hdbElement) {
      hdbElement.textContent = state.selectedFlatStatus || "Not selected";
    }

    /*
    ==========================================================================
    ROOM CONFIGURATION
    ==========================================================================
    */

    Object.values(state.roomSelections || {}).forEach((selection) => {
      if (!selection?.room) {
        return;
      }

      const room = selection.room;

      const upgrades = Array.isArray(selection.upgrades)
        ? selection.upgrades
        : [];

      /*
        ----------------------------------------------------------------------
        Selected Upgrade Names
        ----------------------------------------------------------------------
        */

      const selectedUpgradeNames = upgrades
        .map(upgradeDisplayName)
        .filter(Boolean);

      const selectedOptionNames = Object.values(selection.options || {}).map(
        ({ group, option }) =>
          `${roomOptionDisplayName(group, option)}${Number(option.price || 0) > 0 ? ` (${currency(option.price)})` : ""}`,
      );

      const selectedNames = [
        selection.palette
          ? `Material: ${selection.palette.name}`
          : "Material: Not selected",
        selectedUpgradeNames.length
          ? `Add-ons: ${selectedUpgradeNames.join(", ")}`
          : "Add-ons: None",
        ...selectedOptionNames,
      ];

      /*
        ----------------------------------------------------------------------
        Room Upgrade Total
        ----------------------------------------------------------------------
        */

      const upgradeTotal = roomAdditionalTotal(selection);

      /*
        ----------------------------------------------------------------------
        Update Summary Room
        ----------------------------------------------------------------------
        */

      updateRightHandRoomSummary(room, selectedNames, upgradeTotal);

      console.log("[Lynqr][SUMMARY] Room configuration", {
        room: room.name,

        handle: room.handle,

        slug: room.slug,

        selectedNames,

        upgradeTotal,
      });
    });

    /*
    ==========================================================================
    PRICE BREAKDOWN
    ==========================================================================
    */

    const basePrice = Number(state.product?.price || 0);

    const upgradeTotal = Object.values(state.roomSelections || {}).reduce(
      (total, selection) => total + roomAdditionalTotal(selection),
      0,
    );

    const total = basePrice + upgradeTotal;

    /*
    --------------------------------------------------------------------------
    Base Package
    --------------------------------------------------------------------------
    */

    const basePriceElement = document.querySelector("[data-lynqr-base-price]");

    if (basePriceElement) {
      basePriceElement.textContent = currency(basePrice);

      basePriceElement.dataset.price = String(basePrice);
    }

    /*
    --------------------------------------------------------------------------
    Upgrade Total
    --------------------------------------------------------------------------
    */

    const upgradeTotalElement = document.querySelector(
      "[data-lynqr-upgrade-total]",
    );

    if (upgradeTotalElement) {
      upgradeTotalElement.textContent = currency(upgradeTotal);

      upgradeTotalElement.dataset.price = String(upgradeTotal);
    }

    /*
    --------------------------------------------------------------------------
    Grand Total
    --------------------------------------------------------------------------
    */

    const totalElement = document.querySelector("[data-lynqr-total-price]");

    if (totalElement) {
      totalElement.textContent = currency(total);

      totalElement.dataset.price = String(total);
    }

    /*
    ==========================================================================
    MOBILE / TABLET SUMMARY BAR
    ==========================================================================
    */

    document
      .querySelectorAll("[data-lynqr-mobile-summary-total]")
      .forEach((element) => {
        element.textContent = currency(total);
      });

    /*
    ==========================================================================
    STATE
    ==========================================================================
    */

    state.totalPrice = total;

    const pricing = pricingBreakdown();

    document
      .querySelectorAll("[data-lynqr-deposit-price]")
      .forEach((element) => {
        element.textContent = currency(pricing.shopifyDeposit);
      });

    document
      .querySelectorAll("[data-lynqr-remaining-balance]")
      .forEach((element) => {
        element.textContent = currency(pricing.remainingBalance);
      });

    document.querySelectorAll("[data-lynqr-deposit-gst]").forEach((element) => {
      element.textContent = currency(pricing.depositGst);
    });

    document
      .querySelectorAll("[data-lynqr-total-payable-now]")
      .forEach((element) => {
        element.textContent = currency(pricing.totalPayableNow);
      });

    console.log("[Lynqr][SUMMARY] COMPLETE", {
      package: packageName,

      theme: state.selectedTheme?.name,

      palette: state.selectedPalette?.name,

      basePrice,

      upgradeTotal,

      total,
    });

    console.groupEnd();
  }
  /*==============================================================================
Configuration Refresh
Central Render Pipeline
==============================================================================*/

  let rendering = false;

  function refreshConfiguration() {
    if (rendering) {
      return;
    }

    rendering = true;

    try {
      /*
        ----------------------------------------------------------------------
        Mark Dirty
        ----------------------------------------------------------------------
        */

      markDirty();

      /*
        ----------------------------------------------------------------------
        Recalculate Price
        ----------------------------------------------------------------------
        */

      calculateTotal();

      /*
        ----------------------------------------------------------------------
        Synchronise All Upgrade Checkboxes
        ----------------------------------------------------------------------
        */

      Object.keys(state.roomSelections).forEach((roomHandle) => {
        refreshUpgradeSelection(roomHandle);
        refreshRoomOptionSelection(roomHandle);
      });

      /*
        ----------------------------------------------------------------------
        Design + Right Summary + Prices
        ----------------------------------------------------------------------
        */

      renderConfigurationSummary();

      /*
        ----------------------------------------------------------------------
        Theme Hero
        ----------------------------------------------------------------------
        */

      refreshHeroImage();

      /*
        ----------------------------------------------------------------------
        Room Galleries
        ----------------------------------------------------------------------
        */

      refreshGallery();

      /*
        ----------------------------------------------------------------------
        Price Summary
        ----------------------------------------------------------------------
        */

      refreshPriceSummary();
      refreshRoomNavigationState();

      /*
        ----------------------------------------------------------------------
        Persist
        ----------------------------------------------------------------------
        */

      scheduleSave();

      /*
        ----------------------------------------------------------------------
        Notify
        ----------------------------------------------------------------------
        */

      emit("configuration.updated", buildConfiguration());
    } finally {
      rendering = false;
    }
  }

  /*==============================================================================
Render Scheduler
Prevents multiple UI refreshes in the same frame.
==============================================================================*/

  let renderQueued = false;

  function scheduleRender() {
    if (renderQueued) {
      return;
    }

    renderQueued = true;

    requestAnimationFrame(() => {
      renderQueued = false;

      refreshConfiguration();
    });
  }

  /*==============================================================================
Debug Helpers
==============================================================================*/

  window.LynqrDebug = Object.freeze({
    state,

    roomGalleries,
    upgradeGalleries,
    loadThemeData,
    resolveRoomGallery,
    roomGalleryImages,
    roomLightboxImages,
    roomNavigationStatus,
    firstIncompleteRoomHandle,
    activeRoomAtViewportPosition,

    buildConfiguration,

    buildLineItemProperties,

    configurationKey,

    roomDisplayState,

    refreshGallery,

    refreshConfiguration,

    toggleUpgrade,
    setUpgradeQuantity,
    getUpgradeMaxQuantity,
    getUpgradeQuantity,
    getUpgradeLineTotal,
    selectRoomPalette,
    selectRoomOption,
    selectFlatStatus,
    getEffectiveRoomConfiguration,
    clearUnavailableRoomSelections,
    roomOptionIsSelected,
    refreshRoomOptionSelection,
    groupRoomOptionsForDisplay,
    resolveRoomOptionGallery,
    inspectRoomOptionGallery,
    roomOptionSubgroupDependency,
    roomOptionIsEligible,
    normalizeRoomOptionCompatibility,
    clearRoomOption,
    renderRoomOptionControls,
    initialiseRoomOptionGalleryLightbox,
    startOver,
    buildQuotation,
    validateQuotationRequest,
    renderPrintableQuotation,
    roomSelectedAddonSummary,
    pricingBreakdown,
    refreshPriceSummary,
    expectedCartTotalCents,
    lynqrConfigurationId,

    snapshot,

    addPackageToCart,
  });

  /*==============================================================================
Shopify Cart Engine
==============================================================================*/

  async function addPackageToCart(quantity = 1) {
    checkoutTrace("cart.start", {
      quantity,
      totalPrice: state.totalPrice,
      roomSelections: Object.entries(state.roomSelections || {}).map(
        ([room, selection]) => ({
          room,
          upgrades: (selection?.upgrades || []).map((upgrade) => ({
            handle: getUpgradeHandle(upgrade),
            price: upgrade?.price,
            shopifyVariantId: upgrade?.shopifyVariantId,
            shopifyDepositPriceCents: upgrade?.shopifyDepositPriceCents,
          })),
        }),
      ),
    });

    console.group("[Lynqr][CART] addPackageToCart()");

    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    const validation = validateConfiguration();

    console.log("[Lynqr][CART] VALIDATION", {
      valid: validation,

      selectedTheme: state.selectedTheme,

      selectedPalette: state.selectedPalette,

      roomSelections: state.roomSelections,
    });

    if (!validation) {
      console.warn("[Lynqr][CART] ABORT — configuration invalid");

      console.groupEnd();

      return false;
    }

    /*
    |--------------------------------------------------------------------------
    | PACKAGE PRODUCT / VARIANT
    |--------------------------------------------------------------------------
    */

    const productId = Number(state.product?.id);

    const packageVariantId = Number(
      state.product?.baseDepositVariantId || state.product?.variantId || 0,
    );
    const configurationId = lynqrConfigurationId();

    const cartQuantity = Number(quantity) || 1;

    console.log("[Lynqr][CART] PACKAGE PRODUCT / VARIANT", {
      product: state.product,

      productId: state.product?.id,

      productIdNumber: productId,

      selectedVariantId: state.product?.variantId,

      packageVariantIdNumber: packageVariantId,

      packageVariantValid:
        Number.isInteger(packageVariantId) && packageVariantId > 0,

      productHandle: state.product?.handle,

      productPrice: state.product?.price,

      quantity: cartQuantity,
    });

    /*
    |--------------------------------------------------------------------------
    | VALIDATE PACKAGE VARIANT
    |--------------------------------------------------------------------------
    */

    if (!Number.isInteger(packageVariantId) || packageVariantId <= 0) {
      console.error(
        "[Lynqr][CART] ABORT — invalid package Shopify variant ID",
        {
          productId: state.product?.id,

          variantId: state.product?.variantId,

          product: state.product,
        },
      );

      emit("cart.error", {
        message:
          "Online checkout is temporarily unavailable for this configuration. Please contact LYNQR.",
      });

      console.groupEnd();

      return false;
    }

    /*
    |--------------------------------------------------------------------------
    | PACKAGE LINE ITEM PROPERTIES
    |--------------------------------------------------------------------------
    */

    const properties = buildLineItemProperties();

    console.log("[Lynqr][CART] PACKAGE LINE ITEM PROPERTIES", properties);

    /*
    |--------------------------------------------------------------------------
    | CART ITEMS
    |--------------------------------------------------------------------------
    |
    | First item:
    |
    |     Base package
    |
    | Additional items:
    |
    |     Selected upgrades
    |
    | Shopify will then calculate:
    |
    |     Base Package
    |     + Upgrade Handles
    |     + Upgrade Lighting
    |     -----------------------
    |     = Grand Total
    |
    |--------------------------------------------------------------------------
    */

    const items = [];

    /*
    |--------------------------------------------------------------------------
    | COLLECT SELECTED UPGRADES
    |--------------------------------------------------------------------------
    */

    const selectedUpgrades = [];

    Object.values(state.roomSelections || {}).forEach((selection) => {
      const roomName =
        selection?.room?.name || selection?.room?.handle || "Room";

      const upgrades = Array.isArray(selection?.upgrades)
        ? selection.upgrades
        : [];

      upgrades.forEach((selectedUpgrade) => {
        if (!selectedUpgrade) {
          return;
        }

        /*
                    --------------------------------------------------------------
                    | UPGRADE RECORD
                    --------------------------------------------------------------
                    */

        const upgrade = selectedUpgrade?.upgrade || selectedUpgrade;

        const upgradeName =
          upgrade?.name ||
          selectedUpgrade?.name ||
          selectedUpgrade?.handle ||
          "Upgrade";

        const upgradeHandle =
          upgrade?.handle ||
          selectedUpgrade?.handle ||
          upgrade?.slug ||
          selectedUpgrade?.slug ||
          "";

        /*
                    --------------------------------------------------------------
                    | SHOPIFY VARIANT ID
                    --------------------------------------------------------------
                    |
                    | Support several possible bootstrap structures.
                    |
                    --------------------------------------------------------------
                    */

        const upgradeVariantId = Number(
          upgrade?.variantId ||
            upgrade?.shopifyVariantId ||
            selectedUpgrade?.variantId ||
            selectedUpgrade?.shopifyVariantId ||
            selectedUpgrade?.upgradeVariantId ||
            0,
        );

        /*
                    --------------------------------------------------------------
                    | UPGRADE DEBUG
                    --------------------------------------------------------------
                    */

        console.log("[Lynqr][CART] SELECTED UPGRADE", {
          room: roomName,

          upgrade: upgradeName,

          upgradeHandle,

          selectedUpgrade,

          upgradeRecord: upgrade,

          upgradeVariantId,

          upgradeVariantIdValid:
            Number.isInteger(upgradeVariantId) && upgradeVariantId > 0,

          upgradePrice:
            selectedUpgrade?.price ?? selectedUpgrade?.upgrade?.price ?? null,
        });

        /*
                    --------------------------------------------------------------
                    | STORE UPGRADE
                    --------------------------------------------------------------
                    */

        selectedUpgrades.push({
          roomName,

          upgradeName: upgradeDisplayName(selectedUpgrade) || upgradeName,

          upgradeHandle,

          variantId: upgradeVariantId,

          selectedUpgrade,

          upgrade,

          componentType: "upgrade",
          quantity: getUpgradeQuantity(selectedUpgrade),
        });
      });

      selectedPaidOptions(selection).forEach(({ group, option }) => {
        selectedUpgrades.push({
          roomName,
          upgradeName: roomOptionDisplayName(group, option),
          upgradeHandle: option.handle || option.slug || "",
          variantId: Number(option.shopifyVariantId || option.variantId || 0),
          selectedUpgrade: option,
          upgrade: option,
          optionGroup: group,
          componentType: "paid-option",
          quantity: 1,
        });
      });
    });

    /*
    |--------------------------------------------------------------------------
    | UPGRADE SUMMARY
    |--------------------------------------------------------------------------
    */

    console.log("[Lynqr][CART] SELECTED UPGRADES SUMMARY", {
      count: selectedUpgrades.length,

      upgrades: selectedUpgrades.map((item) => ({
        room: item.roomName,

        upgrade: item.upgradeName,

        handle: item.upgradeHandle,

        variantId: item.variantId,
      })),
    });

    /*
    |--------------------------------------------------------------------------
    | VALIDATE UPGRADE SHOPIFY VARIANT IDS
    |--------------------------------------------------------------------------
    */

    const upgradesWithoutVariant = selectedUpgrades
      .filter(
        (item) =>
          toCents(item.selectedUpgrade?.price || item.upgrade?.price || 0) > 0,
      )
      .filter(
        (item) => !Number.isInteger(item.variantId) || item.variantId <= 0,
      );

    if (upgradesWithoutVariant.length) {
      console.error(
        "[Lynqr][CART] ABORT — selected upgrade has no Shopify variant ID",
        {
          missing: upgradesWithoutVariant.map((item) => ({
            room: item.roomName,

            upgrade: item.upgradeName,

            handle: item.upgradeHandle,

            selectedUpgrade: item.selectedUpgrade,

            upgrade: item.upgrade,
          })),
        },
      );

      emit("cart.error", {
        message:
          "Unable to add package to cart: one or more selected upgrades do not have a Shopify variant.",
      });

      console.groupEnd();

      return false;
    }

    const pricing = pricingBreakdown();
    const chargeableUpgrades = selectedUpgrades.filter(
      (item) =>
        toCents(item.selectedUpgrade?.price || item.upgrade?.price || 0) > 0,
    );
    const upgradesWithoutDepositPrice = chargeableUpgrades.filter(
      (item) =>
        !Number.isInteger(
          Number(
            item.selectedUpgrade?.shopifyDepositPriceCents ||
              toCents(item.selectedUpgrade?.shopifyDepositPrice),
          ),
        ) ||
        Number(
          item.selectedUpgrade?.shopifyDepositPriceCents ||
            toCents(item.selectedUpgrade?.shopifyDepositPrice),
        ) < 0,
    );
    const mismatchedUpgradePrices = chargeableUpgrades.filter((item) => {
      const fullValueCents = toCents(
        item.selectedUpgrade?.price || item.upgrade?.price || 0,
      );
      const depositCents = Number(
        item.selectedUpgrade?.shopifyDepositPriceCents ||
          toCents(item.selectedUpgrade?.shopifyDepositPrice),
      );
      return (
        depositCents !==
        Math.round(
          fullValueCents *
            ((Number(state.product?.depositPercentage) || 70) / 100),
        )
      );
    });
    const basePriceMismatch =
      pricing.baseDepositPriceCents !==
      Math.round(
        pricing.basePackageValueCents * (pricing.depositPercentage / 100),
      );

    if (
      !packageVariantId ||
      upgradesWithoutDepositPrice.length ||
      mismatchedUpgradePrices.length ||
      basePriceMismatch ||
      !pricing.isValid
    ) {
      checkoutTrace("cart.validation.failed", {
        failure: "DEPOSIT_MAPPING",
        fullPackageValueCents: pricing.configuredPackageValueCents,
        expectedDepositCents: pricing.expectedDepositCents,
        actualDepositCents: pricing.shopifyDepositCents,
        basePriceMismatch,
        upgradesWithoutDepositPrice: upgradesWithoutDepositPrice.length,
        mismatchedUpgradePrices: mismatchedUpgradePrices.map((item) => ({
          room: item.roomName,
          upgrade: item.upgradeName,
          handle: item.upgradeHandle,
          variantId: item.variantId,
        })),
      });

      console.error(
        "[Lynqr][CART] Deposit pricing is not configured correctly.",
        {
          pricing,
          upgradesWithoutDepositPrice,
        },
      );

      console.warn(
        "[Lynqr][DEPOSIT-MAPPING] Missing or mismatched deposit variant",
        {
          product: state.product?.handle,
          baseDepositVariantId: packageVariantId,
          baseDepositPriceCents: pricing.baseDepositPriceCents,
          expectedDepositCents: pricing.expectedDepositCents,
          actualDepositCents: pricing.shopifyDepositCents,
          upgrades: [
            ...upgradesWithoutDepositPrice,
            ...mismatchedUpgradePrices,
          ].map((item) => ({
            room: item.roomName,
            upgrade: item.upgradeName,
            handle: item.upgradeHandle,
            variantId: item.variantId,
          })),
        },
      );

      emit("cart.error", {
        message:
          "Online checkout is temporarily unavailable for this configuration. Please contact LYNQR.",
      });

      console.groupEnd();
      return false;
    }

    /*
    |--------------------------------------------------------------------------
    | ADD UPGRADE ITEMS
    |--------------------------------------------------------------------------
    */

    chargeableUpgrades.forEach((item) => {
      const upgradeProperties = {
        Room: item.roomName,

        Upgrade: item.upgradeName,
        _lynqr_configuration_id: configurationId,
        _lynqr_component_type:
          item.componentType === "paid-option"
            ? "paid-option-deposit"
            : "upgrade-deposit",
        _lynqr_full_value_cents: String(
          toCents(item.selectedUpgrade?.price || item.upgrade?.price || 0),
        ),
      };

      const existingItem = items.find((candidate) =>
        candidate.id === item.variantId &&
        JSON.stringify(candidate.properties) === JSON.stringify(upgradeProperties),
      );
      if (existingItem) {
        existingItem.quantity += cartQuantity * item.quantity;
      } else {
        items.push({
          id: item.variantId,
          quantity: cartQuantity * item.quantity,
          properties: upgradeProperties,
        });
      }

      console.log("[Lynqr][CART] UPGRADE CART ITEM", {
        room: item.roomName,

        upgrade: item.upgradeName,

        handle: item.upgradeHandle,

        variantId: item.variantId,

        quantity: cartQuantity * item.quantity,

        properties: upgradeProperties,
      });
    });

    /*
    |--------------------------------------------------------------------------
    | ADD BASE PACKAGE
    |--------------------------------------------------------------------------
    */

    items.push({
      id: packageVariantId,

      quantity: cartQuantity,

      properties,
    });

    console.log("[Lynqr][CART] BASE PACKAGE ITEM", {
      variantId: packageVariantId,

      quantity: cartQuantity,

      price: state.product?.price,
    });

    /*
    |--------------------------------------------------------------------------
    | BUILD FINAL PAYLOAD
    |--------------------------------------------------------------------------
    */

    const payload = {
      items,
    };

    /*
    |--------------------------------------------------------------------------
    | PRICE EXPECTATION
    |--------------------------------------------------------------------------
    */

    const basePrice = Number(state.product?.price) || 0;

    const packageTotal = Number(state.totalPrice) || 0;

    const addOnTotal = Math.max(0, packageTotal - basePrice);

    console.log("[Lynqr][CART] PRICE EXPECTATION", {
      basePrice,

      addOnTotal,

      expectedGrandTotal: packageTotal,

      selectedUpgradeCount: selectedUpgrades.length,
    });

    /*
    |--------------------------------------------------------------------------
    | FINAL PAYLOAD LOG
    |--------------------------------------------------------------------------
    */

    console.log("[Lynqr][CART] FINAL PAYLOAD", {
      payload,

      itemCount: items.length,

      basePackageVariantId: packageVariantId,

      upgradeItems: items.slice(1),
    });

    emit("cart.adding", payload);

    /*
    |--------------------------------------------------------------------------
    | REQUEST
    |--------------------------------------------------------------------------
    */

    try {
      console.log("[Lynqr][CART] POST /cart/add.js", {
        itemCount: items.length,

        items,
      });

      const response = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart/add.js`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",

            Accept: "application/json",
          },

          body: JSON.stringify(payload),
        },
      );

      /*
        ----------------------------------------------------------------------
        | RESPONSE
        ----------------------------------------------------------------------
        */

      console.log("[Lynqr][CART] RESPONSE", {
        ok: response.ok,

        status: response.status,

        statusText: response.statusText,

        url: response.url,
      });

      /*
        ----------------------------------------------------------------------
        | READ RESPONSE BODY
        ----------------------------------------------------------------------
        */

      const responseText = await response.text();

      console.log("[Lynqr][CART] RESPONSE BODY", responseText);

      /*
        ----------------------------------------------------------------------
        | HANDLE SHOPIFY ERROR
        ----------------------------------------------------------------------
        */

      if (!response.ok) {
        let shopifyError = responseText;

        try {
          const parsed = JSON.parse(responseText);

          console.error("[Lynqr][CART] SHOPIFY ERROR OBJECT", parsed);

          shopifyError =
            parsed?.description ||
            parsed?.message ||
            parsed?.error ||
            responseText;
        } catch (parseError) {
          console.warn("[Lynqr][CART] RESPONSE IS NOT JSON", {
            parseError,
            responseText,
          });
        }

        throw new Error(shopifyError || "Unable to add package to cart.");
      }

      /*
        ----------------------------------------------------------------------
        | SUCCESS RESPONSE
        ----------------------------------------------------------------------
        */

      let cartItem = null;

      try {
        cartItem = JSON.parse(responseText);
      } catch (parseError) {
        console.warn("[Lynqr][CART] SUCCESS RESPONSE IS NOT JSON", {
          parseError,
          responseText,
        });
      }

      console.log("[Lynqr][CART] SUCCESS", {
        cartItem,

        packageVariantId,

        productId,

        quantity: cartQuantity,

        upgradeCount: selectedUpgrades.length,

        expectedTotal: packageTotal,
      });

      /* Shopify is authoritative for the component prices. Re-read the cart
           and validate only lines belonging to this configuration. */
      const cartResponse = await fetch(
        `${window.Shopify?.routes?.root || "/"}cart.js`,
        { headers: { Accept: "application/json" } },
      );
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const matchingLines = (cart.items || []).filter(
          (line) =>
            line.properties?.["_lynqr_configuration_id"] === configurationId,
        );
        const actualCents = matchingLines.reduce(
          (sum, line) =>
            sum + Number(line.final_line_price || line.line_price || 0),
          0,
        );
        /* The expected deposit is per package, not per cart line. Add-on
               lines are components of the same package and must not multiply
               the package deposit expectation. */
        const baseLineQuantity = matchingLines
          .filter(
            (line) =>
              line.properties?.["_lynqr_component_type"] === "base-deposit",
          )
          .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
        const expectedCents =
          pricing.expectedDepositCents * (baseLineQuantity || cartQuantity);
        checkoutTrace("cart.post_add.validation", {
          configurationId,
          actualCents,
          expectedCents,
          baseLineQuantity,
          matchingLines: matchingLines.map((line) => ({
            id: line.id,
            quantity: line.quantity,
            finalLinePrice: line.final_line_price,
            component: line.properties?.["_lynqr_component_type"],
          })),
        });
        if (actualCents !== expectedCents) {
          console.warn("[Lynqr][DEPOSIT-MAPPING] Shopify cart total mismatch", {
            configurationId,
            actualCents,
            expectedCents,
            baseLineQuantity,
            cartQuantity,
            matchingLines,
          });
          emit("cart.error", {
            message:
              "Online checkout is temporarily unavailable for this configuration. Please contact LYNQR.",
          });
          console.groupEnd();
          return false;
        }
      }

      /*
        ----------------------------------------------------------------------
        | CART RESULT
        ----------------------------------------------------------------------
        */

      emit("cart.added", cartItem);

      console.groupEnd();

      return cartItem;
    } catch (error) {
      console.error("[Lynqr][CART] ERROR", {
        message: error?.message,

        error,

        productId: state.product?.id,

        packageVariantId,

        payload,

        selectedUpgrades,
      });

      emit("cart.error", {
        message: error?.message || "Unable to add package to cart.",
      });

      console.groupEnd();

      return false;
    }
  }

  /*==============================================================================
Cart Events
==============================================================================*/

  on(
    "cart.adding",

    () => {
      console.log("[Lynqr] Adding package to cart...");
    },
  );

  on(
    "cart.added",

    () => {
      window.location.href = "/cart";
    },
  );

  on(
    "cart.error",

    ({ detail }) => {
      alert(detail.message);
    },
  );

  /*==============================================================================
Cart Button State
==============================================================================*/

  function roomConfigurationComplete() {
    return Object.values(state.roomSelections).every((selection) => {
      if (!selection?.palette) return false;
      return activeOptionGroups(selection)
        .filter((group) => group?.required === true)
        .every((group) => Boolean(selection.options?.[group.handle]));
    });
  }

  function refreshCartButtonState() {
    const button = document.querySelector("[data-lynqr-add-to-cart]");

    if (!button) {
      return;
    }

    const hasTheme = Boolean(state.selectedTheme);

    const hasFlatStatus = Boolean(state.selectedFlatStatus);

    const roomsComplete = roomConfigurationComplete();

    const enabled = hasFlatStatus && hasTheme && roomsComplete;

    /*
    --------------------------------------------------------------------------
    Button State
    --------------------------------------------------------------------------
    */

    button.disabled = !enabled;

    /*
    --------------------------------------------------------------------------
    Tooltip
    --------------------------------------------------------------------------
    */

    if (!hasFlatStatus) {
      button.title = "Please select a Flat Status before adding to cart.";
    } else if (!hasTheme) {
      button.title = "Please select a Theme before adding to cart.";
    } else if (!roomsComplete) {
      button.title =
        "Please complete the Material Palette and required options for every room.";
    } else {
      button.removeAttribute("title");
    }
  }

  /*==============================================================================
Cart Button Binding
==============================================================================*/

  function bindCartButton() {
    const button = document.querySelector("[data-lynqr-add-to-cart]");

    if (!button) return;

    button.addEventListener(
      "click",

      async (event) => {
        event.preventDefault();

        button.disabled = true;

        try {
          await addPackageToCart(1);
        } finally {
          button.disabled = false;
        }
      },
    );
  }
  let saveTimer = null;

  function scheduleSave() {
    clearTimeout(saveTimer);

    saveTimer = setTimeout(() => {
      if (!state.isDirty || !hasConfigurationChanged()) {
        return;
      }

      saveConfiguration();

      clearDirty();
    }, 300);
  }
  /*==============================================================================
State Change Tracking
==============================================================================*/

  function markDirty() {
    state.isDirty = true;
  }

  /*==============================================================================
Reset Dirty Flag
==============================================================================*/

  function clearDirty() {
    state.isDirty = false;
  }

  /*==============================================================================
Watch State Changes
==============================================================================*/
  on("theme.changed", () => {
    markDirty();

    refreshCartButtonState();
  });

  on("palette.changed", () => {
    markDirty();

    refreshCartButtonState();
  });
  /*==============================================================================
Configuration Change Events
==============================================================================*/

  on("room.changed", ({ detail }) => {
    /*
        toggleUpgrade() already performs the immediate affected-room
        update. This schedules one consolidated application render.
        */

    scheduleRender();
  });

  /*==============================================================================
Configuration Snapshot
==============================================================================*/

  function configurationHash() {
    return JSON.stringify({
      flatStatus: state.selectedFlatStatus,

      theme: state.selectedTheme ? state.selectedTheme.handle : null,

      rooms: Object.values(state.roomSelections).map((room) => ({
        room: room.room.handle,

        palette: room.palette?.handle || null,

        options: Object.values(room.options || {})
          .map(({ group, option }) => `${group.handle}:${option.handle}`)
          .sort(),

        upgrades: room.upgrades
          .map((u) => getUpgradeHandle(u))
          .filter(Boolean)
          .sort(),
      })),
    });
  }

  let previousHash = configurationHash();

  /*==============================================================================
Detect Changes
==============================================================================*/

  function hasConfigurationChanged() {
    const current = configurationHash();

    const changed = current !== previousHash;

    previousHash = current;

    return changed;
  }

  /*==============================================================================
DOM Cache
==============================================================================*/

  const domCache = new Map();

  function dom(selector) {
    if (domCache.has(selector)) {
      return domCache.get(selector);
    }

    const element = document.querySelector(selector);

    domCache.set(
      selector,

      element,
    );

    return element;
  }

  function domAll(selector) {
    return [...document.querySelectorAll(selector)];
  }

  /*==============================================================================
Invalidate DOM Cache
==============================================================================*/

  function clearDomCache() {
    domCache.clear();
  }

  /*==============================================================================
Image Cache
==============================================================================*/

  const imageCache = new Set();

  /*==============================================================================
Preload Image
==============================================================================*/

  function preloadImage(src) {
    if (!src) return;

    if (imageCache.has(src)) return;

    imageCache.add(src);

    const image = new Image();

    image.src = src;
  }

  /*==============================================================================
Window Resize
==============================================================================*/

  let resizeTimer;

  window.addEventListener(
    "resize",

    () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(
        refreshGallery,

        100,
      );
    },
  );

  /*==============================================================================
Visibility Change
==============================================================================*/

  document.addEventListener(
    "visibilitychange",

    () => {
      if (document.hidden) return;

      refreshGallery();
    },
  );
  on("configurator.ready", () => {
    preloadThemeImages();
  });
  /*==============================================================================
Business Rule Engine
==============================================================================*/

  const businessRules = {
    /*
    --------------------------------------------------------------------------
    Upgrade Dependencies

    premium-lighting

    requires

    lighting
    --------------------------------------------------------------------------
    */

    dependencies: {
      /*
        "premium-lighting": [

            "lighting"

        ]
        */
    },

    /*
    --------------------------------------------------------------------------
    Upgrade Conflicts

    premium-lighting

    cannot exist with

    lighting
    --------------------------------------------------------------------------
    */

    conflicts: {
      /*
        "premium-lighting": [

            "lighting"

        ]
        */
    },
  };

  /*==============================================================================
Validate Upgrade Rules
==============================================================================*/

  function validateRoomRules(roomHandle) {
    const room = roomSelection(roomHandle);

    if (!room) return true;

    const selected = room.upgrades.map((u) => u.handle);

    /*
    --------------------------------------------------------------------------
    Dependencies
    --------------------------------------------------------------------------
    */

    for (const handle of selected) {
      const dependencies = businessRules.dependencies[handle] || [];

      for (const dependency of dependencies) {
        if (!selected.includes(dependency)) {
          emit(
            "validation.error",

            {
              room: roomHandle,

              message: `${handle} requires ${dependency}.`,
            },
          );

          return false;
        }
      }
    }

    /*
    --------------------------------------------------------------------------
    Conflicts
    --------------------------------------------------------------------------
    */

    for (const handle of selected) {
      const conflicts = businessRules.conflicts[handle] || [];

      for (const conflict of conflicts) {
        if (selected.includes(conflict)) {
          emit(
            "validation.error",

            {
              room: roomHandle,

              message: `${handle} cannot be combined with ${conflict}.`,
            },
          );

          return false;
        }
      }
    }

    return true;
  }

  /*==============================================================================
Validate Whole Package
==============================================================================*/

  function validateBusinessRules() {
    for (const room of state.rooms) {
      if (!validateRoomRules(room.handle)) {
        return false;
      }
    }

    return true;
  }

  /*==============================================================================
Configuration Engine
==============================================================================*/

  function currentConfiguration() {
    const configuration = {
      version: 1,

      product: {
        id: state.product.id,

        handle: state.product.handle,

        title: state.product.title,
      },

      package: {
        hdbType: state.package.hdbType,
        flatStatus: state.selectedFlatStatus,
      },

      theme: state.selectedTheme
        ? {
            id: state.selectedTheme.id,
            handle: state.selectedTheme.handle,
            name: state.selectedTheme.name,
          }
        : null,

      rooms: [],

      pricing: {
        subtotal: Number(state.product.price),

        upgrades: 0,

        total: state.totalPrice,
      },
    };

    Object.values(state.roomSelections).forEach((selection) => {
      const room = {
        id: selection.room.id,

        handle: selection.room.handle,

        name: selection.room.name,

        palette: selection.palette
          ? {
              id: selection.palette.id,
              handle: selection.palette.handle,
              name: selection.palette.name,
            }
          : null,

        options: Object.values(selection.options || {}).map(
          ({ group, option }) => ({
            group: group.name,
            groupHandle: group.handle,
            name: option.name,
            handle: option.handle,
            price: Number(option.price || 0),
            subgroup: option.subgroup
              ? {
                  handle: option.subgroup.handle,
                  name: option.subgroup.name,
                }
              : null,
          }),
        ),

        configurationKey: configurationKey(selection.room.handle),

        upgrades: [],
      };

      selection.upgrades.forEach((upgrade) => {
        room.upgrades.push({
          id: upgrade.id,
          handle: getUpgradeHandle(upgrade),

          name: upgrade.upgrade.name,

          price: Number(upgrade.price),
          ...(getUpgradeMaxQuantity(upgrade) !== null ? { quantity: getUpgradeQuantity(upgrade) } : {}),
        });

        configuration.pricing.upgrades += getUpgradeLineTotal(upgrade);
      });

      configuration.pricing.upgrades += selectedPaidOptions(selection).reduce(
        (total, selectionOption) =>
          total + Number(selectionOption.option?.price || 0),
        0,
      );

      configuration.rooms.push(room);
    });

    return configuration;
  }

  /*==============================================================================
Configuration Hash
==============================================================================*/
  function configurationFingerprint() {
    const json = JSON.stringify(currentConfiguration());

    const bytes = new TextEncoder().encode(json);

    let binary = "";

    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary);
  }

  /*
   * The Base64 configuration string is also used by share URLs. Do not use a
   * truncated prefix of it as the cart correlation key: upgrades are serialized
   * after the common product/theme fields, so different configurations can
   * otherwise receive the same _lynqr_configuration_id.
   */
  function lynqrConfigurationId() {
    const fingerprint = configurationFingerprint();
    let first = 2166136261;
    let second = 16777619;

    for (let index = 0; index < fingerprint.length; index += 1) {
      const code = fingerprint.charCodeAt(index);
      first = Math.imul(first ^ code, 16777619);
      second = Math.imul(second ^ (code + index), 2246822519);
    }

    return `cfg-${(first >>> 0).toString(16).padStart(8, "0")}${(second >>> 0).toString(16).padStart(8, "0")}`;
  }
  /*==============================================================================
Export Configuration
==============================================================================*/

  function exportConfiguration() {
    return JSON.stringify(
      currentConfiguration(),

      null,

      2,
    );
  }
  /*==============================================================================
Import Configuration
==============================================================================*/

  function importConfiguration(configuration) {
    if (typeof configuration === "string") {
      configuration = JSON.parse(configuration);
    }

    if (!configuration) {
      return;
    }

    const importedFlatStatus =
      configuration.flatStatus || configuration.package?.flatStatus || null;
    if (importedFlatStatus) {
      selectFlatStatus(importedFlatStatus);
    }

    /*
    --------------------------------------------------------------------------
    Theme
    --------------------------------------------------------------------------
    */

    if (configuration.theme?.handle) {
      selectTheme(configuration.theme.handle);
    }

    /*
    --------------------------------------------------------------------------
    Material Palette
    --------------------------------------------------------------------------
    */

    const paletteHandle =
      configuration.materialPalette?.handle || configuration.palette?.handle;

    if (paletteHandle) {
      selectPalette(paletteHandle);
    }

    /*
    --------------------------------------------------------------------------
    Rooms
    --------------------------------------------------------------------------
    */

    if (!Array.isArray(configuration.rooms)) {
      return;
    }

    configuration.rooms.forEach((room) => {
      const roomHandle = room.handle || room.roomHandle;

      if (!roomHandle || !Array.isArray(room.upgrades)) {
        return;
      }

      if (room.palette?.handle) {
        selectRoomPalette(roomHandle, room.palette.handle);
      }

      restoreRoomOptions(roomHandle, room.options || []);

      /*
        ----------------------------------------------------------------------
        Upgrades
        ----------------------------------------------------------------------
        */

      room.upgrades.forEach((upgrade) => {
        const upgradeHandle = upgrade.handle || upgrade.slug;

        if (!upgradeHandle) {
          return;
        }

        const selection = roomSelection(roomHandle);

        if (!selection) {
          return;
        }

        toggleUpgrade(roomHandle, upgradeHandle, true, upgrade.quantity ?? 1);
      });
    });
  }
  /*==============================================================================
Share URL
==============================================================================*/

  function configurationURL() {
    const url = new URL(window.location.href);

    url.searchParams.set(
      "cfg",

      configurationFingerprint(),
    );

    return url.toString();
  }
  /*==============================================================================
Restore From URL
==============================================================================*/

  function restoreFromURL() {
    const params = new URLSearchParams(window.location.search);

    const encoded = params.get("cfg");

    if (!encoded) {
      return;
    }

    try {
      const binary = atob(encoded);

      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

      const json = new TextDecoder().decode(bytes);

      const configuration = JSON.parse(json);

      importConfiguration(configuration);
    } catch (error) {
      console.warn("[Lynqr] Invalid configuration URL.", error);
    }
  }
  /*==============================================================================
Public API
==============================================================================*/

  return Object.freeze({
    /*--------------------------------------------------------------------------
    Application
    --------------------------------------------------------------------------*/

    data,
    state,

    /*--------------------------------------------------------------------------
    Event Bus
    --------------------------------------------------------------------------*/

    on,
    emit,

    /*--------------------------------------------------------------------------
    Helpers
    --------------------------------------------------------------------------*/

    currency,
    slugify,

    /*--------------------------------------------------------------------------
    Theme
    --------------------------------------------------------------------------*/

    selectTheme,
    selectFlatStatus,

    /*--------------------------------------------------------------------------
    Material Palette
    --------------------------------------------------------------------------*/

    selectPalette,
    availablePalettes,
    selectRoomPalette,
    selectRoomOption,
    roomOptionIsSelected,
    startOver,

    /*--------------------------------------------------------------------------
    Room Configuration
    --------------------------------------------------------------------------*/

    toggleUpgrade,
    setUpgradeQuantity,
    roomSelection,
    configurationKey,

    loadThemeData,
    resolveRoomGallery,

    /*--------------------------------------------------------------------------
    Gallery
    --------------------------------------------------------------------------*/

    refreshRoomImage,
    refreshGallery,
    refreshHeroImage,

    /*--------------------------------------------------------------------------
    Pricing
    --------------------------------------------------------------------------*/

    calculateTotal,
    roomSelectedAddonSummary,
    pricingBreakdown,
    refreshPriceSummary,

    /*--------------------------------------------------------------------------
    Configuration
    --------------------------------------------------------------------------*/

    currentConfiguration,
    buildConfiguration,
    buildLineItemProperties,
    buildQuotation,
    renderPrintableQuotation,
    validateConfiguration,
    refreshConfiguration,
    configurationFingerprint,
    configurationURL,
    exportConfiguration,
    importConfiguration,
    restoreFromURL,

    /*--------------------------------------------------------------------------
    Persistence
    --------------------------------------------------------------------------*/

    saveConfiguration,
    restoreConfiguration,

    /*--------------------------------------------------------------------------
    Validation
    --------------------------------------------------------------------------*/

    validateSelections,
    validateBusinessRules,

    /*--------------------------------------------------------------------------
    Utilities
    --------------------------------------------------------------------------*/

    snapshot,

    /*--------------------------------------------------------------------------
    Cart
    --------------------------------------------------------------------------*/

    addPackageToCart,
  });
})();
